import { useEffect, useState } from "react";
import { Drawer, Modal } from "antd";
import { Link, Navigate, useParams } from "react-router-dom";
import CareRichTextEditor from "../../components/care/CareRichTextEditor";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { careService } from "../../services/careService";
import {
  assessmentRecordSlug,
  formatDate,
  processRecords,
  processResidentName,
  recordLabel,
  recordSlug,
} from "./AssessmentsPage";
import { richTextMarkup } from "./richTextUtils";

const meetAndGreetQuestions = [
  "What daily routines make you feel most comfortable?",
  "What support do you need with transfers, walking, bathing, dressing, or toileting?",
  "What medications, allergies, or health concerns should the care team understand?",
  "What behaviors, triggers, or calming approaches should caregivers know?",
  "Who should be contacted first when care needs change?",
  "What activities, foods, faith practices, hobbies, or preferences matter to you?",
];

const signedDocumentOptions = [
  "Admission agreement",
  "Resident rights and responsibilities",
  "Medication management consent",
  "HIPAA/privacy acknowledgment",
  "House policies",
  "Emergency contact authorization",
  "Financial agreement/payment policy",
  "Care service agreement",
];

const defaultCareCircleMembers = [
  ["Elaine Johnson", "Daughter / PoA"],
  ["Robert Johnson", "Son"],
  ["Maya Torres", "Care taker"],
  ["Linda Green", "Guardian contact"],
];

const defaultCareProviderStaff = [
  ["Sarah Mitchell", "Lead caregiver"],
  ["Daniel Reed", "Care aide"],
];

const dailyNoteCategories = [
  "Behavior",
  "Health",
  "Welfare",
  "Medication",
  "Care discrepancy",
  "Family update",
  "Mobility",
  "Meals and hydration",
  "Other",
];

const dateInputValue = (date) => new Date(date).toISOString().slice(0, 10);

const monthStartInputValue = (date) => {
  const current = new Date(date);
  return dateInputValue(new Date(current.getFullYear(), current.getMonth(), 1));
};

const monthEndInputValue = (date) => {
  const current = new Date(date);
  return dateInputValue(new Date(current.getFullYear(), current.getMonth() + 1, 0));
};

const shiftDate = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const formatDateTime = (date) =>
  new Date(date).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

const isWithinDateRange = (date, startDate, endDate) => {
  const target = dateInputValue(date);
  return target >= startDate && target <= endDate;
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const negotiatedTemplateSections = [
  {
    title: "Provider and Resident Information",
    fields: [
      ["providerName", "Provider's Name"],
      ["dateNcpStarted", "Date NCP Started"],
      ["movedInDate", "Moved In Date"],
      ["dateCompleted", "Date Completed"],
      ["dateDischarged", "Date Discharged"],
      ["residentName", "Resident's Name"],
      ["pronouns", "Pronouns"],
      ["dateOfBirthAge", "Date of Birth / Age"],
      ["primaryLanguage", "Primary Language"],
      ["speaksEnglish", "Speaks English?"],
      ["interpreterNeeded", "Interpreter needed?"],
      ["allergies", "Allergies"],
    ],
  },
  {
    title: "Legal Documents and Specialty Needs",
    fields: [
      ["legalDocuments", "Legal Documents: None, Advanced Directives, POLST Form, Other"],
      ["specialtyNeeds", "Specialty Needs: No, Dementia, Mental Health, Developmental Disability, Other"],
    ],
  },
  {
    title: "Emergency Evacuation",
    fields: [
      ["evacuationAssistance", "Evacuation Assistance Required"],
      ["evacuationSafetyInstructions", "Resident's Evacuation and Safety Instructions"],
      ["evacuationCaregiverInstructions", "Caregiver will"],
    ],
  },
  {
    title: "Mental/Physical Health - Current Medical Status/Diagnosis",
    fields: [
      ["medicalDiagnosis", "Current Medical Status / Diagnosis"],
      ["healthIndicatorsSummary", "Health indicators, pain, allergies, risks, and concerns"],
    ],
  },
  {
    title: "Responsible Parties - Contacts",
    fields: [
      ["contact1", "Contact 1: Name, relationship, phone/fax, address/email"],
      ["contact2", "Contact 2: Name, relationship, phone/fax, address/email"],
      ["contact3", "Contact 3: Name, relationship, phone/fax, address/email"],
      ["contact4", "Contact 4: Name, relationship, phone/fax, address/email"],
      ["contact5", "Contact 5: Name, relationship, phone/fax, address/email"],
      ["contact6", "Contact 6: Name, relationship, phone/fax, address/email"],
    ],
  },
  {
    title: "Communication, Speech, Hearing, and Vision",
    fields: [
      ["communicationModes", "Modes of Expression"],
      ["expressionProblems", "Problems with Mode of Expression"],
      ["expressionEquipment", "Expression equipment"],
      ["selfUnderstood", "How resident makes self-understood"],
      ["understandsOthers", "How resident understands others"],
      ["communicationStrengths", "Strengths and Abilities"],
      ["communicationAssistance", "Assistance Required / Caregiver will"],
      ["hearingProblems", "Problems with Hearing / equipment"],
      ["visionProblems", "Problems with Vision / equipment"],
      ["phoneAbility", "Ability to Use the Phone"],
      ["preferredLanguageComments", "Preferred language / comments"],
    ],
  },
  {
    title: "Medication Management - Overview",
    fields: [
      ["medicationAllergies", "Medication allergies"],
      ["medicationAssistanceNeeds", "Medication assistance needs"],
      ["psychopharmacologicMeds", "Psychopharmacologic medications"],
      ["marReference", "MAR reference"],
      ["medsOrderedBy", "Meds are ordered by"],
      ["medsDeliveredBy", "Meds are delivered by"],
      ["pharmacyPacked", "Pharmacy packed / note"],
    ],
  },
  {
    title: "Medication Management",
    fields: [
      ["medicationManagementType", "Self-administration / self-administration with assistance / medication administration"],
      ["medicationRoutes", "Oral, topical, eye drops, inhalers, sprays, injections, allergy kits, suppositories, other"],
      ["nurseDelegation", "Requires nurse delegation / RN delegator contact"],
      ["medicationStrengths", "Strengths and Abilities"],
      ["medicationAssistance", "Assistance Required / Caregiver will"],
      ["medicationOutsideAfh", "Medication plan when resident is not in the AFH"],
      ["medicationRefusalPlan", "Medication refusal plan"],
    ],
  },
  {
    title: "Health Indicators",
    fields: [
      ["healthIndicatorMonitoring", "Pain, weight, vital signs, hospitalization, emergency visits, other"],
      ["currentWeightHeight", "Current weight / height"],
      ["healthStrengths", "Strengths and Abilities"],
      ["healthAssistance", "Assistance Required"],
      ["monitoringReporting", "Monitoring/reporting significant changes and concerns"],
      ["allergySubstanceReaction", "Allergy substance / reaction"],
    ],
  },
  {
    title: "Treatments, Programs, Therapies",
    fields: [
      ["treatmentTypes", "Oxygen, dialysis, blood thinners, INR/lab, glucose monitoring, CPAP/BIPAP, nebulizer, other"],
      ["rangeOfMotionTherapies", "Range of motion, PT/OT/ST, nurse delegation tasks"],
      ["treatmentStrengths", "Strengths and Abilities"],
      ["treatmentAssistance", "Assistance Required / Caregiver will"],
      ["programsAttended", "Home Health, Adult Day Health, Hospice, other programs"],
      ["meaningfulDay", "Meaningful Day / interventions"],
      ["physicalEnablers", "Physical enablers"],
      ["treatmentRefusalPlan", "Treatment/program/therapy refusal plan"],
    ],
  },
  {
    title: "Psych/Social/Cognitive Status",
    fields: [
      ["sleepSupport", "Sleep disturbance and nighttime assistance"],
      ["memoryImpairment", "Memory impairment: short-term, long-term, oriented to person"],
      ["decisionMaking", "Impaired decision making"],
      ["behaviors", "Disruptive, assaultive, resistive, depression, anxiety, irritability, disorientation, wandering, exit seeking, hallucinations, delusions, agitation, unsafe behavior, suicidal ideation, disrobing, crying, fears, spitting, throwing, other"],
      ["psychMeds", "Psychopharmacological medications and symptoms addressed"],
      ["behavioralHealthSupport", "Behavioral Health Support Crisis Plan, counseling, MHP, past behaviors"],
      ["specializedBehaviorPrograms", "DSHS specialized behavioral programs / contact info"],
      ["psychInterventions", "Meaningful Day / other interventions"],
      ["typicalDayNarrative", "Narrative - typical day"],
    ],
  },
  {
    title: "Left Alone and Universal Precautions",
    fields: [
      ["leftAloneAbility", "Ability of Resident to Be Left Unattended"],
      ["leftAloneStrengths", "Strengths and Abilities"],
      ["leftAloneAssistance", "Assistance Required / Caregiver will"],
      ["universalPrecautions", "Always / special precautions / alternative visitation / mask ability / sanitizer safety / vaccinations / shared equipment / other"],
      ["universalPrecautionsStrengths", "Strengths and Abilities"],
      ["universalPrecautionsCaregiver", "Caregiver will"],
      ["universalPrecautionsAssistance", "Assistance Required"],
    ],
  },
  {
    title: "Activities of Daily Living - Ambulation/Mobility",
    fields: [
      ["functionalLimitations", "Resident functional limitations that impact ADL functioning"],
      ["locomotionRoom", "Locomotion in room and immediate living environment"],
      ["locomotionOutside", "Locomotion outside immediate living environment"],
      ["fallRisks", "Risks for falls / fall prevention plan"],
      ["bedroomDoorLock", "Resident chooses bedroom door lock"],
      ["mobilityEquipment", "Equipment/supplies and vendor"],
      ["mobilityLimitations", "Limitations"],
      ["mobilityStrengths", "Strengths and Abilities"],
      ["mobilityAssistance", "Assistance Required / Caregiver will"],
    ],
  },
  {
    title: "Bed Mobility/Transfer",
    fields: [
      ["transferLevel", "Independent / supervision-cueing / assistance needed / totally dependent"],
      ["skinCarePositioning", "Skin care required due to inability to position self"],
      ["turningRepositioning", "Turning and repositioning frequency"],
      ["transferSafetyPlan", "Safety assessment, fall prevention, safety plan"],
      ["medicalDevices", "Medical devices/enablers, Hoyer lift, transfer pole, risk assessment, nighttime needs"],
      ["transferEquipment", "Equipment/supplies"],
      ["transferStrengths", "Strengths and Abilities"],
      ["transferAssistance", "Assistance Required / Caregiver will"],
    ],
  },
  {
    title: "Eating",
    fields: [
      ["eatingLevel", "Independent / supervision-cueing / assistance needed / totally dependent"],
      ["specialDiet", "Special diet/supplements, eating habits, food allergies"],
      ["eatingEquipment", "Equipment/supplies/procedures"],
      ["eatingLimitations", "Limitations"],
      ["eatingStrengths", "Strengths and Abilities"],
      ["eatingAssistance", "Assistance Required / Caregiver will"],
    ],
  },
  {
    title: "Toileting/Continence Issues",
    fields: [
      ["toiletingLevel", "Independent / supervision-cueing / assistance needed / totally dependent"],
      ["toiletingFrequency", "Frequency/how often"],
      ["continenceIssues", "Bladder, bowel, skin care, catheter, ostomy, equipment, limitations"],
      ["toiletingStrengths", "Strengths and Abilities"],
      ["toiletingAssistance", "Assistance Required / Caregiver will"],
    ],
  },
  {
    title: "Dressing, Personal Hygiene, Bathing, Foot Care, and Skin Care",
    fields: [
      ["dressing", "Dressing support, equipment, procedures, limitations, strengths, assistance"],
      ["personalHygiene", "Hair, teeth, shaving, makeup, face/hands/perineum, dentures, oral hygiene, menses care"],
      ["bathing", "Shower/sponge bath/tub transfer, when/how often, equipment, limitations, strengths, assistance"],
      ["footCare", "Foot care, diabetic foot care, nail care, home health agency, equipment, limitations"],
      ["skinCare", "Routine/special skin care, skin problems, pressure injuries, dressing changes, nurse delegation, home health, equipment"],
    ],
  },
  {
    title: "Instrumental Activities of Daily Living",
    fields: [
      ["finances", "Managing finances, financial records, payee name/contact"],
      ["shopping", "Shopping assistance, special transportation needs, frequency, equipment, limitations"],
      ["transportation", "Medical services, special transportation, escort required, frequency, equipment, limitations"],
      ["activitiesSocial", "Social/cultural considerations, interests, relationships, employment support, groups/day health, special arrangements, participation issues"],
      ["activityPreferences", "Activity preferences at a glance and activity narrative"],
      ["smoking", "Resident smokes, safety concerns, smoking policy, cigarette/lighter storage"],
    ],
  },
  {
    title: "Case Management and Other Issues",
    fields: [
      ["caseManagement", "Case management name, agency, phone, email, fax"],
      ["cmContactedWhen", "When CM will be contacted"],
      ["otherIssues", "Other issues, concerns, or problems"],
    ],
  },
  {
    title: "Negotiated Care Plan Review and Approval",
    fields: [
      ["reviewSchedule", "Original plan date and dates of review/revision"],
      ["involvedInDevelopment", "Involved in NCP development"],
      ["approvingPerson", "Person approving plan"],
      ["providerSignature", "Provider signature and date"],
      ["residentSignature", "Resident signature and date"],
      ["residentRepresentativeSignature", "Resident representative signature and date"],
      ["residentVerbalAgreement", "Resident verbally agreed to NCP - date"],
      ["sentToDshs", "NCP sent to DSHS CM on"],
      ["residentRecommendations", "Resident recommendations"],
    ],
  },
];

const ncpContactColumns = [
  { key: "name", label: "Name" },
  { key: "relationship", label: "Relationship" },
  { key: "homeBusinessPhone", label: "Home/Business Phone" },
  { key: "cellFax", label: "Cell Phone/FAX" },
  { key: "addressEmail", label: "Address/Email" },
];

const ncpContactFieldKey = (rowNumber, columnKey) => `contact${rowNumber}${columnKey.charAt(0).toUpperCase()}${columnKey.slice(1)}`;

const negotiatedTemplatePagesDefinition = [
  {
    pageNumber: 1,
    sections: [
      {
        title: "Provider and Resident Information",
        fields: [
          ["providerName", "Provider's Name"],
          ["dateNcpStarted", "Date NCP Started"],
          ["movedInDate", "Moved In Date"],
          ["dateCompleted", "Date Completed"],
          ["dateDischarged", "Date Discharged"],
          ["residentName", "Resident's Name"],
          ["pronouns", "Pronouns"],
          ["dateOfBirthAge", "Date of Birth / Age"],
          ["primaryLanguage", "Primary Language"],
          ["speaksEnglish", "Speaks English?"],
          ["interpreterNeeded", "Interpreter needed?"],
          ["allergies", "ALLERGIES"],
          ["legalDocuments", "Legal Documents: [] None, [] Advanced Directives, [ ] POLST Form, [ ] Other"],
          ["specialtyNeeds", "Specialty Needs: [ ] No, [ ] Dementia, [ ] Mental Health, [ ] Developmental Disability, [ ] Other"],
        ],
      },
      {
        title: "Emergency Evacuation",
        fields: [
          ["evacuationIndependent", "[] NONE - RESIDENT IS INDEPENDENT"],
          ["evacuationAssistance", "[] ASSISTANCE REQUIRED"],
          ["evacuationSafetyInstructions", "RESIDENT'S EVACUATION and SAFETY INSTRUCTIONS"],
          ["evacuationCaregiverInstructions", "Caregiver will"],
        ],
      },
    ],
  },
  {
    pageNumber: 2,
    sections: [
      {
        title: "Mental/Physical Health - Current Medical Status/Diagnosis",
        fields: [
          ["medicalDiagnosis", "MENTAL/PHYSICAL HEALTH - CURRENT MEDICAL STATUS/DIAGNOSIS"],
          ["assessmentSectionIndex", "Template section index: Activities/Social, Allergies, Ambulation/Mobility, Bathing, Bed Mobility/Transfer, Behavior, Case Management, Communication, Decision Making, Dressing, Eating, Falls, Finances, Foot Care, Health Indicators, Left Alone, Medication Management, Memory, Mental/Physical Health/Diagnosis, NCP Review/Signatures, Other Issues/Concerns, Pain, Personal Hygiene, Shopping, Skin Care, Sleep, Smoking, Specialized Behavior Program, Toilet Use/Continence, Transportation, Treatments/Programs/Therapies, Universal Precautions, Vision"],
        ],
      },
      {
        title: "Responsible Parties - Contacts",
        fields: Array.from({ length: 6 }, (_, rowIndex) =>
          ncpContactColumns.map((column) => [
            ncpContactFieldKey(rowIndex + 1, column.key),
            `Contact ${rowIndex + 1} - ${column.label}`,
          ]),
        ).flat(),
      },
    ],
  },
  {
    pageNumber: 3,
    sections: [
      {
        title: "Communication - Speech/Hearing/Vision",
        fields: [
          ["communicationComments", "Comments"],
          ["communicationModes", "Modes of Expression"],
          ["expressionProblems", "Problems with Mode of Expression - [ ] Yes [ ] No / Describe"],
          ["expressionEquipment", "Equipment"],
          ["selfUnderstood", "How resident makes self-understood"],
          ["understandsOthers", "How resident understands others"],
          ["communicationStrengths", "Resident Strengths and Abilities / Prefers To Do Independently / Preferences and Personal Goals"],
          ["communicationAssistance", "Assistance Required / Caregiver Instructions / Who, How, When/How Often"],
          ["hearingProblems", "Problems with Hearing - [ ] Yes [ ] No / Describe / Equipment"],
          ["visionProblems", "Problems with Vision - [ ] Yes [ ] No / Describe / Equipment"],
          ["phoneAbility", "Ability to Use the Phone: [ ] Independent [ ] Assistance Needed [ ] Dependent / [ ] Resident has own phone, number"],
          ["preferredLanguageComments", "Preferred Language / Comments"],
        ],
      },
    ],
  },
  {
    pageNumber: 4,
    sections: [
      {
        title: "Medication Management - Overview",
        fields: [
          ["medicationAllergies", "[ ] MEDICATION ALLERGIES"],
          ["medicationAssistanceNeeds", "[ ] Resident needs more than one kind of medication assistance need"],
          ["psychopharmacologicMeds", "[ ] Resident is prescribed psychopharmacologic medications - see behavior section"],
          ["marReference", "[ ] See MAR for current medications, dosage, frequency, and route"],
          ["medsOrderedBy", "Meds are ordered by"],
          ["medsDeliveredBy", "Meds are delivered by"],
          ["pharmacyPacked", "Meds are / Pharmacy Packed / Note"],
        ],
      },
      {
        title: "Medication Management",
        fields: [
          ["medicationManagementType", "[ ] SELF-ADMINISTRATION / [ ] SELF-ADMINISTRATION W/ ASSISTANCE / [ ] MEDICATION ADMINISTRATION"],
          ["medicationRoutes", "Type needed: [ ] Oral [ ] Topical [ ] Eye drops/ointment [ ] Inhalers [ ] Sprays [ ] Injections [ ] Allergy Kits [ ] Suppositories [ ] Other"],
          ["injectionSupport", "Injections by: [ ] Resident [ ] Surrogate [ ] Family [ ] Licensed Professional [ ] Qualified CG under Nurse Delegation"],
          ["nurseDelegation", "[ ] Requires Nurse Delegation / RN Delegator Name, Phone, FAX, Email"],
          ["medicationStrengths", "Strengths and Abilities / reason resident needs this amount of medication assistance"],
          ["medicationAssistance", "Assistance Required / Caregiver will / 5 rights + documentation + right to refuse"],
        ],
      },
    ],
  },
  {
    pageNumber: 5,
    sections: [
      {
        title: "Medication Plans",
        fields: [
          ["medicationOutsideAfhStrengths", "Medication Plan When Resident is not in the AFH - Strengths and Abilities"],
          ["medicationOutsideAfh", "Medication Plan When Resident is not in the AFH - Assistance Required / Caregiver will"],
          ["medicationRefusalStrengths", "Medication Refusal Plan - Strengths and Abilities"],
          ["medicationRefusalPlan", "Medication Refusal Plan - Assistance Required / Caregiver will"],
        ],
      },
      {
        title: "Health Indicators",
        fields: [
          ["healthIndicatorMonitoring", "[ ] Pain / Pain Impact / [ ] Weight Loss-Gain / Current Weight / Current Height / [ ] Vital Signs / [ ] Hospitalization or Emergency Visits / [ ] Other"],
          ["healthStrengths", "Strengths and Abilities"],
          ["healthAssistance", "Assistance Required"],
          ["monitoringReporting", "Monitoring/Reporting significant changes and/or concerns"],
          ["allergySubstanceReaction", "Allergies - Substance / Reaction"],
        ],
      },
    ],
  },
  {
    pageNumber: 6,
    sections: [
      {
        title: "Treatments/Programs/Therapies",
        fields: [
          ["treatmentTypes", "Type of Treatment: [ ] Oxygen Use - Vendor [ ] Dialysis - Health Provider [ ] Blood Thinners [ ] INR/LAB [ ] Easily bruised/Anti coagulation therapy [ ] Blood Glucose Monitoring [ ] Injection [ ] CPAP/BIPAP [ ] Nebulizer [ ] Other"],
          ["rangeOfMotionTherapies", "Range of Motion / [ ] PT/OT/ST / [ ] Nurse Delegation for Treatments/Therapies - Tasks"],
          ["treatmentStrengths", "Strengths and Abilities"],
          ["treatmentAssistance", "Assistance Required / Caregiver will"],
          ["programsAttended", "Programs Resident Requires/Attends: [ ] Home Health [ ] Adult Day Health [ ] Hospice - Agency / Hospice Plan [ ] Other"],
          ["meaningfulDay", "Interventions: [ ] Meaningful Day"],
          ["physicalEnablers", "Physical Enablers - Strengths and Abilities / Assistance Required / Caregiver will"],
          ["treatmentRefusalPlan", "Treatment/Program/Therapy Refusal Plan - Strengths and Abilities / Assistance Required / Caregiver will"],
        ],
      },
    ],
  },
  {
    pageNumber: 7,
    sections: [
      {
        title: "Psych/Social/Cognitive Status - Sleep and Memory",
        fields: [
          ["sleepSupport", "Sleep: [ ] Sleep disturbance / [ ] Nighttime assistance needed"],
          ["sleepStrengths", "Sleep - Strengths and Abilities / Preferences and Personal Goals"],
          ["sleepAssistance", "Sleep - Assistance Required / Who Will Provide, When And How"],
          ["memoryImpairment", "Memory Impairment: [ ] Short-term [ ] Long-term [ ] Oriented to Person"],
          ["memoryStrengths", "Memory - Strengths and Abilities / Preferences"],
          ["memoryAssistance", "Memory - Assistance Required / Who Will Provide, When And How"],
          ["decisionMaking", "[ ] Impaired decision making"],
        ],
      },
    ],
  },
  {
    pageNumber: 8,
    sections: [
      {
        title: "Psych/Social/Cognitive Status - Behaviors and Supports",
        fields: [
          ["behaviors", "Behaviors checked/described: disruptive, assaultive, resistive to care, depression, anxiety, irritability, disorientation, wandering/pacing, exit seeking, hallucinations, delusions, verbally/physically agitated or aggressive, unsafe behavior, suicidal ideation, unfamiliar situations, disrobing, crying, unaware of consequences, fears/suspicions, spitting, breaks/throws things, other"],
          ["psychMeds", "[ ] Requires psychopharmacological medications - describe symptoms each medication addresses / See Current MAR"],
          ["behavioralHealthSupport", "WAC 388-76-10463 / [ ] Behavioral Health Support Crisis Plan / [ ] Counseling / Mental Health Provider / Past Behaviors"],
          ["specializedBehaviorPrograms", "DSHS Specialized Behavioral Programs: Meaningful Day, Expanded Community Services, Specialized Behavior Services, Mental Health Provider/Program / Contact info"],
          ["psychInterventions", "Interventions: [ ] Meaningful Day [ ] Other"],
          ["typicalDayNarrative", "Narrative optional - What does a typical day look like?"],
        ],
      },
    ],
  },
  {
    pageNumber: 9,
    sections: [
      {
        title: "Left Alone and Universal Precautions",
        fields: [
          ["leftAloneAbility", "Ability of Resident to Be Left Unattended"],
          ["leftAloneStrengths", "Left Alone - Strengths and Abilities"],
          ["leftAloneAssistance", "Left Alone - Assistance Required / Caregiver will"],
          ["universalPrecautions", "[ ] Always / [ ] Special Precautions / [ ] Alternative method for visitation / Mask: able or not able / sanitizer/disinfectant wipes safe / vaccinations / shared medical equipment / other"],
          ["universalPrecautionsStrengths", "Universal Precautions - Strengths and Abilities"],
          ["universalPrecautionsCaregiver", "Caregiver will always use gloves when in contact with secretions, handwashing before and after gloving, discard gloves at end of each task"],
          ["universalPrecautionsAssistance", "Universal Precautions - Assistance Required"],
        ],
      },
    ],
  },
  {
    pageNumber: 10,
    sections: [
      {
        title: "Activities of Daily Living - Ambulation/Mobility",
        fields: [
          ["functionalLimitations", "Resident functional limitations that impact ADL functioning"],
          ["locomotionRoom", "Locomotion in room/immediate living environment: [ ] Independent [ ] Supervision/Cueing [ ] Assistance Needed [ ] Totally Dependent"],
          ["locomotionOutside", "Locomotion outside immediate living environment: [ ] Independent [ ] Supervision/Cueing [ ] Assistance Needed [ ] Totally Dependent"],
          ["fallRisks", "[ ] Risks for falls / [ ] Fall prevention plan"],
          ["bedroomDoorLock", "[ ] Resident chooses bedroom door lock"],
          ["mobilityEquipment", "Equipment/Supplies / Vendor"],
          ["mobilityLimitations", "Limitations"],
          ["mobilityStrengths", "Strengths and Abilities / Evacuation addressed under Evacuation Plan"],
          ["mobilityAssistance", "Assistance Required / Caregiver will"],
        ],
      },
    ],
  },
  {
    pageNumber: 11,
    sections: [
      {
        title: "Bed Mobility/Transfer",
        fields: [
          ["transferLevel", "Transfer/Bed Mobility: [ ] Independent [ ] Supervision/Cueing [ ] Assistance Needed [ ] Totally Dependent"],
          ["skinCarePositioning", "[ ] Skin care required due to inability to position self / Equipment/Supplies"],
          ["turningRepositioning", "[ ] Turning and Repositioning needed - Frequency"],
          ["transferSafetyPlan", "[ ] Safety assessment, alternatives explored; how to keep resident safe / risks for falls / fall prevention plan / safety plan"],
          ["medicalDevices", "Medical Device(s): [ ] Enablers [ ] Hoyer Lift [ ] Transfer Pole [ ] Medical Device/Enabler Risk Assessment [ ] Nighttime care needs"],
          ["transferEquipment", "Equipment/supplies"],
          ["transferStrengths", "Strengths and Abilities / Monitoring and reporting significant changes"],
          ["transferAssistance", "Assistance Required / Caregiver will"],
        ],
      },
    ],
  },
  {
    pageNumber: 12,
    sections: [
      {
        title: "Eating and Toileting/Continence",
        fields: [
          ["eatingLevel", "Eating: [ ] Independent [ ] Supervision/Cueing [ ] Assistance Needed [ ] Totally Dependent"],
          ["specialDiet", "[ ] Special Diet/Supplements / Eating Habits / Food Allergies"],
          ["eatingEquipment", "Eating Equipment/Supplies/Procedures / Limitations"],
          ["eatingStrengths", "Eating - Strengths and Abilities / Monitoring/reporting significant changes"],
          ["eatingAssistance", "Eating - Assistance Required / Caregiver will"],
          ["toiletingLevel", "Toileting: [ ] Independent [ ] Supervision/Cueing [ ] Assistance Needed [ ] Totally Dependent / Frequency"],
          ["continenceIssues", "Continence Issues: bladder, bowel, skin care due to incontinence / Equipment/Supplies/Procedures / Limitations"],
          ["toiletingStrengths", "Toileting - Strengths and Abilities / Monitoring/reporting significant changes"],
          ["toiletingAssistance", "Toileting - Assistance Required / Caregiver will"],
        ],
      },
    ],
  },
  {
    pageNumber: 13,
    sections: [
      {
        title: "Dressing, Personal Hygiene, and Bathing",
        fields: [
          ["dressing", "Dressing: [ ] Independent [ ] Supervision/Cueing [ ] Assistance Needed [ ] Totally Dependent / Equipment/Supplies/Procedures / Limitations / Strengths / Assistance"],
          ["personalHygiene", "Personal Hygiene: own teeth, partials, dentures, oral hygiene, flossing, brushing, soaking, hair care, menses care, when/how often, equipment, limitations, strengths, assistance"],
          ["bathing", "Bathing: [ ] Independent [ ] Supervision/Cueing [ ] Assistance Needed [ ] Totally Dependent / when/how often / equipment / limitations / strengths / assistance"],
        ],
      },
    ],
  },
  {
    pageNumber: 14,
    sections: [
      {
        title: "Foot Care and Skin Care",
        fields: [
          ["footCare", "Foot Care: [ ] Independent [ ] Supervision/Cueing [ ] Assistance Needed [ ] Totally Dependent / diabetic foot care / nail care / when/how often / home health agency / equipment / limitations / strengths / assistance"],
          ["skinCare", "Skin Care: [ ] Independent [ ] Supervision/Cueing [ ] Assistance Needed [ ] Totally Dependent / skin care frequency / status / skin problems / pressure injuries / dressing changes / nurse delegated / home health / equipment / limitations / caregiver will"],
        ],
      },
    ],
  },
  {
    pageNumber: 15,
    sections: [
      {
        title: "Instrumental Activities of Daily Living",
        fields: [
          ["finances", "Managing Finances: [ ] Independent [ ] Assistance Needed [ ] Dependent / who manages finances / financial records / payee contact / strengths / assistance"],
          ["shopping", "Shopping: [ ] Independent [ ] Assistance Needed [ ] Dependent / special transportation needs / how often / equipment / limitations / strengths / assistance"],
          ["transportation", "Transportation: [ ] Independent [ ] Assistance Needed [ ] Dependent / medical services / special transportation needs / escort required / how often / equipment / limitations / strengths / assistance"],
        ],
      },
    ],
  },
  {
    pageNumber: 16,
    sections: [
      {
        title: "Activities/Social",
        fields: [
          ["activitiesSocial", "Social/cultural considerations, traditions, preferences, interests, relationships, employment support, clubs/groups/day health, special arrangements, participation issues"],
          ["activityPreferences", "Activity Preferences at a Glance: reading, audio books, storytelling, phone visits, reminiscing, current events, Bible study/church, visitors, gardening, outings, zoo/pets, exercise, cooking, chores, TV/movies, parties, arts/crafts, games, beauty time, music, employment support, community integration, other"],
          ["activityNarrative", "ACTIVITY NARRATIVE"],
          ["specialArrangements", "Special Arrangements / Participation Assistance"],
        ],
      },
    ],
  },
  {
    pageNumber: 17,
    sections: [
      {
        title: "Smoking and Case Management",
        fields: [
          ["smoking", "Smoking: [ ] Resident Smokes / Safety Concerns / Smoking Policy reviewed and signed / Storage of cigarettes/lighter / Strengths / Assistance"],
          ["caseManagement", "[ ] Resident receives Case Management / Name / Agency / Phone / Email / FAX"],
          ["cmContactedWhen", "CM will be contacted when resident needs assistive device/services, provider needs care plan help, significant changes require care plan changes"],
        ],
      },
    ],
  },
  {
    pageNumber: 18,
    sections: [
      {
        title: "Other Issues/Concerns/Problems and NCP Review",
        fields: [
          ["otherIssues", "OTHER ISSUES/CONCERNS/PROBLEMS"],
          ["reviewSchedule", "This NCP will be reviewed/revised after significant change, when plan no longer addresses needs/preferences, at request of resident/representative, or at least every twelve months"],
          ["abbreviations", "Abbreviations: ADL, DPOA, MD, W/c, AFH, D/t, PCP, CG, Hx, PRN, Dr., MAR, RND"],
          ["reviewDates", "DATE OF ORIGINAL PLAN / Dates of Review/Revision"],
        ],
      },
    ],
  },
  {
    pageNumber: 19,
    sections: [
      {
        title: "Negotiated Care Plan Review and Approval",
        fields: [
          ["involvedInDevelopment", "INVOLVED IN NCP DEVELOPMENT: [ ] Resident [ ] Resident Representative [ ] Parent [ ] Health Professional [ ] Other"],
          ["approvingPerson", "PERSON APPROVING PLAN"],
          ["providerSignature", "PROVIDER - Signature / Date"],
          ["residentSignature", "RESIDENT - Signature / Date"],
          ["residentRepresentativeSignature", "RESIDENT REPRESENTATIVE - Signature / Date"],
          ["residentVerbalAgreement", "[ ] Resident verbally agreed to NCP - Date"],
          ["sentToDshs", "[] NCP sent to DSHS CM on"],
          ["residentRecommendations", "Resident Recommendations"],
          ["signatureNote", "The person signing writes the date they actually read and agreed to the plan. If verbally agreed, note name/role, date read, and recommended changes."],
        ],
      },
    ],
  },
];

const ncpPageOneIndexLinks = [
  "Activities/Social",
  "Allergies",
  "Ambulation/Mobility",
  "Bathing",
  "Bed Mobility/Transfer",
  "Behavior",
  "Case Management",
  "Communication",
  "Decision Making",
  "Dressing",
  "Eating",
  "Falls (Ambulation)",
  "Falls (Bed)",
  "Finances",
  "Foot Care",
  "Health Indicators",
  "Left Alone",
  "Medication Management",
  "Memory",
  "Mental/Phy. Health/Diag.",
  "NCP Review/Signatures",
  "Other Issues/Concerns",
  "Pain",
  "Personal Hygiene",
  "Shopping",
  "Skin Care",
  "Sleep",
  "Smoking",
  "Specialized Beh. Prog.",
  "Toilet Use/Continence",
  "Transportation",
  "Treat/Prog/Therapies",
  "Universal Precautions",
  "Vision",
];

const medicationManagementRouteOptions = [
  "Oral",
  "Topical",
  "Eye drops/ointment",
  "Inhalers",
  "Sprays",
  "Injections",
  "Allergy Kits",
  "Suppositories",
  "Other",
];

const ncpPageThreeMedicationRouteOptions = medicationManagementRouteOptions.slice(0, 5);
const ncpPageFourMedicationRouteOptions = medicationManagementRouteOptions.slice(5);
const injectionSupportOptions = [
  "Resident",
  "Surrogate",
  "By Family",
  "Licensed Professional",
  "Qualified CG under Nurse Delegation",
  "(Insulin Only)",
];
const healthIndicatorOptions = [
  "Pain",
  "Pain Impact:",
  "Weight Loss/Gain",
  "Current Weight:",
  "Current Height:",
  "Vital Signs",
  "Hospitalization or Emergency Visits",
  "Other:",
];
const treatmentTypeOptions = [
  "Oxygen Use - Vendor:",
  "Dialysis - Health Provider:",
  "Blood Thinners",
  "INR/LAB - Health Provider:",
  "Easily bruised/Anti coagulation therapy",
  "Blood Glucose Monitoring",
  "Injection",
  "CPAP/BIPAP",
  "Nebulizer",
];
const treatmentTherapyOptions = [
  "Range of Motion",
  "PT/OT/ST",
  "Nurse Delegation for",
  "Treatments/Therapies - Tasks:",
  "Other:",
];
const treatmentProgramOptions = [
  "Home Health",
  "Adult Day Health",
  "Hospice - Agency:",
  "Hospice Plan",
  "Other:",
];
const psychStatusCells = [
  {
    className: "ncp-psych-group ncp-psych-sleep",
    lines: [
      { text: "Sleep", section: true },
      { text: "Sleep disturbance", blue: true, checkbox: true },
      { text: "Nighttime", suffix: " assistance needed", blue: true, checkbox: true },
    ],
  },
  {
    className: "ncp-psych-group ncp-psych-memory",
    lines: [
      { text: "Memory Impairment", section: true },
      { text: "Short-term", blue: true, checkbox: true },
      { text: "Long-term", blue: true, checkbox: true },
      { text: "Orientated to Person", checkbox: true },
    ],
  },
  {
    className: "ncp-psych-group ncp-psych-decision",
    lines: [
      { text: "If any behavior is checked, describe in the column to the right.", note: true },
      { text: "Impaired decision making", blue: true, checkbox: true },
    ],
  },
  ...[
    "Disruptive behavior",
    "Assaultive",
    "Resistive to care",
    "Depression",
    "Anxiety",
    "Irritability",
    "Disorientation",
    "Wandering in home/Pacing",
    "Exit seeking",
    "Hallucinations",
    "Delusions",
    "Verbally agitated/aggressive",
    "Physically agitated/aggressive",
    "Inappropriate or unsafe behavior",
    "Suicidal Ideation",
  ].map((text) => ({
    className: "ncp-psych-row",
    lines: [{ text, blue: text !== "Wandering in home/Pacing", blueText: text === "Wandering in home/Pacing" ? "Wandering in home" : null, suffix: text === "Wandering in home/Pacing" ? "/Pacing" : "", checkbox: true }],
  })),
];
const psychPageEightBehaviorRows = [
  "Difficulty in new or unfamiliar situations",
  "Disrobing",
  "Weeping/Crying",
  "Unaware of Consequences",
  "Unrealistic fears and suspicions",
  "Inappropriate spitting",
  "Breaks/throws things",
  "Other",
];
const psychPageEightSupportLines = [
  { text: "Requires psychopharmacological medications. If checked, describe symptoms each medication is addressing) See Current MAR ", checkbox: true, strong: true },
  { text: "WAC 388-76-10463", suffix: " (link)", blue: true, indent: true },
  { text: "Behavioral Health Support Crisis Plan (See attached crisis plan)", checkbox: true },
  { text: "Counseling", checkbox: true },
  { text: "Mental Health Provider (MHP):" },
  { text: "Past Behaviors:", spacer: true },
];
const specializedBehaviorProgramLines = [
  { text: "DSHS Specialized Behavioral Programs:", strong: true },
  { text: "Meaningful Day", checked: true },
  { text: "Expanded Community Services" },
  { text: "(HCS ONLY)" },
  { text: "Specialized Behavior Services (HCS ONLY)" },
  { text: "Mental Health Provider/Program" },
  { text: "Contact info:", spacer: true },
];
const universalPrecautionLines = [
  { text: "Always" },
  { text: "Special Precautions:", blue: true },
  { text: "Alternative method for visitation" },
  { text: "Mask - Resident is:", noBox: true },
  { text: "Able to wear a mask", nested: true },
  { text: "Not able to wear a mask", nested: true },
  { text: "Resident is safe to have sanitizer or disinfectant wipes left out for caregiver and client use." },
];
const universalPrecautionPageTenLines = [
  { text: "Resident has been/or is up to date on", suffix: " vaccinations:", blueSuffix: true },
  { text: "Resident shares the following medical equipment;" },
  { text: "Other:" },
];

const countNcpLabelChecks = (label) => String(label || "").match(/\[\s*\]/g)?.length || 0;

const createNegotiatedPlanPayload = ({ assessmentId, recordId, initialValues = {} }) => ({
  version: "AFH-HCS-NCP-3.12.2024",
  assessmentId,
  recordId,
  residentId: assessmentId?.replace(/^ap-/, "") || "",
  status: "draft",
  pages: negotiatedTemplatePagesDefinition.map((page) => ({
    pageNumber: page.pageNumber,
    title: `Page ${page.pageNumber} of 19`,
    sections: page.sections.map((section, sectionIndex) => ({
      id: `page-${page.pageNumber}-section-${sectionIndex + 1}`,
      title: section.title,
      fields: section.fields.map(([key, label], fieldIndex) => ({
        id: `page-${page.pageNumber}-section-${sectionIndex + 1}-field-${fieldIndex + 1}-${key}`,
        key,
        label,
        value: initialValues[key] || "",
        checks: Array.from({ length: countNcpLabelChecks(label) }, (_, index) => ({
          index,
          checked: false,
        })),
      })),
    })),
  })),
});

const cloneNegotiatedPlanPayload = (payload) => JSON.parse(JSON.stringify(payload));

const findNegotiatedPayloadField = (payload, key) => {
  for (const page of payload?.pages || []) {
    for (const section of page.sections || []) {
      const field = (section.fields || []).find((item) => item.key === key);
      if (field) return field;
    }
  }
  return null;
};

const ensureNegotiatedPlanPayloadFields = (payload) => {
  const normalized = createNegotiatedPlanPayload({
    assessmentId: payload?.assessmentId,
    recordId: payload?.recordId,
  });
  const existingById = new Map();
  const existingByKey = new Map();
  (payload?.pages || []).forEach((page) => {
    (page.sections || []).forEach((section) => {
      (section.fields || []).forEach((field) => {
        if (field.id) existingById.set(field.id, field);
        if (!existingByKey.has(field.key)) existingByKey.set(field.key, field);
      });
    });
  });

  return {
    ...payload,
    pages: normalized.pages.map((page) => ({
      ...page,
      sections: page.sections.map((section) => ({
        ...section,
        fields: section.fields.map((field) => {
          const existing = existingById.get(field.id) || existingByKey.get(field.key);
          return existing ? { ...field, ...existing, id: field.id, key: field.key, label: field.label } : field;
        }),
      })),
    })),
  };
};

const updateNegotiatedPayloadField = (payload, key, updater) => ({
  ...payload,
  pages: (payload.pages || []).map((page) => ({
    ...page,
    sections: (page.sections || []).map((section) => ({
      ...section,
      fields: (section.fields || []).map((field) => (
        field.key === key ? updater(field) : field
      )),
    })),
  })),
});

export default function AssessmentRecordDetailPage() {
  const { assessmentId, recordId } = useParams();
  const [assessment, setAssessment] = useState(null);
  const [record, setRecord] = useState(null);
  const [recordName, setRecordName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [customQuestions, setCustomQuestions] = useState([]);
  const [customQuestion, setCustomQuestion] = useState("");
  const [customDocuments, setCustomDocuments] = useState([]);
  const [customDocument, setCustomDocument] = useState("");
  const [careCircleMembers, setCareCircleMembers] = useState(defaultCareCircleMembers);
  const [careProviderStaffMembers, setCareProviderStaffMembers] = useState(defaultCareProviderStaff);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [meetNotes, setMeetNotes] = useState("");
  const [recordNotes, setRecordNotes] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preliminaryPreviewOpen, setPreliminaryPreviewOpen] = useState(false);
  const [negotiatedPreviewOpen, setNegotiatedPreviewOpen] = useState(false);
  const [dailyNoteDrawerOpen, setDailyNoteDrawerOpen] = useState(false);
  const [dailyNoteEditingNote, setDailyNoteEditingNote] = useState(null);
  const [dailyNoteDate, setDailyNoteDate] = useState(dateInputValue(new Date()));
  const [dailyNoteCategory, setDailyNoteCategory] = useState("Behavior");
  const [dailyNoteOtherCategory, setDailyNoteOtherCategory] = useState("");
  const [dailyNoteRichText, setDailyNoteRichText] = useState("");
  const [dailyNotePhotos, setDailyNotePhotos] = useState([]);
  const [selectedDailyNote, setSelectedDailyNote] = useState(null);
  const [probationAssignedStaff, setProbationAssignedStaff] = useState(null);
  const [probationStaffDrawer, setProbationStaffDrawer] = useState(null);
  const [preliminaryPlanDescription, setPreliminaryPlanDescription] = useState("");
  const [preliminaryGenerationLocked, setPreliminaryGenerationLocked] = useState(false);
  const [preliminaryPlanHistory, setPreliminaryPlanHistory] = useState([]);
  const [preliminaryAttachedFiles, setPreliminaryAttachedFiles] = useState([]);
  const [negotiatedPlanPayload, setNegotiatedPlanPayload] = useState(() => createNegotiatedPlanPayload({
    assessmentId,
    recordId,
    initialValues: {
      providerName: "Olreadia Care",
      dateNcpStarted: assessment?.startDate || "",
      residentName: processResidentName(assessment),
      primaryLanguage: "English",
      speaksEnglish: "Yes",
      interpreterNeeded: "No",
    },
  }));
  const [negotiatedPlanHistory, setNegotiatedPlanHistory] = useState([]);
  const [probationNoteRange, setProbationNoteRange] = useState({
    start: monthStartInputValue(new Date()),
    end: monthEndInputValue(new Date()),
  });
  const [visibleProbationNoteCount, setVisibleProbationNoteCount] = useState(10);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    Promise.all([
      careService.assessmentProcesses.get(assessmentId),
      careService.assessmentRecords.byProcessAndType(assessmentId, recordId),
    ])
      .then(([process, recordData]) => {
        if (!isMounted) return;
        const matchedRecord = processRecords(process).find((item) => recordSlug(item) === recordId || assessmentRecordSlug(recordLabel(item)) === recordId);
        setAssessment(process || null);
        setRecord(recordData || {});
        setRecordName(recordData?.name || recordData?.title || recordLabel(matchedRecord) || recordId);
        setError("");
      })
      .catch((err) => {
        if (!isMounted) return;
        setAssessment(null);
        setRecord(null);
        setRecordName("");
        setError(err?.response?.data?.message || err?.message || "Unable to load assessment record.");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [assessmentId, recordId]);

  useEffect(() => {
    if (!record) return;
    setMeetNotes(record.notes || "");
    setRecordNotes(record.notes || "");
    setCareCircleMembers(record.careCircle?.length ? record.careCircle : defaultCareCircleMembers);
    setCareProviderStaffMembers(record.careProviderStaff?.length ? record.careProviderStaff : defaultCareProviderStaff);
    setPreliminaryPlanDescription(record.description || record.preliminaryPlanDescription || "");
    setPreliminaryGenerationLocked(Boolean(record.generationLocked || record.preventFurtherGeneration));
    setPreliminaryPlanHistory(record.history || record.planHistory || []);
    setPreliminaryAttachedFiles(record.attachedFiles || []);
    setNegotiatedPlanHistory(record.history || record.planHistory || []);
    setProbationAssignedStaff(record.assignedStaff || null);
  }, [record]);

  if (loading) {
    return (
      <CareShell className="dashboard-content residents-page assessments-page">
        <Panel title="Assessment Record">Loading assessment record...</Panel>
      </CareShell>
    );
  }
  if (error) {
    return (
      <CareShell className="dashboard-content residents-page assessments-page">
        <div className="residents-actions">
          <Link className="mini-btn" to={`/assessments/${assessmentId}`}><i className="ti ti-arrow-left" /> Assessment</Link>
        </div>
        <p className="form-error">{error}</p>
      </CareShell>
    );
  }
  if (!assessment) return <Navigate to="/assessments" replace />;
  if (!recordName) return <Navigate to={`/assessments/${assessment.id}`} replace />;

  const isMeetAndGreet = recordId === "meet-and-greet";
  const isClientOnboarding = recordId === "client-onboarding";
  const isResidentProbation = recordId === "resident-30-day-probation";
  const isAnnualResidencyReview = recordId === "annual-residency-progress";
  const isPreliminaryPlan = recordId === "preliminary-plan";
  const isNegotiatedPlan = recordId === "negotiated-plan";
  const isCareMeetingRecord = isMeetAndGreet || isClientOnboarding;
  const canPreviewDocument = record && recordId === "initial-assessment";
  const probationNotes = (record?.dailyNotes || [])
    .filter((note) => isWithinDateRange(note.date, probationNoteRange.start, probationNoteRange.end))
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const visibleProbationNotes = probationNotes.slice(0, visibleProbationNoteCount);
  const baseProbationStaff = probationAssignedStaff || record?.assignedStaff || [];
  const assignedProbationStaff = baseProbationStaff.map((staff, originalIndex) => ({ ...staff, originalIndex })).sort(
    (a, b) => new Date(b.to).getTime() - new Date(a.to).getTime(),
  );

  const addCustomQuestion = () => {
    const nextQuestion = customQuestion.trim();
    if (!nextQuestion) return;
    setCustomQuestions((current) => [...current, nextQuestion]);
    setCustomQuestion("");
  };

  const addCustomDocument = () => {
    const nextDocument = customDocument.trim();
    if (!nextDocument) return;
    setCustomDocuments((current) => [...current, nextDocument]);
    setCustomDocument("");
  };

  const addCustomCareCircleMember = () => {
    setCareCircleMembers((current) => [...current, ["", ""]]);
  };

  const removeCareCircleMember = (indexToRemove) => {
    setCareCircleMembers((current) => current.filter((_, index) => index !== indexToRemove));
  };

  const addCareProviderStaffMember = () => {
    setCareProviderStaffMembers((current) => [...current, ["", ""]]);
  };

  const removeCareProviderStaffMember = (indexToRemove) => {
    setCareProviderStaffMembers((current) => current.filter((_, index) => index !== indexToRemove));
  };

  const addUploadedDocuments = (event) => {
    const files = Array.from(event.target.files || []).map((file) => ({ fileName: file.name, name: file.name }));
    if (!files.length) return;
    setUploadedDocuments((current) => [...current, ...files]);
    event.target.value = "";
  };

  const updateUploadedDocumentName = (indexToUpdate, name) => {
    setUploadedDocuments((current) =>
      current.map((document, index) => (index === indexToUpdate ? { ...document, name } : document)),
    );
  };

  const removeUploadedDocument = (indexToRemove) => {
    setUploadedDocuments((current) => current.filter((_, index) => index !== indexToRemove));
  };

  const addPreliminaryAttachedFiles = (event) => {
    const addedAt = new Date().toISOString();
    const files = Array.from(event.target.files || []).map((file) => ({
      type: file.type?.startsWith("image/") ? "photo" : "document",
      name: file.name,
      caption: file.name,
      addedAt,
      previewUrl: file.type?.startsWith("image/") ? URL.createObjectURL(file) : "",
    }));
    if (!files.length) return;
    setPreliminaryAttachedFiles((current) => [...current, ...files]);
    event.target.value = "";
  };

  const updatePreliminaryAttachedFile = (indexToUpdate, field, value) => {
    setPreliminaryAttachedFiles((current) =>
      current.map((file, index) => (index === indexToUpdate ? { ...file, [field]: value } : file)),
    );
  };

  const removePreliminaryAttachedFile = (indexToRemove) => {
    setPreliminaryAttachedFiles((current) => current.filter((_, index) => index !== indexToRemove));
  };

  const addDailyNotePhotos = (event) => {
    const files = Array.from(event.target.files || []).map((file) => ({
      fileName: file.name,
      description: file.name,
      previewUrl: URL.createObjectURL(file),
    }));
    if (!files.length) return;
    setDailyNotePhotos((current) => [...current, ...files]);
    event.target.value = "";
  };

  const updateDailyNotePhotoDescription = (indexToUpdate, description) => {
    setDailyNotePhotos((current) =>
      current.map((photo, index) => (index === indexToUpdate ? { ...photo, description } : photo)),
    );
  };

  const removeDailyNotePhoto = (indexToRemove) => {
    setDailyNotePhotos((current) => current.filter((_, index) => index !== indexToRemove));
  };

  const openAddDailyNoteDrawer = () => {
    setDailyNoteEditingNote(null);
    setDailyNoteDate(dateInputValue(new Date()));
    setDailyNoteCategory("Behavior");
    setDailyNoteOtherCategory("");
    setDailyNoteRichText("");
    setDailyNotePhotos([]);
    setDailyNoteDrawerOpen(true);
  };

  const openEditDailyNoteDrawer = (note) => {
    const knownCategory = dailyNoteCategories.includes(note.category);
    setDailyNoteEditingNote(note);
    setDailyNoteDate(dateInputValue(note.date));
    setDailyNoteCategory(knownCategory ? note.category : "Other");
    setDailyNoteOtherCategory(knownCategory ? "" : note.category);
    setDailyNoteRichText(note.notes || `<p>${note.summary || ""}</p>`);
    setDailyNotePhotos(note.photos || []);
    setSelectedDailyNote(null);
    setDailyNoteDrawerOpen(true);
  };

  const addProbationStaff = () => {
    setProbationStaffDrawer({
      index: null,
      values: { name: "", role: "", from: `${dateInputValue(new Date())}T07:00:00`, to: `${dateInputValue(new Date())}T15:00:00` },
    });
  };

  const removeProbationStaff = (indexToRemove) => {
    setProbationAssignedStaff((current) => (current || record?.assignedStaff || []).filter((_, index) => index !== indexToRemove));
    setProbationStaffDrawer(null);
  };

  const editProbationStaff = (staff) => {
    const { originalIndex, ...values } = staff;
    setProbationStaffDrawer({ index: originalIndex, values });
  };

  const updateProbationStaffDrawerField = (field, value) => {
    setProbationStaffDrawer((current) => current ? { ...current, values: { ...current.values, [field]: value } } : current);
  };

  const saveProbationStaffDrawer = () => {
    if (!probationStaffDrawer) return;
    setProbationAssignedStaff((current) => {
      const nextStaff = [...(current || record?.assignedStaff || [])];
      if (probationStaffDrawer.index === null) {
        nextStaff.push(probationStaffDrawer.values);
      } else {
        nextStaff[probationStaffDrawer.index] = probationStaffDrawer.values;
      }
      return nextStaff;
    });
    setProbationStaffDrawer(null);
  };

  const confirmMarkCompleted = () => {
    Modal.confirm({
      title: `Mark ${recordName} as completed?`,
      content: `This will move the ${recordName} record to completed status for this assessment process.`,
      okText: "Mark Completed",
      cancelText: "Keep Editing",
      okButtonProps: { className: "assessment-confirm-primary" },
    });
  };

  const planDescription = preliminaryPlanDescription || record?.description || "";
  const generationLocked = preliminaryGenerationLocked || Boolean(record?.generationLocked);
  const negotiatedTemplateDefaults = {
    providerName: "Olreadia Care",
    dateNcpStarted: assessment.startDate,
    movedInDate: "2024-05-08",
    residentName: processResidentName(assessment),
    primaryLanguage: "English",
    speaksEnglish: "Yes",
    interpreterNeeded: "No",
    allergies: "Verify against state assessment and medication list.",
    legalDocuments: "PoA documentation received; confirm Advanced Directives and POLST status.",
    specialtyNeeds: "No specialty need confirmed yet; monitor dementia, mental health, and developmental disability indicators from state assessment.",
    evacuationAssistance: "Assistance required. Resident needs standby assistance and clear cueing.",
    evacuationSafetyInstructions: "Keep walker within reach, remove barriers, and guide resident with calm one-step instructions.",
    evacuationCaregiverInstructions: "Caregiver will keep walkways clear and assist resident through the safest exit route.",
    medicalDiagnosis: "Review state assessment diagnosis and medication history. Early notes show transfer fatigue and medication prompting needs.",
    contact1: "Amanda Parker - Social Worker / Case Manager.",
    contact1Name: "Amanda Parker",
    contact1Relationship: "Social Worker / Case Manager",
    contact2: "Elaine Johnson - Daughter / PoA, preferred family contact.",
    contact2Name: "Elaine Johnson",
    contact2Relationship: "Daughter / PoA",
    contact3: "Robert Johnson - Son.",
    contact3Name: "Robert Johnson",
    contact3Relationship: "Son",
    communicationModes: "Resident communicates verbally and responds to calm explanations.",
    selfUnderstood: "Resident makes needs known verbally.",
    understandsOthers: "Resident understands short, respectful instructions.",
    communicationStrengths: "Resident is receptive to routine, music, and family phone calls.",
    communicationAssistance: "Caregiver will use calm tone, one-step cueing, and allow time for response.",
    medicationAssistanceNeeds: "Medication prompts and monitoring are needed.",
    medicationManagementType: "Medication administration/support per MAR and physician orders.",
    medicationAssistance: "Caregiver will follow the five rights plus documentation and report adverse reactions or refusal.",
    medicationRefusalPlan: "If resident refuses medication, document refusal and notify PCP at the time of refusal.",
    healthIndicatorMonitoring: "Monitor hydration, transfer fatigue, mobility changes, welfare concerns, and pain indicators.",
    monitoringReporting: "Report significant changes to PCP, PoA/family, social worker, and care team as appropriate.",
    treatmentTypes: "Confirm PT/OT/ST, nurse delegation, home health, oxygen, glucose monitoring, wound care, and hospice status.",
    meaningfulDay: "Use meaningful day routines including warm tea, quiet breakfast, music after lunch, and family contact.",
    sleepSupport: "Continue observing sleep and nighttime assistance needs.",
    behaviors: "No disruptive behavior observed in current notes. Continue documenting wandering, agitation, refusal, mood, and safety concerns.",
    typicalDayNarrative: "Quiet morning routine, coffee, light breakfast, music after lunch, warm tea before lunch, and family call after dinner.",
    leftAloneAbility: "Do not leave resident unattended until ability is confirmed by assessment and provider review.",
    universalPrecautionsCaregiver: "Caregiver will use standard precautions, hand hygiene, gloves as appropriate, and report infection concerns.",
    functionalLimitations: "Mild transfer fatigue and walker use noted.",
    locomotionRoom: "Walker with standby support.",
    fallRisks: "Maintain walker within reach and cue non-slip footwear.",
    mobilityEquipment: "Walker; confirm additional equipment needs.",
    mobilityAssistance: "Caregiver will provide standby transfer and mobility support.",
    transferLevel: "Assistance needed / standby support.",
    transferAssistance: "Caregiver will monitor fatigue and provide safe transfer cueing.",
    eatingLevel: "Resident eats by mouth; confirm assistance level and special diet.",
    specialDiet: "Low-salt meals preferred; confirm physician diet orders.",
    continenceIssues: "Continue observing continence and toileting patterns.",
    dressing: "Confirm self/assist/supervise level for shoes, stockings, and clothing.",
    personalHygiene: "Confirm oral hygiene, grooming, hair care, shaving, nails, dentures, and assistance level.",
    bathing: "Confirm shower/bed bath preference, bath days, and assistance level.",
    skinCare: "Monitor skin concerns, pressure areas, and any dressing or nurse delegation needs.",
    finances: "Confirm who manages finances and financial records.",
    shopping: "Confirm shopping assistance and transportation needs.",
    transportation: "Confirm medical transportation, escort requirements, and frequency.",
    activitiesSocial: "Resident prefers quiet routines, music, warm tea, and family contact.",
    activityPreferences: "Music, phone conversation/visiting, quiet one-on-one support, and gentle walking as tolerated.",
    caseManagement: "Amanda Parker - Social Worker / Case Manager.",
    cmContactedWhen: "Contact CM when care needs change, assistive devices are needed, or plan updates require coordination.",
    reviewSchedule: "Review after significant change, when plan no longer meets needs/preferences, at resident/representative request, and at least every twelve months.",
    involvedInDevelopment: "Resident, resident representative/PoA, provider, social worker/case manager, and care team.",
    dateCompleted: "Pending completion.",
    dateDischarged: "Not applicable.",
    evacuationIndependent: "Not selected.",
    assessmentSectionIndex: "Complete all applicable NCP sections from state assessment, preliminary service plan, MaG, onboarding, annual progress, and daily notes.",
    hearingProblems: "Confirm hearing-aid/deafness status from assessment and resident/family interview.",
    visionProblems: "Confirm eyesight, glasses, blindness, cataracts, and legal blindness status.",
    phoneAbility: "Confirm whether resident uses phone independently and whether resident has own phone.",
    preferredLanguageComments: "English preferred; confirm any communication accommodations.",
    communicationComments: "Continue documenting communication preferences and changes noted by the care team.",
    medicationAllergies: "Verify medication allergies from MAR/state assessment.",
    psychopharmacologicMeds: "Confirm psychopharmacologic medications from MAR and behavior section.",
    marReference: "See MAR for current medications, dosage, frequency, and route.",
    medsOrderedBy: "Confirm ordering provider.",
    medsDeliveredBy: "Confirm pharmacy/delivery source.",
    pharmacyPacked: "Confirm whether medications are pharmacy packed.",
    medicationRoutes: "Confirm oral, topical, eye drops, inhalers, sprays, injections, allergy kits, suppositories, and other routes.",
    injectionSupport: "No injection support identified yet; confirm against MAR and nurse delegation.",
    nurseDelegation: "Confirm whether nurse delegation is required.",
    medicationStrengths: "Resident responds to routine medication prompts.",
    medicationOutsideAfhStrengths: "Confirm resident strengths when away from AFH.",
    medicationOutsideAfh: "Medication plan outside AFH to be confirmed with PoA/family and pharmacy.",
    medicationRefusalStrengths: "Resident can express preferences; monitor refusals.",
    healthStrengths: "Resident participates in calm routines and accepts reminders.",
    healthAssistance: "Caregiver monitors changes and documents concerns.",
    allergySubstanceReaction: "Verify allergy substances and reactions from state assessment.",
    rangeOfMotionTherapies: "Confirm ROM, PT/OT/ST, and delegated therapy tasks.",
    treatmentStrengths: "Resident can participate with cueing as tolerated.",
    treatmentAssistance: "Caregiver will follow provider and therapy instructions.",
    programsAttended: "Confirm community programs, home health, adult day health, hospice, and other services.",
    physicalEnablers: "Confirm any physical enablers and risk assessments.",
    treatmentRefusalPlan: "Document refusals and notify responsible provider/team as appropriate.",
    sleepStrengths: "Resident benefits from quiet nighttime routine.",
    sleepAssistance: "Caregiver will monitor nighttime needs and document changes.",
    memoryImpairment: "Confirm memory impairment details from assessment.",
    memoryStrengths: "Resident responds to familiar routines.",
    memoryAssistance: "Caregiver will provide cueing and reassurance.",
    decisionMaking: "Confirm decision-making support needs.",
    psychMeds: "Confirm psychopharmacological medication use and symptoms addressed.",
    behavioralHealthSupport: "Confirm crisis plan, counseling, mental health provider, and past behaviors.",
    specializedBehaviorPrograms: "Meaningful Day noted; confirm other DSHS specialized behavior programs.",
    psychInterventions: "Use calm routines, meaningful day activities, and resident-centered redirection.",
    leftAloneStrengths: "Resident can communicate needs but requires safety review before being left alone.",
    leftAloneAssistance: "Caregiver will provide supervision level indicated by assessment.",
    universalPrecautions: "Use standard precautions; confirm mask, vaccination, sanitizer, shared equipment, and special precautions.",
    universalPrecautionsStrengths: "Resident can follow simple infection-control cues.",
    universalPrecautionsAssistance: "Assist resident with precautions as needed.",
    locomotionOutside: "Confirm outdoor/community mobility level.",
    bedroomDoorLock: "Confirm resident choice.",
    mobilityLimitations: "Mild fatigue observed with transfers.",
    mobilityStrengths: "Resident can ambulate with walker and standby support.",
    skinCarePositioning: "No current positioning skin issue identified; monitor.",
    turningRepositioning: "Confirm repositioning frequency if needed.",
    transferSafetyPlan: "Use walker, clear pathways, and calm cueing.",
    medicalDevices: "Walker currently identified; confirm lift/enabler needs.",
    transferEquipment: "Walker; confirm gait belt, transfer board, lift, or other supplies.",
    transferStrengths: "Resident can follow transfer cues.",
    eatingEquipment: "Confirm clothing protector, adaptive utensils, supplements, thickened liquids, and hydration plan.",
    eatingStrengths: "Resident participates in meals and prefers warm tea.",
    eatingAssistance: "Caregiver will cue hydration and follow diet orders.",
    toiletingLevel: "Confirm toileting assistance level and frequency.",
    toiletingStrengths: "Resident can communicate toileting needs; confirm.",
    toiletingAssistance: "Caregiver will provide assistance per assessed level.",
    footCare: "Confirm diabetic foot care, nail care, podiatry, home health, equipment, and assistance.",
    activityNarrative: "Resident benefits from predictable low-stimulation daily rhythm.",
    specialArrangements: "Confirm special arrangements and participation assistance.",
    smoking: "Confirm smoking status, policy review, safety concerns, and storage.",
    otherIssues: "Other issues, concerns, or problems to be updated from assessment and care team review.",
    abbreviations: "ADL, DPOA, MD, W/c, AFH, D/t, PCP, CG, Hx, PRN, Dr., MAR, RND.",
    reviewDates: "Original plan and review/revision dates pending final completion.",
    approvingPerson: "To be confirmed during final review.",
    providerSignature: "Pending signature/date.",
    residentSignature: "Pending signature/date.",
    residentRepresentativeSignature: "Pending signature/date.",
    residentVerbalAgreement: "Not yet recorded.",
    sentToDshs: "Not yet sent.",
    residentRecommendations: "Confirm resident recommendations during final review.",
    signatureNote: "Signer writes the date they actually read and agreed to the plan; document verbal agreement details if applicable.",
  };
  const normalizedNegotiatedPlanPayload = ensureNegotiatedPlanPayloadFields(negotiatedPlanPayload);
  const negotiatedTemplatePages = (normalizedNegotiatedPlanPayload.pages || []).map((page) => ({
    ...page,
    sections: (page.sections || []).map((section) => ({
      ...section,
      fields: (section.fields || []).map((field) => ({
        ...field,
        checks: field.checks || [],
        value: field.value || negotiatedTemplateDefaults[field.key] || "",
      })),
    })),
  }));
  const negotiatedPlanApiPayload = {
    ...normalizedNegotiatedPlanPayload,
    pages: negotiatedTemplatePages.map((page) => ({
      pageNumber: page.pageNumber,
      title: page.title || `Page ${page.pageNumber} of 19`,
      sections: page.sections.map((section) => ({
        id: section.id,
        title: section.title,
        fields: section.fields.map((field) => ({
          id: field.id,
          key: field.key,
          label: field.label,
          value: field.value,
          checks: field.checks || [],
        })),
      })),
    })),
  };
  const negotiatedPayloadField = (key) => findNegotiatedPayloadField(normalizedNegotiatedPlanPayload, key);
  const negotiatedFieldValue = (key) => negotiatedPayloadField(key)?.value || negotiatedTemplateDefaults[key] || "";
  const ncpCheckValue = (key, index) => Boolean(negotiatedPayloadField(key)?.checks?.[index]?.checked);
  const ncpCheckText = (key, index) => ncpCheckValue(key, index) ? "[x]" : "[ ]";
  const ncpDocumentCheckSequence = negotiatedTemplatePages.flatMap((page) =>
    page.sections.flatMap((section) =>
      section.fields.flatMap((field) => {
        const checkboxCount = field.checks?.length || countNcpLabelChecks(field.label);
        return Array.from({ length: checkboxCount }, (_, index) => `${field.key}:${index}`);
      }),
    ),
  );
  const makeNcpCheckboxesFillable = (html) => {
    let checkboxIndex = 0;
    return html.replace(/\[\s*(x?)\s*\]/gi, (_, checked) => {
      const stateKey = ncpDocumentCheckSequence[checkboxIndex];
      const [key, index] = stateKey ? stateKey.split(":") : [];
      const isChecked = Boolean(checked) || Boolean(key && ncpCheckValue(key, Number(index)));
      checkboxIndex += 1;
      return `<input class="ncp-fillable-check" type="checkbox" aria-label="checkbox"${isChecked ? " checked" : ""} />`;
    });
  };
  const ncpIndexHtml = ncpPageOneIndexLinks.map((link) => `<span>${escapeHtml(link)}</span>`).join("");
  const medicationRouteCheckboxHtml = ncpPageThreeMedicationRouteOptions.map((option) => `<p>[ ] ${escapeHtml(option)}</p>`).join("");
  const pageFourMedicationRouteCheckboxHtml = ncpPageFourMedicationRouteOptions.map((option) => `<p>[ ] ${escapeHtml(option)}</p>`).join("");
  const injectionSupportCheckboxHtml = injectionSupportOptions.map((option) => `<p class="${option === "(Insulin Only)" ? "ncp-indent-line" : "ncp-nested-checkbox"}">${option === "(Insulin Only)" ? "" : "[ ] "}${escapeHtml(option)}</p>`).join("");
  const healthIndicatorCheckboxHtml = healthIndicatorOptions.map((option) => {
    const isNested = option === "Pain Impact:" || option === "Current Weight:" || option === "Current Height:";
    const isBlue = option === "Pain" || option === "Other:";
    return `<p class="${isNested ? "ncp-nested-checkbox" : ""}">${isNested ? "" : "[ ] "}<span class="${isBlue ? "ncp-blue" : ""}">${escapeHtml(option)}</span></p>`;
  }).join("");
  const treatmentTypeCheckboxHtml = treatmentTypeOptions.map((option) => {
    const isNested = option === "INR/LAB - Health Provider:" || option === "Easily bruised/Anti coagulation therapy";
    return `<p class="${isNested ? "ncp-nested-checkbox" : ""}">[ ] ${escapeHtml(option)}</p>`;
  }).join("");
  const treatmentTherapyCheckboxHtml = treatmentTherapyOptions.map((option) => {
    const isBlue = option === "Range of Motion" || option === "Other:";
    const isLabelOnly = option === "Treatments/Therapies - Tasks:";
    return `<p>${isLabelOnly ? "" : "[ ] "}<span class="${isBlue ? "ncp-blue" : ""}">${escapeHtml(option)}</span></p>`;
  }).join("");
  const treatmentProgramCheckboxHtml = treatmentProgramOptions.map((option) => {
    const isNested = option === "Hospice Plan";
    const isBlue = option === "Home Health" || option === "Hospice - Agency:";
    return `<p class="${isNested ? "ncp-nested-checkbox" : ""}">[ ] <span class="${isBlue ? "ncp-blue" : ""}">${escapeHtml(option)}</span></p>`;
  }).join("");
  const renderPsychLineHtml = (line) => {
    if (line.section) return `<p class="ncp-psych-section-line">${escapeHtml(line.text)}</p>`;
    if (line.note) return `<p class="ncp-psych-note-line">${escapeHtml(line.text)}</p>`;
    const labelHtml = line.blueText
      ? `<span class="ncp-blue">${escapeHtml(line.blueText)}</span>${escapeHtml(line.suffix || "")}`
      : `<span class="${line.blue ? "ncp-blue" : ""}">${escapeHtml(line.text)}</span>${escapeHtml(line.suffix || "")}`;
    return `<p class="ncp-psych-check-line">${line.checkbox ? "[ ]&nbsp;&nbsp;" : ""}${labelHtml}</p>`;
  };
  const psychStatusRowsHtml = psychStatusCells.map((cell) => `<div class="${cell.className}">${cell.lines.map(renderPsychLineHtml).join("")}</div>`).join("");
  const pageEightBehaviorRowsHtml = psychPageEightBehaviorRows.map((row) => `<div class="ncp-page-eight-behavior-row">[ ]&nbsp;&nbsp;${escapeHtml(row)}</div>`).join("");
  const pageEightSupportHtml = psychPageEightSupportLines.map((line) => {
    const prefix = line.checkbox ? "[ ] " : "";
    const content = line.blue ? `<span class="ncp-blue ncp-doc-link">${escapeHtml(line.text)}</span>${escapeHtml(line.suffix || "")}` : `${escapeHtml(line.text)}${escapeHtml(line.suffix || "")}`;
    return `<p class="${line.spacer ? "ncp-spaced-line" : ""}">${prefix}${line.strong ? `<strong>${content}</strong>` : content}</p>`;
  }).join("");
  const pageEightProgramsHtml = specializedBehaviorProgramLines.map((line) => {
    const box = line.checked ? "☒ " : line.text.includes("Services") || line.text.includes("Provider/Program") || line.text.includes("Meaningful") || line.text.includes("Expanded") ? "☐ " : "";
    return `<p class="${line.spacer ? "ncp-spaced-line" : ""}">${line.strong ? `<strong>${escapeHtml(line.text)}</strong>` : `${box}${escapeHtml(line.text)}`}</p>`;
  }).join("");
  const universalPrecautionHtml = universalPrecautionLines.map((line) => `<p class="${line.nested ? "ncp-nested-checkbox" : ""}">${line.noBox ? "" : "[ ] "}<span class="${line.blue ? "ncp-blue" : ""}">${escapeHtml(line.text)}</span></p>`).join("");
  const universalPrecautionPageTenHtml = universalPrecautionPageTenLines.map((line) => `<p>[ ] ${escapeHtml(line.text)}${line.blueSuffix ? `<br /><span class="ncp-blue">${escapeHtml(line.suffix)}</span>` : ""}</p>`).join("");
  const ncpPageOneDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-one-sheet">
    <div class="ncp-page-one-title">Adult Family Home Resident Negotiated Care Plan <span>(NCP)</span></div>
    <div class="ncp-page-one-note"><strong>NOTE: Place an X in the bracket &#91;&#93; - &#91;x&#93; - to indicate/select your choice.</strong><em>Form Version: ${escapeHtml(record?.formVersion || "")}</em></div>
    <div class="ncp-page-one-grid">
      <div class="ncp-cell ncp-provider-cell"><b>Provider's Name:</b><p>${escapeHtml(negotiatedFieldValue("providerName"))}</p></div>
      <div class="ncp-cell ncp-started-cell"><b>Date NCP Started:</b><p>${escapeHtml(negotiatedFieldValue("dateNcpStarted"))}</p></div>
      <div class="ncp-cell ncp-moved-cell"><b>Moved In Date:</b><p>${escapeHtml(negotiatedFieldValue("movedInDate"))}</p></div>
      <div class="ncp-cell ncp-completed-cell"><b><span>Date Completed:</span></b><p>${escapeHtml(negotiatedFieldValue("dateCompleted"))}</p></div>
      <div class="ncp-cell ncp-discharged-cell"><b>Date Discharged:</b><p>${escapeHtml(negotiatedFieldValue("dateDischarged"))}</p></div>
      <div class="ncp-cell ncp-resident-cell"><b>Resident's Name:</b><p>${escapeHtml(negotiatedFieldValue("residentName"))}</p><b>Pronouns:</b><p>${escapeHtml(negotiatedFieldValue("pronouns"))}</p></div>
      <div class="ncp-cell ncp-dob-cell"><b>Date of Birth/Age:</b><p>${escapeHtml(negotiatedFieldValue("dateOfBirthAge"))}</p></div>
      <div class="ncp-cell ncp-language-cell"><b>Primary Language:</b><p>${escapeHtml(negotiatedFieldValue("primaryLanguage"))}</p><b>Speaks English?</b><p>${escapeHtml(negotiatedFieldValue("speaksEnglish"))}</p><b>Interpreter needed?</b><p>${escapeHtml(negotiatedFieldValue("interpreterNeeded"))}</p></div>
      <div class="ncp-cell ncp-allergy-cell"><b class="ncp-red">ALLERGIES:</b><p class="ncp-allergy-copy">&nbsp;</p></div>
      <div class="ncp-cell ncp-left-column"><b>Legal Documents: ${ncpCheckText("legalDocuments", 0)} None</b><p>${ncpCheckText("legalDocuments", 1)} Advanced Directives</p><p>${ncpCheckText("legalDocuments", 2)} POLST Form</p><p>${ncpCheckText("legalDocuments", 3)} Other: ${escapeHtml(negotiatedFieldValue("legalDocuments"))}</p><br /><b>Specialty Needs: ${ncpCheckText("specialtyNeeds", 0)} No</b><p>${ncpCheckText("specialtyNeeds", 1)} Dementia</p><p>${ncpCheckText("specialtyNeeds", 2)} Mental Health</p><p>${ncpCheckText("specialtyNeeds", 3)} Developmental Disability</p><p>${ncpCheckText("specialtyNeeds", 4)} Other: ${escapeHtml(negotiatedFieldValue("specialtyNeeds"))}</p></div>
      <div class="ncp-cell ncp-emergency-heading"><b>EMERGENCY EVACUATION</b></div>
      <div class="ncp-cell ncp-evacuation-copy"><b>EVACUATION ASSISTANCE REQUIRED:</b><p>[] <strong>NONE - RESIDENT IS INDEPENDENT:</strong> Resident is physically and mentally capable of independently evacuating the home without the assistance of another individual or the use of mobility aids. The department will consider a resident independent if capable of getting out of the home after one cue. ${escapeHtml(negotiatedFieldValue("evacuationIndependent"))}</p><p>[] <strong>ASSISTANCE REQUIRED:</strong> Resident is not physically or mentally capable of evacuating the home without assistance from another individual, mobility aids, or multiple cues.</p><p>Caregiver will keep walkways clear and ensure there are no barriers to evacuation routes. ${escapeHtml(negotiatedFieldValue("evacuationAssistance"))}</p><p><strong class="ncp-red">RESIDENT'S EVACUATION and SAFETY INSTRUCTIONS:</strong></p><p>Caregiver will ${escapeHtml(negotiatedFieldValue("evacuationCaregiverInstructions"))}</p></div>
    </div>
    <div class="ncp-medical-band"><strong>MENTAL/PHYSICAL HEALTH - CURRENT MEDICAL STATUS/DIAGNOSIS</strong><p>${escapeHtml(negotiatedFieldValue("medicalDiagnosis"))}</p></div>
    <div class="ncp-index-grid">${ncpIndexHtml}</div>
    <div class="ncp-page-footer"><span>Resident's Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 1 of 19</strong></div>
  </section>`;
  const ncpPageTwoDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-two-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <table class="ncp-contacts-table">
      <thead>
        <tr><th colspan="5"><strong>RESPONSIBLE PARTIES - CONTACTS</strong><br />Add those involved in care planning for your resident: Case Manager, DPOA, Guardian, Medical/Mental Health Providers, Pharmacy, Hospice, etc.<br />- Indicate which contact method is preferred.</th></tr>
        <tr><th>Name</th><th><span>Relationship</span></th><th>Home/Business<br />Phone</th><th>Cell Phone/FAX</th><th>Address/Email</th></tr>
      </thead>
      <tbody>
        ${[1, 2, 3, 4, 5, 6].map((number) => `<tr>${ncpContactColumns.map((column, columnIndex) => `<td>${columnIndex === 0 ? `${number}. ` : ""}${escapeHtml(negotiatedFieldValue(ncpContactFieldKey(number, column.key)))}</td>`).join("")}</tr>`).join("")}
      </tbody>
    </table>
    <p class="ncp-page-two-tip">(Place your cursor in the last box and hit your tab key to add another row)</p>
    <div class="ncp-communication-title">COMMUNICATION</div>
    <div class="ncp-communication-grid">
      <div class="ncp-comm-head ncp-comm-topic">SPEECH/HEARING/VISION</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Caregiver Instructions<br />Who, How, When/How Often</div>
      <div class="ncp-comm-left"><b>Modes of Expression:</b><p>${escapeHtml(negotiatedFieldValue("communicationModes"))}</p><b>Problems with Mode of Expression - [ ] Yes [ ] No</b><p>Describe: ${escapeHtml(negotiatedFieldValue("expressionProblems"))}</p><p>Equipment: ${escapeHtml(negotiatedFieldValue("expressionEquipment"))}</p><hr /><b>Problems with Hearing - [ ] Yes [ ] No</b><p>Describe: ${escapeHtml(negotiatedFieldValue("hearingProblems"))}</p><p>Equipment:</p><hr /><b>Problems with Vision - [ ] Yes [ ] No</b><p>Describe: ${escapeHtml(negotiatedFieldValue("visionProblems"))}</p><p>Equipment:</p><hr /><b>Ability to Use the Phone</b><p>[ ] Independent [ ] Assistance Needed [ ] Dependent</p><p>[ ] Resident has own phone, number: ${escapeHtml(negotiatedFieldValue("phoneAbility"))}</p><hr /><b>Preferred Language:</b><p>${escapeHtml(negotiatedFieldValue("preferredLanguageComments"))}</p></div>
      <div class="ncp-comm-middle"><b>How resident makes self-understood:</b><p>${escapeHtml(negotiatedFieldValue("selfUnderstood"))}</p><b>How resident understands others:</b><p>${escapeHtml(negotiatedFieldValue("understandsOthers"))}</p><b class="ncp-blue">Strength and Abilities</b><p>${escapeHtml(negotiatedFieldValue("communicationStrengths"))}</p></div>
      <div class="ncp-comm-right"><b class="ncp-blue">Assistance Required</b><p>Caregiver will ${escapeHtml(negotiatedFieldValue("communicationAssistance"))}</p></div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 2 of 19</strong></div>
  </section>`;
  const ncpPageThreeDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-three-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-communication-title">COMMUNICATION</div>
    <div class="ncp-comments-grid">
      <div class="ncp-comm-head ncp-comm-topic">SPEECH/HEARING/VISION</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Caregiver Instructions<br />Who, How, When/How Often</div>
      <div class="ncp-comments-row"><b>Comments:</b></div>
    </div>
    <div class="ncp-med-overview">
      <div class="ncp-section-bar">MEDICATION MANAGEMENT - Overview</div>
      <div class="ncp-med-overview-grid">
        <div><p>[ ] <strong class="ncp-red">MEDICATION ALLERGIES:</strong></p><p>[ ] Resident needs more than one kind of medication assistance need</p><p>[ ] Resident is prescribed psychopharmacologic medications - <em>see behavior section for strategies and modification to address symptoms addressed by this/these medications</em></p></div>
        <div><p>[ ] See MAR for current medications, dosage, frequency, and route</p><br /><p>Meds are ordered by:</p><p>Meds are delivered by:</p><p>[ ] Meds are <strong class="ncp-blue ncp-doc-link">Pharmacy Packed:</strong></p><p>Note:</p></div>
      </div>
    </div>
    <div class="ncp-med-management">
      <div class="ncp-section-bar">MEDICATION MANAGEMENT</div>
      <div class="ncp-med-management-grid">
        <div class="ncp-med-intro"><em>The amount of assistance required to receive prescription medications, over the counter medications, or herbal supplements.</em></div>
        <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
        <div class="ncp-comm-head">Assistance Required<br />Caregiver Instruction<br />Who, How, When/How Often</div>
        <div class="ncp-med-left"><p><strong class="ncp-blue ncp-doc-link">[ ] SELF-ADMINISTRATION</strong></p><p><strong class="ncp-blue ncp-doc-link">[ ] SELF-ADMINISTRATION W/ ASSISTANCE</strong></p><p><strong class="ncp-blue ncp-doc-link">[ ] MEDICATION ADMINISTRATION</strong></p><br /><p>Equipment:</p><p>Type of Medication Management is Needed:</p><div class="ncp-checkbox-list">${medicationRouteCheckboxHtml}</div></div>
        <div class="ncp-med-middle"><strong class="ncp-blue">Strengths and Abilities</strong></div>
        <div class="ncp-med-right"><b>Describe the reason the resident needs this amount of medication assistance:</b><br /><br /><strong class="ncp-blue">Assistance Required</strong><p>Caregiver will</p><br /><p>CG will follow the 5 rights of medication administration + 2 <strong>every time:</strong></p><ul><li>Right resident</li><li>Right medication</li><li>Right dose</li></ul></div>
      </div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 3 of 19</strong></div>
  </section>`;
  const ncpPageFourDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-four-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-section-bar">MEDICATION MANAGEMENT</div>
    <div class="ncp-page-four-grid">
      <div class="ncp-med-intro"><em>The amount of assistance required to receive prescription medications, over the counter medications, or herbal supplements.</em></div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Caregiver Instruction<br />Who, How, When/How Often</div>
      <div class="ncp-page-four-left"><p><strong>[ ]&nbsp;&nbsp;Injections:</strong></p><div class="ncp-checkbox-list">${injectionSupportCheckboxHtml}</div><div class="ncp-checkbox-list">${pageFourMedicationRouteCheckboxHtml}</div><p>[ ] Requires <strong class="ncp-blue ncp-doc-link">Nurse Delegation</strong></p><div class="ncp-rn-block"><strong>RN Delegator</strong><ul><li><b>Name:</b></li><li><b>Phone:</b></li><li><b>FAX:</b></li><li><b>Email:</b></li></ul></div></div>
      <div class="ncp-page-four-middle"></div>
      <div class="ncp-page-four-right"><ul><li>Right route</li><li>Right time</li></ul><p>+ Right documentation</p><p>+ Follow strategy for resident's right to refuse</p><br /><p>CG is to follow Dr. orders, follow RND instructions; document in MAR and report significant changes, concerns/adverse reactions to Dr. immediately.</p></div>
      <div class="ncp-page-four-plan-title"><strong class="ncp-blue">Medication Plan When Resident is not in the AFH</strong></div>
      <div class="ncp-page-four-plan-middle"><strong>Strengths and Abilities</strong></div>
      <div class="ncp-page-four-plan-right"><strong class="ncp-blue">Assistance Required</strong><p>Caregiver will</p></div>
      <div class="ncp-page-four-plan-title"><strong class="ncp-blue">Medication Refusal Plan</strong></div>
      <div class="ncp-page-four-plan-middle"><strong>Strengths and Abilities</strong></div>
      <div class="ncp-page-four-plan-right"><strong>Assistance Required</strong><p>Caregiver will</p></div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 4 of 19</strong></div>
  </section>`;
  const ncpPageFiveDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-five-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-section-bar ncp-underlined-bar">HEALTH INDICATORS</div>
    <div class="ncp-page-five-health-grid">
      <div class="ncp-health-topic"><strong>Health Indicator Monitoring and Support</strong></div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-health-left"><div class="ncp-checkbox-list">${healthIndicatorCheckboxHtml}</div></div>
      <div class="ncp-health-middle"><strong>Strengths and Abilities</strong></div>
      <div class="ncp-health-right"><strong>Assistance Required</strong><p class="ncp-health-reporting"><strong>Monitoring/Reporting significant changes and/or concerns:</strong> Caregiver is to report concerns and significant changes immediately to relevant individuals (health Care provider POA, CM, etc.).</p></div>
      <div class="ncp-health-allergy-title"><strong>Allergies</strong></div>
      <div class="ncp-health-allergy-cell"><strong>Substance:</strong></div>
      <div class="ncp-health-allergy-cell"><strong>Reaction:</strong></div>
    </div>
    <div class="ncp-section-bar ncp-page-five-treatment-bar">TREATMENTS/PROGRAMS/THERAPIES</div>
    <div class="ncp-page-five-treatment-grid">
      <div class="ncp-comm-head">Treatment/Program<br />Therapy/Interventions</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-treatment-left"><strong>Type of Treatment:</strong><div class="ncp-checkbox-list">${treatmentTypeCheckboxHtml}</div></div>
      <div class="ncp-treatment-middle"><strong class="ncp-blue">Strengths and Abilities</strong></div>
      <div class="ncp-treatment-right"><strong class="ncp-blue">Assistance Required</strong><p>Caregiver will</p></div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 5 of 19</strong></div>
  </section>`;
  const ncpPageSixDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-six-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-section-bar">TREATMENTS/PROGRAMS/THERAPIES</div>
    <div class="ncp-page-six-grid">
      <div class="ncp-comm-head">Treatment/Program<br />Therapy/Interventions</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-six-left"><div class="ncp-checkbox-list">${treatmentTherapyCheckboxHtml}</div></div>
      <div class="ncp-page-six-middle"></div>
      <div class="ncp-page-six-right"></div>
      <div class="ncp-page-six-left"><strong>Programs Resident Requires/Attends:</strong><div class="ncp-checkbox-list">${treatmentProgramCheckboxHtml}</div></div>
      <div class="ncp-page-six-middle"></div>
      <div class="ncp-page-six-right"></div>
      <div class="ncp-page-six-left"><strong>Interventions</strong><p>[ ]&nbsp;&nbsp;&nbsp;&nbsp;Meaningful Day</p></div>
      <div class="ncp-page-six-middle"></div>
      <div class="ncp-page-six-right"></div>
      <div class="ncp-page-six-plan-title"><strong class="ncp-blue">Physical Enablers</strong></div>
      <div class="ncp-page-six-plan-middle"><strong>Strengths and Abilities</strong></div>
      <div class="ncp-page-six-plan-right"><strong class="ncp-blue">Assistance Required</strong><p>Caregiver will</p></div>
      <div class="ncp-page-six-plan-title"><strong>TREATMENT/PROGRAM/THERAPY REFUSAL PLAN</strong></div>
      <div class="ncp-page-six-plan-middle"><strong>Strengths and Abilities</strong></div>
      <div class="ncp-page-six-plan-right"><strong class="ncp-blue">Assistance Required</strong><p>Caregiver will</p></div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 6 of 19</strong></div>
  </section>`;
  const ncpPageSevenDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-seven-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-seven-grid">
      <div class="ncp-psych-title">PSYCH/SOCIAL/COGNITIVE STATUS</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      ${psychStatusRowsHtml}
      <div class="ncp-psych-middle"><strong>Resident Strengths And Abilities</strong><strong>Prefers To Do Independently</strong><strong>Preferences and Personal Goals</strong></div>
      <div class="ncp-psych-right"><strong>Assistance Required</strong><strong>Who Will Provide, When And How</strong></div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 7 of 19</strong></div>
  </section>`;
  const ncpPageEightDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-eight-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-eight-grid">
      <div class="ncp-psych-title">PSYCH/SOCIAL/COGNITIVE STATUS</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      ${pageEightBehaviorRowsHtml}
      <div class="ncp-page-eight-support">${pageEightSupportHtml}</div>
      <div class="ncp-page-eight-programs">${pageEightProgramsHtml}</div>
      <div class="ncp-page-eight-middle-top"></div>
      <div class="ncp-page-eight-right-top"></div>
      <div class="ncp-page-eight-middle-bottom"></div>
      <div class="ncp-page-eight-right-bottom"></div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 8 of 19</strong></div>
  </section>`;
  const ncpPageNineDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-nine-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-nine-psych-grid">
      <div class="ncp-psych-title">PSYCH/SOCIAL/COGNITIVE STATUS</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-nine-interventions"><strong>Interventions</strong><p>[ ]&nbsp;&nbsp;&nbsp;&nbsp;Meaningful Day</p><p>[ ]&nbsp;&nbsp;&nbsp;&nbsp;Other:</p></div>
      <div class="ncp-page-nine-psych-middle"></div>
      <div class="ncp-page-nine-psych-right"></div>
      <div class="ncp-page-nine-narrative"><strong>Narrative (optional) - What does a typical day look like?</strong></div>
    </div>
    <div class="ncp-page-nine-left-grid">
      <div class="ncp-section-bar">Left Alone</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-nine-left-title"><strong class="ncp-blue">Ability of Resident to Be Left Unattended</strong></div>
      <div class="ncp-page-nine-left-middle"><strong>Strengths and Abilities</strong></div>
      <div class="ncp-page-nine-left-right"><strong>Assistance Required</strong><p>Caregiver will</p></div>
    </div>
    <div class="ncp-page-nine-universal-grid">
      <div class="ncp-section-bar">UNIVERSAL PRECAUTIONS</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-nine-universal-left">${universalPrecautionHtml}</div>
      <div class="ncp-page-nine-universal-middle"><strong>Strengths and Abilities</strong></div>
      <div class="ncp-page-nine-universal-right"><p>Caregiver will always use latex/plastic gloves when in contact with any secretions to prevent spread of infection.</p><p>Thorough hand washing with soap will be done before and after gloving. Gloves will be put on and discarded at the end of each task. If the AFH provider orders these gloves they can be paid for through the medical coupon.</p><strong>Assistance Required</strong></div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 9 of 19</strong></div>
  </section>`;
  const ncpPageTenDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-ten-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-ten-universal-grid">
      <div class="ncp-section-bar">UNIVERSAL PRECAUTIONS</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-ten-universal-left">${universalPrecautionPageTenHtml}</div>
      <div class="ncp-page-ten-universal-middle"></div>
      <div class="ncp-page-ten-universal-right"></div>
    </div>
    <div class="ncp-page-ten-adl-grid">
      <div class="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-ten-functional"><strong>Resident functional limitations that impact ADL functioning:</strong></div>
      <div class="ncp-page-ten-ambulation"><strong>AMBULATION/MOBILITY</strong><p><strong>Locomotion in room and immediate living environment:</strong></p><p>[ ] Independent&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;[ ] Supervision/Cueing</p><p>[ ] Assistance Needed [ ] Totally Dependent</p><br /><p><strong>Locomotion outside of immediate living environment (including outdoors):</strong></p><p>[ ] Independent&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;[ ] Supervision/Cueing</p><p>[ ] Assistance Needed [ ] Totally Dependent</p><br /><p><strong>[ ] Risks for falls</strong></p><p><strong>[ ] Fall prevention plan:</strong></p></div>
      <div class="ncp-page-ten-adl-middle"><strong class="ncp-blue">Strengths and Abilities</strong><br /><br /><p><strong>Evacuation addressed under Evacuation Plan</strong></p></div>
      <div class="ncp-page-ten-adl-right"><p><strong>Monitoring/Reporting significant changes and/or concerns:</strong> Caregiver is to monitor the resident during the ADL, report concerns and significant changes immediately to relevant individuals (health Care provider POA, CM, etc.)</p><strong class="ncp-blue">Assistance Required</strong><p>Caregiver will</p></div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 10 of 19</strong></div>
  </section>`;
  const ncpPageElevenDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-eleven-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-eleven-adl-grid">
      <div class="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-eleven-top-left"><p>[ ] <strong class="ncp-blue">Resident chooses bedroom door lock</strong></p><br /><p><strong>Equipment/Supplies:</strong></p><p class="ncp-nested-checkbox">Vendor:</p><br /><p><strong>Limitations:</strong></p></div>
      <div class="ncp-page-eleven-top-middle"></div>
      <div class="ncp-page-eleven-top-right"></div>
      <div class="ncp-page-eleven-transfer-left"><strong>BED MOBILITY/TRANSFER</strong><p><em>Transfer includes moving between bed, chair, wheelchair, standing position - excludes to/from bath/toilet. Bed mobility includes the ability to move in bed.</em></p><p>[ ] Independent&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;[ ] Supervision/Cueing</p><p>[ ] Assistance Needed [ ] Totally Dependent</p><br /><p>[ ] Skin care required due to inability to position self:</p><p class="ncp-nested-checkbox">Equipment/Supplies:</p><br /><p>[ ] Turning and Repositioning needed - Frequency:</p><br /><p>[ ] Safety assessment, alternatives explored; how to keep resident safe:</p><p class="ncp-nested-checkbox">[ ] Risks for falls:</p><p class="ncp-nested-checkbox">[ ] Fall prevention plan</p></div>
      <div class="ncp-page-eleven-transfer-middle"><strong class="ncp-blue">Strengths and Abilities</strong></div>
      <div class="ncp-page-eleven-transfer-right"><p><strong>Monitoring/Reporting significant changes and/or concerns:</strong> Caregiver is to monitor the resident during the ADL, report concerns and significant changes immediately to relevant individuals (health Care provider POA, CM, etc.)</p><strong class="ncp-blue">Assistance Required</strong><p>Caregiver will</p></div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 11 of 19</strong></div>
  </section>`;
  const ncpPageTwelveDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-twelve-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-twelve-adl-grid">
      <div class="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-twelve-device-left"><p>[ ] Safety plan</p><p>[ ] <strong class="ncp-blue">Medical Device(s):</strong></p><p class="ncp-nested-checkbox">[ ] Enablers:</p><p class="ncp-nested-checkbox">[ ] <strong>Hoyer Lift</strong></p><p class="ncp-nested-checkbox">[ ] <strong>Transfer Pole</strong></p><p class="ncp-nested-checkbox">[ ] Medical Device/Enabler Risk Assessment:</p><p>[ ] Nighttime care needs</p><br /><p>Equipment/supplies:</p></div>
      <div class="ncp-page-twelve-device-middle"></div>
      <div class="ncp-page-twelve-device-right"></div>
      <div class="ncp-page-twelve-eating-left"><strong>EATING</strong><p><em>How the Resident eats and drinks (regardless of skill). Includes intake of nourishment by other means (e.g., tube feeding, total parenteral nutrition)</em></p><p>[ ] Independent&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;[ ] Supervision/Cueing</p><p>[ ] Assistance Needed [ ] Totally Dependent</p><br /><p><strong>[ ] Special Diet/Supplements:</strong></p><p><strong>[ ] Eating Habits:</strong></p><p><strong>[ ] Food Allergies:</strong></p><p><strong>[ ] Equipment/Supplies/Procedures:</strong></p><p><strong>Limitations:</strong></p></div>
      <div class="ncp-page-twelve-eating-middle"><strong class="ncp-blue">Strengths and Abilities</strong></div>
      <div class="ncp-page-twelve-eating-right"><p><strong>Monitoring/Reporting significant changes and/or concerns:</strong> Caregiver is to monitor the resident during the ADL, report concerns and significant changes immediately to relevant individuals (health Care provider POA, CM, etc.)</p><strong class="ncp-blue">Assistance Required</strong><p>Caregiver will</p></div>
      <div class="ncp-page-twelve-toileting-left"><strong>TOILETING/CONTINENCE ISSUES</strong><p><em>How individual uses the toilet room (or commode, bed pan, urinal); transfers on/off toilet, cleanses, changes incontinence pads, manages ostomy or catheter, adjusts clothes</em></p></div>
      <div class="ncp-page-twelve-toileting-middle"><strong class="ncp-blue">Strengths and Abilities</strong></div>
      <div class="ncp-page-twelve-toileting-right"><p><strong>Monitoring/Reporting significant changes and/or concerns:</strong> Caregiver is to monitor the resident during the ADL, report concerns and significant changes immediately to relevant individuals (health Care provider POA, CM, etc.)</p><strong class="ncp-blue">Assistance Required</strong></div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 12 of 19</strong></div>
  </section>`;
  const ncpAdlMonitoringHtml = `<p><strong>Monitoring/Reporting significant changes and/or concerns:</strong> Caregiver is to monitor the resident during the ADL, report concerns and significant changes immediately to relevant individuals (health Care provider POA, CM, etc.)</p><strong class="ncp-blue">Assistance Required</strong><p>Caregiver will</p>`;
  const ncpPageThirteenDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-thirteen-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-thirteen-adl-grid">
      <div class="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-thirteen-toileting-left"><p>[ ] Independent&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;[ ] Supervision/Cueing</p><p>[ ] Assistance Needed [ ] Totally Dependent</p><p><strong>Frequency/How Often:</strong></p><br /><p>Continence Issues:</p><p><strong>[ ] Bladder Incontinence</strong></p><p><strong>[ ] Bowel Incontinence</strong></p><p><strong>[ ] Skin care due to bowel/bladder incontinence</strong></p><br /><p><strong>Equipment/Supplies/Procedures:</strong></p><p><strong>Limitations:</strong></p></div>
      <div class="ncp-page-thirteen-toileting-middle"></div>
      <div class="ncp-page-thirteen-toileting-right"><p>Caregiver will</p></div>
      <div class="ncp-page-thirteen-dressing-left"><strong>DRESSING</strong><p><em>How individual puts on, fastens, and takes off all items of street clothing, including donning/removing prosthesis</em></p><p>[ ] Independent&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;[ ] Supervision/Cueing</p><p>[ ] Assistance Needed [ ] Totally Dependent</p><br /><p><strong>Equipment/Supplies/Procedures:</strong></p><p><strong>Limitations:</strong></p></div>
      <div class="ncp-page-thirteen-dressing-middle"><strong class="ncp-blue">Strengths and Abilities</strong></div>
      <div class="ncp-page-thirteen-dressing-right">${ncpAdlMonitoringHtml}</div>
      <div class="ncp-page-thirteen-hygiene-left"><strong>PERSONAL HYGIENE</strong><span> - </span><em>How individual maintains personal hygiene, including combing hair, brushing teeth, shaving, applying makeup, washing/drying face, hands, and perineum</em><p>[ ] Independent&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;[ ] Supervision/Cueing</p><p>[ ] Assistance Needed [ ] Totally Dependent</p></div>
      <div class="ncp-page-thirteen-hygiene-middle"><strong class="ncp-blue">Strengths and Abilities</strong></div>
      <div class="ncp-page-thirteen-hygiene-right">${ncpAdlMonitoringHtml}</div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 13 of 19</strong></div>
  </section>`;
  const ncpPageFourteenDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-fourteen-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-fourteen-adl-grid">
      <div class="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-fourteen-hygiene-left"><p>[ ] Own teeth [ ] Partials [ ] Dentures</p><p>[ ] <strong>Oral Hygiene</strong> (including dentures):</p><p class="ncp-nested-checkbox">[ ] Flossing</p><p class="ncp-nested-checkbox">[ ] Brushing</p><p class="ncp-nested-checkbox">[ ] Soaking</p><p>[ ] <strong>Hair Care:</strong></p><p>[ ] <strong>Menses Care:</strong></p><br /><p><strong>When/how often:</strong></p><p><strong>Equipment/Supplies/Procedures:</strong></p><p><strong>Limitations:</strong></p></div>
      <div class="ncp-page-fourteen-hygiene-middle"></div>
      <div class="ncp-page-fourteen-hygiene-right"></div>
      <div class="ncp-page-fourteen-bathing-left"><strong>BATHING</strong><p><em>How individual takes full-body shower, sponge bath, and transfer in/out of tub/shower</em></p><p>[ ] Independent&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;[ ] Supervision/Cueing</p><p>[ ] Assistance Needed [ ] Totally Dependent</p><br /><p><strong>When/how often:</strong></p><p><strong>Equipment/Supplies/Procedures:</strong></p><p><strong>Limitations:</strong></p></div>
      <div class="ncp-page-fourteen-bathing-middle"><strong class="ncp-blue">Strengths and Abilities</strong></div>
      <div class="ncp-page-fourteen-bathing-right">${ncpAdlMonitoringHtml}</div>
      <div class="ncp-page-fourteen-foot-left"><strong>FOOT CARE</strong><p>[ ] Independent&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;[ ] Supervision/Cueing</p><p>[ ] Assistance Needed [ ] Totally Dependent</p><br /><p>[ ] <strong class="ncp-blue">Foot Care:</strong> How Often:</p></div>
      <div class="ncp-page-fourteen-foot-middle"><strong class="ncp-blue">Strengths and Abilities</strong></div>
      <div class="ncp-page-fourteen-foot-right">${ncpAdlMonitoringHtml}</div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 14 of 19</strong></div>
  </section>`;
  const ncpPageFifteenDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-fifteen-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-fifteen-adl-grid">
      <div class="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-fifteen-foot-left"><p>[ ] <strong class="ncp-blue">Diabetic Foot Care</strong></p><p>[ ] Nail Care</p><p><strong>When/how often:</strong></p><br /><p>[ ] <strong class="ncp-blue">Home Health Agency:</strong></p><br /><p><strong>Equipment/Supplies/Procedures:</strong></p><p><strong>Limitations:</strong></p></div>
      <div class="ncp-page-fifteen-foot-middle"></div>
      <div class="ncp-page-fifteen-foot-right"><p>Caregiver will</p></div>
      <div class="ncp-page-fifteen-skin-left"><strong>SKIN CARE</strong><p>[ ] Independent&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;[ ] Supervision/Cueing</p><p>[ ] Assistance Needed [ ] Totally Dependent</p><br /><p>[ ] <strong>Skin Care</strong> - How Often:</p><p class="ncp-nested-checkbox">[ ] Status:</p><p>[ ] <strong>Skin Problems</strong> - Describe:</p><p class="ncp-nested-checkbox">[ ] Status:</p><p>[ ] <strong>Pressure Injuries</strong> - Describe:</p><p>[ ] <strong>Dressing Changes</strong> - How Often:</p><p class="ncp-nested-checkbox">[ ] <strong>Nurse Delegated</strong></p><br /><p><strong>When/how often:</strong></p><br /><p>[ ] <strong class="ncp-blue">Home Health Agency:</strong></p><br /><p><strong>Equipment/Supplies/Procedures:</strong></p><p><strong>Limitations:</strong></p></div>
      <div class="ncp-page-fifteen-skin-middle"></div>
      <div class="ncp-page-fifteen-skin-right"><p>Caregiver will</p></div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 15 of 19</strong></div>
  </section>`;
  const ncpPageSixteenDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-sixteen-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-sixteen-iadl-grid">
      <div class="ncp-section-bar">INSTRUMENTAL ACTIVITIES OF DAILY LIVING</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When And How</div>
      <div class="ncp-page-sixteen-finances-left"><strong>MANAGING FINANCES</strong><p>[ ] Independent [ ] Assistance Needed [ ] Dependent</p><br /><p>[ ] <strong>Who Manages Finances:</strong></p><p>[ ] <strong>Who Manages Financial Records:</strong></p><br /><p>[ ] Payee Name and Contact information:</p></div>
      <div class="ncp-page-sixteen-finances-middle"><strong class="ncp-blue">Strengths and Abilities</strong><p><strong>Prefers to do independently:</strong></p></div>
      <div class="ncp-page-sixteen-finances-right"><strong class="ncp-blue">Assistance Required</strong><p>Caregiver will</p></div>
      <div class="ncp-page-sixteen-shopping-left"><strong>SHOPPING</strong><p>[ ] Independent [ ] Assistance Needed [ ] Dependent</p><br /><p>[ ] <strong class="ncp-blue">Special transportation needs:</strong></p><p><strong>How often/when:</strong></p><br /><p><strong>Equipment/Supplies/Procedures:</strong></p><p><strong>Limitations:</strong></p></div>
      <div class="ncp-page-sixteen-shopping-middle"><strong class="ncp-blue">Strengths and Abilities</strong></div>
      <div class="ncp-page-sixteen-shopping-right"><strong class="ncp-blue">Assistance Required</strong><p>Caregiver will</p></div>
      <div class="ncp-page-sixteen-transport-left"><strong>TRANSPORTATION</strong><p>[ ] Independent [ ] Assistance Needed [ ] Dependent</p><br /><p>[ ] <strong>Medical services:</strong></p><p>[ ] <strong class="ncp-blue">Special transportation needs:</strong></p><p>[ ] <strong>Escort Required</strong></p><p><strong>How often/when:</strong></p><p><strong>Equipment/Supplies/Procedures:</strong></p><p><strong>Limitations:</strong></p></div>
      <div class="ncp-page-sixteen-transport-middle"><strong class="ncp-blue">Strengths and Abilities</strong></div>
      <div class="ncp-page-sixteen-transport-right"><strong class="ncp-blue">Assistance Required</strong><p>Caregiver will</p></div>
      <div class="ncp-page-sixteen-social-left"><strong>ACTIVITIES/SOCIAL</strong><p><em>Social/Cultural considerations, traditions, or preferences</em></p></div>
      <div class="ncp-page-sixteen-social-middle"><strong class="ncp-blue">Strengths and Abilities</strong></div>
      <div class="ncp-page-sixteen-social-right"><strong class="ncp-blue">Assistance Required</strong><p>Caregiver will</p></div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 16 of 19</strong></div>
  </section>`;
  const ncpPageSeventeenDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-seventeen-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-seventeen-grid">
      <div class="ncp-page-seventeen-social-left"><p>[ ] Independent [ ] Assistance Needed [ ] Dependent</p><br /><p>[ ] Interests/Activities/Religious Activities:</p><p>[ ] Social/Cultural Traditions/Preferences:</p><p>[ ] Family/Friends/Relationships:</p><p>[ ] Employment Support:</p><p>[ ] Clubs/Groups/Day Health:</p><p>[ ] Special Arrangements:</p><p>[ ] Participation Issues:</p><br /><p>Emergency Numbers: <strong><em>See face sheet in resident binder</em></strong></p></div>
      <div class="ncp-page-seventeen-social-middle"></div>
      <div class="ncp-page-seventeen-social-right"><p><strong class="ncp-blue">Special Arrangements:</strong></p><br /><p><strong class="ncp-blue">Participation Assistance:</strong></p></div>
      <div class="ncp-page-seventeen-preferences-title"><strong>ACTIVITY PREFERENCES AT A GLANCE</strong></div>
      <div class="ncp-page-seventeen-preferences-left"><p>[ ] Reading book and/or magazines</p><p>[ ] Listening to audio books and/or podcasts</p><p>[ ] Storytelling</p><p>[ ] Phone conversation/visiting</p><p>[ ] Reminiscing</p><p>[ ] Current events</p><p>[ ] Discussion group</p><p>[ ] Bible study or church</p><p>[ ] Visitors</p></div>
      <div class="ncp-page-seventeen-preferences-middle"><p>[ ] Gardening</p><p>[ ] Outings with family or provider</p><p>[ ] Visiting zoos and/ or spending time with pets and animals</p><p>[ ] Exercises/range of motion</p><p>[ ] Therapeutic Walking</p><p>[ ] Cooking or baking</p><p>[ ] House chore activities</p><p>[ ] Watching TV, movies, or favorite shows</p></div>
      <div class="ncp-page-seventeen-preferences-right"><p>[ ] Parties and social gatherings</p><p>[ ] Arts and crafts</p><p>[ ] Table games, Bingo, cards, puzzles</p><p>[ ] Beauty time, beautician visit</p><p>[ ] Music appreciation/therapy/singing</p><p>[ ] Employment support</p><p>[ ] Community Integration</p><p>[ ] Other:</p><p>[ ] Other:</p></div>
      <div class="ncp-page-seventeen-narrative"><strong>ACTIVITY NARRATIVE:</strong></div>
      <div class="ncp-page-seventeen-smoking-left"><strong>SMOKING</strong><p>[ ] Resident Smokes</p><p>[ ] Safety Concerns:</p><p>[ ] Smoking Policy reviewed with resident. Signed by resident and placed in their record.</p><br /><p>Storage of Cigarettes/lighter:</p><br /><br /></div>
      <div class="ncp-page-seventeen-smoking-middle"><strong class="ncp-blue">Strengths and Abilities</strong></div>
      <div class="ncp-page-seventeen-smoking-right"><strong class="ncp-blue">Assistance Required</strong><p>Caregiver will</p></div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 17 of 19</strong></div>
  </section>`;
  const ncpPageEighteenDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-eighteen-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-eighteen-grid">
      <div class="ncp-page-eighteen-case-left"><strong>CASE MANAGEMENT</strong><p>[ ] Resident receives Case Management</p><br /><p>Name:</p><p>Agency:</p><p>Phone:</p><p>Email:</p><p>FAX:</p></div>
      <div class="ncp-page-eighteen-case-middle"></div>
      <div class="ncp-page-eighteen-case-right"><p>CM will be contacted when:</p><ul><li>The resident needs assistive device or other services to meet their needs</li><li>When the provider need help with the care plan</li><li>Significant changes with the condition/needs that necessitate changes with the care plan</li></ul></div>
      <div class="ncp-page-eighteen-other"><strong>OTHER ISSUES/CONCERNS/PROBLEMS</strong></div>
      <div class="ncp-page-eighteen-review-left"><strong class="ncp-blue">NEGOTIATED CARE PLAN REVIEW</strong></div>
      <div class="ncp-page-eighteen-review-middle"><p>The resident will participate in their NCP development/reviews to the extent they are able.</p></div>
      <div class="ncp-page-eighteen-review-right"><p>This NCP will be reviewed/revised:</p><ul><li>After an assessment for a significant change in the resident's physical or mental condition;</li><li>When the plan, or parts of the plan, no longer address the resident's needs and preferences;</li><li>At the request of the resident or the resident representative; or</li><li>At least every twelve months.</li></ul></div>
    </div>
    <div class="ncp-page-eighteen-abbreviations"><strong>Abbreviations used in this NCP:</strong><div><p>ADL= Activities of Daily Living</p><p>AFH = Adult Family Home</p><p>CG = Caregiver</p><p>Dr. = Doctor</p></div><div><p>DPOA = Durable Power of Attorney</p><p>D/t = due to</p><p>Hx = history</p><p>MAR = medication assistance record</p></div><div><p>MD = medical doctor</p><p>PCP = primary care physician</p><p>PRN = As needed</p><p>RND = Register Nurse Delegator</p></div><div><p>W/c= Wheelchair</p></div></div>
    <div class="ncp-page-eighteen-approval"><h3>Negotiated Care Plan Review and Approval</h3><strong>DATE OF ORIGINAL PLAN:</strong><p>Dates of Review/<span class="ncp-blue">Revision</span>:</p></div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 18 of 19</strong></div>
  </section>`;
  const ncpPageNineteenRows = Array.from({ length: 10 }, (_, index) => `<div class="ncp-page-nineteen-approver">${index === 0 ? '<strong class="ncp-blue">PROVIDER</strong>' : index === 4 ? '<strong class="ncp-blue">RESIDENT</strong>' : index === 8 ? '<strong>RESIDENT REPRESENTATIVE</strong>' : "&nbsp;"}</div><div class="ncp-page-nineteen-signature"></div><div class="ncp-page-nineteen-signature"></div>`).join("");
  const ncpPageNineteenDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-nineteen-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-nineteen-grid">
      <div class="ncp-page-nineteen-involved"><strong class="ncp-blue">INVOLVED IN NCP DEVELOPMENT</strong><p>[ ] Resident</p><p>[ ] Resident Representative</p><p>[ ] Parent</p><p>[ ] Health Professional</p><p>[ ] Other:</p><p>[ ] Other:</p><p>[ ] Other:</p></div>
      <div class="ncp-page-nineteen-head">PERSON APPROVING PLAN</div>
      <div class="ncp-page-nineteen-head">SIGNATURE <span>DATE*</span></div>
      <div class="ncp-page-nineteen-head">SIGNATURE <span>DATE*</span></div>
      ${ncpPageNineteenRows}
      <div class="ncp-page-nineteen-verbal">[ ] Resident verbally agreed to NCP - Date:</div>
      <div class="ncp-page-nineteen-sent">[] NCP sent to DSHS CM on:</div>
      <div class="ncp-page-nineteen-recommendations"><strong class="ncp-blue">Resident Recommendations:</strong></div>
    </div>
    <div class="ncp-page-nineteen-note"><strong>*</strong>The person signing writes <u>the date they actually read and agreed to the plan</u>. If the participant has verbally agreed to the plan, the provider should note below: (a) the name and role of the participant; (b) the date the participant had the plan to read to them; and (c) what if any changes the participant recommended for the plan.</div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 19 of 19</strong></div>
  </section>`;
  const negotiatedTemplateDocumentHtml = makeNcpCheckboxesFillable(negotiatedTemplatePages
    .map(
      (page) => page.pageNumber === 1 ? ncpPageOneDocumentHtml : page.pageNumber === 2 ? ncpPageTwoDocumentHtml : page.pageNumber === 3 ? ncpPageThreeDocumentHtml : page.pageNumber === 4 ? ncpPageFourDocumentHtml : page.pageNumber === 5 ? ncpPageFiveDocumentHtml : page.pageNumber === 6 ? ncpPageSixDocumentHtml : page.pageNumber === 7 ? ncpPageSevenDocumentHtml : page.pageNumber === 8 ? ncpPageEightDocumentHtml : page.pageNumber === 9 ? ncpPageNineDocumentHtml : page.pageNumber === 10 ? ncpPageTenDocumentHtml : page.pageNumber === 11 ? ncpPageElevenDocumentHtml : page.pageNumber === 12 ? ncpPageTwelveDocumentHtml : page.pageNumber === 13 ? ncpPageThirteenDocumentHtml : page.pageNumber === 14 ? ncpPageFourteenDocumentHtml : page.pageNumber === 15 ? ncpPageFifteenDocumentHtml : page.pageNumber === 16 ? ncpPageSixteenDocumentHtml : page.pageNumber === 17 ? ncpPageSeventeenDocumentHtml : page.pageNumber === 18 ? ncpPageEighteenDocumentHtml : page.pageNumber === 19 ? ncpPageNineteenDocumentHtml : `<section class="ncp-preview-page-sheet"><div class="ncp-preview-page-head"><strong>ADULT FAMILY HOME RESIDENT NEGOTIATED CARE PLAN (NCP)</strong><span>Page ${page.pageNumber} of 19</span></div>${page.sections
        .map(
          (section) => `<table class="ncp-preview-table"><thead><tr><th colspan="2">${escapeHtml(section.title)}</th></tr></thead><tbody>${section.fields
            .map((field) => `<tr><td class="ncp-preview-label">${escapeHtml(field.label)}</td><td class="ncp-preview-value">${escapeHtml(field.value).replace(/\n/g, "<br />") || "&nbsp;"}</td></tr>`)
            .join("")}</tbody></table>`,
        )
        .join("")}</section>`,
    )
    .join(""));

  const applyPreliminaryAiAction = (nextDescription) => {
    setPreliminaryPlanHistory((current) => [...current, planDescription]);
    setPreliminaryPlanDescription(nextDescription);
  };

  const refinePreliminaryPlan = () => {
    applyPreliminaryAiAction(`${planDescription}<p><strong>AI refinement:</strong> Clarified care priorities, tightened language, and aligned observations with assessment, MaG, onboarding, and daily note data.</p>`);
  };

  const summarizePreliminaryPlan = () => {
    applyPreliminaryAiAction("<p><strong>Summary:</strong> Resident needs calm routines, standby transfer support, medication prompts, family contact coordination, and continued observation of discrepancies before final negotiated care planning.</p>");
  };

  const formatPreliminaryPlan = () => {
    applyPreliminaryAiAction(`
      <h3>Preliminary Service Plan</h3>
      <p>${planDescription.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()}</p>
      <h4>Resident Profile and Responsible Parties</h4>
      <p>Confirm resident demographics, move-in date, responsible party, DPOA/DPOH/guardian/POA/POH status, family history, close friends, and significant relatives.</p>
      <h4>Personal History, Religion, Interests, and Social Preferences</h4>
      <p>Document celebrations, special events, religious participation, church visits, dietary practices, hobbies, entertainment, music, reading, group preferences, outdoor activities, and pets.</p>
      <h4>Food Preferences and Dietary Needs</h4>
      <p>Capture meal times, favorite snacks, dining location preference, alcohol permission, physician diet orders, favorite foods, dislikes, beverages, meats, vegetables, fruits, and comfort foods.</p>
      <h4>Communication, Sensory, and Safety</h4>
      <p>Document speech, hearing, eyesight, phone use, bedroom lock/privacy preference, allergies, evacuation level, and evacuation assistance instructions.</p>
      <h4>Medication, Treatments, and Health Monitoring</h4>
      <p>Record medication management level, nurse delegation, injections, oxygen, pain, vital signs, blood sugar checks, weight monitoring, wound care, home health, PT/OT/speech/RN, and hospice.</p>
      <h4>Mental Capacity, Attitude, Behavior, and Interventions</h4>
      <p>Summarize orientation, memory, mood, behavior risks, wandering/elopement, psycho-pharmacological medications, door alarms, location checks, and intervention strategies.</p>
      <h4>Mobility, Transfers, ADLs, Continence, and Equipment</h4>
      <ul>
        <li>Ambulation, transfer, bed mobility, restorative support, repositioning, and paralysis status.</li>
        <li>Continence, toileting, dressing, bathing, skin care, grooming, mouth care, eating, fluids, and hydration plan.</li>
        <li>Equipment such as walker, hospital bed, oxygen, bath bench, gait belt, lift, transfer board, blood pressure machine, and O2 saturation meter.</li>
      </ul>
      <h4>Goals, Preferences, and Refusal Plan</h4>
      <p>Document resident goals, preferences, medication refusal plan, other refusal behaviors, and how staff should respond.</p>
    `);
  };

  const undoPreliminaryPlanAction = () => {
    setPreliminaryPlanHistory((current) => {
      if (!current.length) return current;
      const previous = current[current.length - 1];
      setPreliminaryPlanDescription(previous);
      return current.slice(0, -1);
    });
  };

  const updateNegotiatedTemplateField = (key, value) => {
    setNegotiatedPlanPayload((current) => updateNegotiatedPayloadField(ensureNegotiatedPlanPayloadFields(current), key, (field) => ({ ...field, value })));
  };

  const updateNegotiatedTemplateCheck = (key, index, checked) => {
    setNegotiatedPlanPayload((current) => updateNegotiatedPayloadField(ensureNegotiatedPlanPayloadFields(current), key, (field) => {
      const checkCount = Math.max(field.checks?.length || 0, index + 1);
      const checks = Array.from({ length: checkCount }, (_, checkIndex) => (
        field.checks?.[checkIndex] || { index: checkIndex, checked: false }
      ));
      checks[index] = { ...checks[index], checked };
      return { ...field, checks };
    }));
  };

  const applyNegotiatedAiAction = (transform) => {
    const snapshot = cloneNegotiatedPlanPayload(negotiatedPlanPayload);
    setNegotiatedPlanHistory((current) => [...current, snapshot]);
    setNegotiatedPlanPayload((current) => ({
      ...current,
      pages: (current.pages || []).map((page) => ({
        ...page,
        sections: (page.sections || []).map((section) => ({
          ...section,
          fields: (section.fields || []).map((field) => ({
            ...field,
            value: transform({
              ...field,
              value: field.value || negotiatedTemplateDefaults[field.key] || "",
            }, section),
          })),
        })),
      })),
    }));
  };

  const improveNegotiatedPlan = () => {
    applyNegotiatedAiAction((field) => `${field.value}${field.value ? "\n\n" : ""}AI improvement: Align this field with assessment, preliminary plan, MaG, onboarding, daily notes, and annual progress data before final approval.`);
  };

  const formatNegotiatedPlan = () => {
    applyNegotiatedAiAction((field) => String(field.value || "").replace(/\s+/g, " ").trim());
  };

  const undoNegotiatedPlanAction = () => {
    setNegotiatedPlanHistory((current) => {
      if (!current.length) return current;
      const previous = current[current.length - 1];
      setNegotiatedPlanPayload(previous);
      return current.slice(0, -1);
    });
  };

  const printNegotiatedPlan = () => {
    const printWindow = window.open("", "_blank", "width=1100,height=760");
    if (!printWindow) return;
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>${recordName} - ${processResidentName(assessment)}</title>
          <style>
            @page { size: landscape; margin: 0.16in; }
            * { box-sizing: border-box; }
            body { color: #172b21; font-family: Arial, sans-serif; font-size: 9.2px; line-height: 1.18; margin: 0; }
            h1 { background: #f2f2f2; border: 1px solid #a6a6a6; color: #111827; font-size: 16px; margin: 0; padding: 12px; text-align: center; }
            .note { color: #b45309; font-style: italic; font-weight: 700; margin: 6px 0 10px; }
            .ncp-preview-page-sheet { break-after: page; page-break-after: always; position: relative; }
            .ncp-preview-page-sheet:last-child { break-after: auto; page-break-after: auto; }
            .ncp-preview-page-head { align-items: center; background: #f2f2f2; border: 1px solid #a6a6a6; display: flex; justify-content: space-between; margin-bottom: 6px; padding: 6px 8px; }
            .ncp-preview-page-head strong { color: #111827; font-size: 11px; text-transform: uppercase; }
            .ncp-preview-page-head span { color: #475569; font-weight: 700; }
            .ncp-fillable-check { appearance: none; -webkit-appearance: none; background: transparent; border: 0; color: #000; cursor: pointer; display: inline-block; font-family: Arial, sans-serif; font-size: 1em; font-weight: 900; height: 1em; line-height: 1; margin: 0 1px; min-width: 1.45em; outline: none; position: relative; text-align: center; vertical-align: -0.08em; width: 1.45em; }
            .ncp-fillable-check::after { color: #000; content: "[ ]"; font-size: 1em; font-weight: 900; left: 50%; line-height: 1; position: absolute; top: 48%; transform: translate(-50%, -50%); white-space: nowrap; }
            .ncp-fillable-check:checked::after { content: "[x]"; }
            .ncp-page-one-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; }
            .ncp-page-one-title { background: #f2f2f2; border: 1px solid #8f8f8f; color: #000; font-size: 13px; font-weight: 900; padding: 5px; text-align: center; text-transform: uppercase; }
            .ncp-page-one-title span { color: #2385db; text-decoration: underline; }
            .ncp-page-one-note { align-items: center; border-left: 1px solid #8f8f8f; border-right: 1px solid #8f8f8f; border-bottom: 2px solid #2385db; display: flex; justify-content: space-between; padding: 3px 4px 1px; }
            .ncp-page-one-note strong { color: #c45911; font-style: italic; }
            .ncp-page-one-note em { color: #2385db; font-weight: 900; }
            .ncp-page-one-grid { border-left: 1px solid #8f8f8f; display: grid; grid-template-columns: repeat(12, minmax(0, 1fr)); grid-template-areas: "provider provider provider provider started started moved moved completed completed discharged discharged" "resident resident resident resident dob dob language language language allergies allergies allergies" "legal legal legal legal emergency emergency emergency emergency emergency emergency emergency emergency" "legal legal legal legal evacuation evacuation evacuation evacuation evacuation evacuation evacuation evacuation"; }
            .ncp-cell { border-right: 1px solid #8f8f8f; border-bottom: 1px solid #8f8f8f; min-height: 31px; padding: 3px 4px; }
            .ncp-span-2 { grid-column: span 2; }
            .ncp-provider-cell { grid-area: provider; }
            .ncp-started-cell { grid-area: started; }
            .ncp-moved-cell { grid-area: moved; }
            .ncp-completed-cell { grid-area: completed; }
            .ncp-discharged-cell { grid-area: discharged; }
            .ncp-resident-cell { grid-area: resident; }
            .ncp-dob-cell { grid-area: dob; }
            .ncp-language-cell { grid-area: language; }
            .ncp-allergy-cell { grid-area: allergies; }
            .ncp-cell b { color: #000; display: block; font-size: 9.4px; line-height: 1.1; }
            .ncp-cell b span, .ncp-red { color: #f00 !important; }
            .ncp-cell p { font-size: 8.8px; line-height: 1.16; margin: 2px 0 0; white-space: pre-wrap; }
            .ncp-allergy-cell .ncp-allergy-copy { color: #000 !important; font-size: 9.2px; font-weight: 800; min-height: 14px; }
            .ncp-left-column { grid-area: legal; min-height: 195px; }
            .ncp-emergency-heading { background: #808080; color: #fff; grid-area: emergency; min-height: 18px; padding: 3px 5px; }
            .ncp-emergency-heading b { color: #fff; font-size: 10px; }
            .ncp-evacuation-copy { grid-area: evacuation; min-height: 177px; }
            .ncp-medical-band { border: 1px solid #8f8f8f; margin-top: 7px; min-height: 35px; }
            .ncp-medical-band strong { background: #808080; color: #fff; display: block; font-size: 10px; padding: 3px 6px; }
            .ncp-medical-band p { font-size: 8.8px; margin: 0; padding: 5px; }
            .ncp-index-grid { display: grid; gap: 3px 28px; grid-template-columns: repeat(5, 1fr); margin-top: 13px; padding-bottom: 40px; padding-top: 4px; }
            .ncp-index-grid span { color: #0563c1; font-size: 9.4px; font-weight: 700; text-decoration: underline; }
            .ncp-page-footer { bottom: 24px; color: #808080; display: grid; font-size: 11px; font-weight: 700; left: 24px; margin-top: 10px; position: absolute; right: 18px; }
            .ncp-page-two-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-two-title { color: #7a7a7a; font-size: 11px; font-weight: 900; margin: 0 0 8px; text-align: center; text-transform: uppercase; }
            .ncp-contacts-table { border-collapse: collapse; table-layout: fixed; width: 100%; }
            .ncp-contacts-table th, .ncp-contacts-table td { border: 1px solid #a6a6a6; color: #000; font-size: 9px; padding: 3px 5px; vertical-align: top; }
            .ncp-contacts-table thead tr:first-child th { background: #808080; color: #fff; font-size: 8.8px; line-height: 1.2; text-align: center; }
            .ncp-contacts-table thead tr:first-child th *, .ncp-communication-title * { color: #fff !important; }
            .ncp-contacts-table thead tr:nth-child(2) th { background: #f2f2f2; font-size: 9px; text-align: center; }
            .ncp-contacts-table th span, .ncp-blue { color: #0563c1 !important; }
            .ncp-doc-link { text-decoration: underline; }
            .ncp-contacts-table td { height: 12px; }
            .ncp-page-two-tip { color: #000; font-size: 8px; font-style: italic; font-weight: 700; margin: 3px 0 6px; text-align: right; }
            .ncp-communication-title { background: #808080; border: 1px solid #8f8f8f; color: #fff; font-size: 10px; font-weight: 900; padding: 3px 5px; }
            .ncp-communication-grid { border-left: 1px solid #a6a6a6; display: grid; grid-template-columns: 1.42fr 0.98fr 0.98fr; }
            .ncp-communication-grid > div { border-right: 1px solid #a6a6a6; border-bottom: 1px solid #a6a6a6; padding: 4px 6px; }
            .ncp-comm-head { align-items: center; display: flex; font-size: 9px; font-weight: 900; justify-content: center; line-height: 1.18; min-height: 31px; text-align: center; }
            .ncp-comm-topic { justify-content: flex-start; text-align: left; }
            .ncp-comm-left, .ncp-comm-middle, .ncp-comm-right { font-size: 8.2px; line-height: 1.18; min-height: 150px; }
            .ncp-comm-left b, .ncp-comm-middle b, .ncp-comm-right b { color: #000; display: block; font-size: 9px; }
            .ncp-comm-left p, .ncp-comm-middle p, .ncp-comm-right p { margin: 1px 0 4px; white-space: pre-wrap; }
            .ncp-comm-left hr { border: 0; border-top: 1px solid #a6a6a6; margin: 4px -6px; }
            .ncp-page-three-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-comments-grid { border-left: 1px solid #a6a6a6; display: grid; grid-template-columns: 1.42fr 0.98fr 0.98fr; }
            .ncp-comments-grid > div { border-right: 1px solid #a6a6a6; border-bottom: 1px solid #a6a6a6; padding: 3px 5px; }
            .ncp-comments-grid .ncp-comm-head { min-height: 52px; }
            .ncp-comments-row { grid-column: 1 / -1; min-height: 16px; font-size: 8.2px; }
            .ncp-section-bar { background: #808080; border: 1px solid #8f8f8f; color: #fff; font-size: 10px; font-weight: 900; padding: 3px 5px; }
            .ncp-med-overview { margin-top: 25px; }
            .ncp-med-overview-grid { border-left: 1px solid #000; display: grid; grid-template-columns: 1.25fr 0.95fr; }
            .ncp-med-overview-grid > div { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; font-size: 8.6px; line-height: 1.22; min-height: 74px; padding: 3px 6px; }
            .ncp-med-overview-grid p { margin: 0 0 4px; }
            .ncp-med-management { display: flex; flex: 1; flex-direction: column; margin-top: 25px; }
            .ncp-med-management-grid { border-left: 1px solid #a6a6a6; display: grid; flex: 1; grid-template-columns: 1fr 0.96fr 1.24fr; }
            .ncp-med-management-grid > div { border-right: 1px solid #a6a6a6; border-bottom: 1px solid #a6a6a6; color: #000; padding: 4px 6px; }
            .ncp-med-intro { font-size: 9px; line-height: 1.38; min-height: 56px; }
            .ncp-med-left, .ncp-med-middle, .ncp-med-right { font-size: 8.4px; line-height: 1.22; min-height: 172px; }
            .ncp-med-left p, .ncp-med-middle p, .ncp-med-right p { margin: 1px 0 5px; white-space: pre-wrap; }
            .ncp-checkbox-list p { margin: 1px 0; }
            .ncp-page-four-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-four-grid { border-left: 1px solid #a6a6a6; display: grid; flex: 1; grid-template-columns: 1fr 0.96fr 1.24fr; }
            .ncp-page-four-grid > div { border-right: 1px solid #a6a6a6; border-bottom: 1px solid #a6a6a6; color: #000; padding: 4px 6px; }
            .ncp-page-four-left, .ncp-page-four-middle, .ncp-page-four-right { font-size: 8.4px; line-height: 1.22; min-height: 228px; }
            .ncp-page-four-left p, .ncp-page-four-right p { margin: 1px 0 4px; }
            .ncp-page-four-right ul, .ncp-rn-block ul { margin: 2px 0 8px 16px; padding: 0; }
            .ncp-page-four-right li, .ncp-rn-block li { margin: 1px 0; }
            .ncp-nested-checkbox { padding-left: 16px; }
            .ncp-indent-line { padding-left: 18px; }
            .ncp-rn-block { margin-top: 7px; padding-left: 16px; }
            .ncp-page-four-plan-title, .ncp-page-four-plan-middle, .ncp-page-four-plan-right { font-size: 8.5px; line-height: 1.24; min-height: 49px; }
            .ncp-page-four-plan-title strong, .ncp-page-four-plan-middle strong, .ncp-page-four-plan-right strong { display: block; font-size: 8.5px; margin-bottom: 4px; }
            .ncp-page-five-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-underlined-bar { text-decoration: underline; }
            .ncp-page-five-health-grid, .ncp-page-five-treatment-grid { border-left: 1px solid #a6a6a6; display: grid; grid-template-columns: 1fr 0.96fr 1.24fr; }
            .ncp-page-five-health-grid > div, .ncp-page-five-treatment-grid > div { border-right: 1px solid #a6a6a6; border-bottom: 1px solid #a6a6a6; color: #000; padding: 4px 6px; }
            .ncp-health-topic { align-items: center; display: flex; font-size: 9px; min-height: 48px; }
            .ncp-health-left, .ncp-health-middle, .ncp-health-right { font-size: 8.4px; line-height: 1.24; min-height: 168px; }
            .ncp-health-middle strong, .ncp-health-right strong, .ncp-health-allergy-title strong, .ncp-health-allergy-cell strong { display: block; font-size: 8.5px; }
            .ncp-health-reporting { font-size: 8.4px; line-height: 1.24; margin-top: 0; padding-top: 68px; }
            .ncp-health-monitoring-spacer { margin-top: 0; padding-top: 52px; }
            .ncp-health-reporting strong { display: inline; font-size: 9.5px; }
            .ncp-health-allergy-title, .ncp-health-allergy-cell { align-items: center; display: flex; font-size: 8.5px; min-height: 32px; }
            .ncp-page-five-treatment-bar { margin-top: 18px; }
            .ncp-page-five-treatment-grid { flex: 1; }
            .ncp-treatment-left, .ncp-treatment-middle, .ncp-treatment-right { font-size: 8.2px; line-height: 1.18; min-height: 174px; }
            .ncp-treatment-left strong, .ncp-treatment-middle strong, .ncp-treatment-right strong { display: block; font-size: 8.5px; }
            .ncp-treatment-right p { margin: 4px 0 0; }
            .ncp-page-six-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-six-grid { border-left: 1px solid #a6a6a6; display: grid; flex: 1; grid-template-columns: 1fr 0.96fr 1.24fr; }
            .ncp-page-six-grid > div { border-right: 1px solid #a6a6a6; border-bottom: 1px solid #a6a6a6; color: #000; padding: 4px 6px; }
            .ncp-page-six-left, .ncp-page-six-middle, .ncp-page-six-right { font-size: 8.5px; line-height: 1.22; min-height: 82px; }
            .ncp-page-six-plan-title, .ncp-page-six-plan-middle, .ncp-page-six-plan-right { font-size: 8.5px; line-height: 1.24; min-height: 56px; }
            .ncp-page-six-plan-title strong, .ncp-page-six-plan-middle strong, .ncp-page-six-plan-right strong { display: block; font-size: 8.5px; margin-bottom: 4px; }
            .ncp-page-seven-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-seven-grid { border-left: 1px solid #a6a6a6; border-top: 1px solid #a6a6a6; display: grid; grid-template-columns: 1fr 0.91fr 1.21fr; grid-template-rows: 52px; margin-top: 32px; }
            .ncp-page-seven-grid > * { border-right: 1px solid #a6a6a6; border-bottom: 1px solid #a6a6a6; color: #000; }
            .ncp-psych-title { align-items: center; background: #808080; color: #fff !important; display: flex; font-size: 10px; font-weight: 900; justify-content: center; min-height: 52px; text-align: center; }
            .ncp-page-seven-grid > .ncp-comm-head:nth-child(2) { grid-column: 2; grid-row: 1; }
            .ncp-page-seven-grid > .ncp-comm-head:nth-child(3) { grid-column: 3; grid-row: 1; }
            .ncp-psych-middle, .ncp-psych-right { font-size: 8.4px; grid-row: 2 / span 18; line-height: 1.1; min-height: 401px; }
            .ncp-psych-group, .ncp-psych-row { grid-column: 1; padding: 1.5px 4.5px; }
            .ncp-psych-sleep { min-height: 60px; }
            .ncp-psych-memory { min-height: 66px; }
            .ncp-psych-decision { min-height: 56px; }
            .ncp-psych-row { min-height: 16px; }
            .ncp-psych-section-line, .ncp-psych-note-line, .ncp-psych-check-line { margin: 0 0 3px; }
            .ncp-psych-note-line { line-height: 1.2; }
            .ncp-psych-middle, .ncp-psych-right { padding: 3px 5px; text-align: center; }
            .ncp-psych-middle { grid-column: 2; }
            .ncp-psych-right { grid-column: 3; }
            .ncp-psych-middle strong, .ncp-psych-right strong { display: block; font-size: 9.5px; line-height: 1.3; }
            .ncp-page-eight-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-eight-grid { border-left: 1px solid #a6a6a6; border-top: 1px solid #a6a6a6; display: grid; grid-template-columns: 1fr 0.91fr 1.21fr; grid-template-rows: 52px repeat(8, 16px) 182px 139px; margin-top: 16px; }
            .ncp-page-eight-grid > * { border-right: 1px solid #a6a6a6; border-bottom: 1px solid #a6a6a6; color: #000; }
            .ncp-page-eight-grid > .ncp-comm-head:nth-child(2) { grid-column: 2; grid-row: 1; }
            .ncp-page-eight-grid > .ncp-comm-head:nth-child(3) { grid-column: 3; grid-row: 1; }
            .ncp-page-eight-behavior-row { align-items: center; display: flex; font-size: 8px; font-weight: 800; gap: 4px; grid-column: 1; padding: 1px 4px; }
            .ncp-page-eight-support, .ncp-page-eight-programs { font-size: 8px; grid-column: 1; line-height: 1.14; padding: 4px; }
            .ncp-page-eight-support p, .ncp-page-eight-programs p { margin: 0 0 3px; }
            .ncp-spaced-line { margin-top: 16px !important; }
            .ncp-page-eight-middle-top, .ncp-page-eight-right-top, .ncp-page-eight-middle-bottom, .ncp-page-eight-right-bottom { font-size: 8px; padding: 3px 4px; }
            .ncp-page-eight-middle-top, .ncp-page-eight-right-top { grid-row: 2 / span 9; }
            .ncp-page-eight-middle-top { grid-column: 2; }
            .ncp-page-eight-right-top { grid-column: 3; }
            .ncp-page-eight-middle-bottom, .ncp-page-eight-right-bottom { grid-row: 11; }
            .ncp-page-eight-middle-bottom { grid-column: 2; }
            .ncp-page-eight-right-bottom { grid-column: 3; }
            .ncp-page-nine-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-nine-psych-grid, .ncp-page-nine-left-grid, .ncp-page-nine-universal-grid { border-left: 1px solid #000; border-top: 1px solid #000; display: grid; grid-template-columns: 1fr 0.98fr 1.25fr; }
            .ncp-page-nine-psych-grid > *, .ncp-page-nine-left-grid > *, .ncp-page-nine-universal-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-nine-left-grid > .ncp-section-bar, .ncp-page-nine-universal-grid > .ncp-section-bar, .ncp-page-ten-universal-grid > .ncp-section-bar { color: #fff !important; }
            .ncp-page-nine-psych-grid { grid-template-rows: 48px 51px 34px; margin-top: 11px; }
            .ncp-page-nine-left-grid { grid-template-rows: 48px 47px; margin-top: 18px; }
            .ncp-page-nine-universal-grid { flex: 1; grid-template-rows: 48px 1fr; margin-top: 18px; }
            .ncp-page-nine-psych-grid > .ncp-comm-head:nth-child(2), .ncp-page-nine-left-grid > .ncp-comm-head:nth-child(2), .ncp-page-nine-universal-grid > .ncp-comm-head:nth-child(2) { grid-column: 2; grid-row: 1; }
            .ncp-page-nine-psych-grid > .ncp-comm-head:nth-child(3), .ncp-page-nine-left-grid > .ncp-comm-head:nth-child(3), .ncp-page-nine-universal-grid > .ncp-comm-head:nth-child(3) { grid-column: 3; grid-row: 1; }
            .ncp-page-nine-interventions, .ncp-page-nine-left-title, .ncp-page-nine-left-middle, .ncp-page-nine-left-right, .ncp-page-nine-universal-left, .ncp-page-nine-universal-middle, .ncp-page-nine-universal-right, .ncp-page-nine-psych-middle, .ncp-page-nine-psych-right, .ncp-page-nine-narrative { font-size: 8.3px; line-height: 1.18; padding: 4px 5px; }
            .ncp-page-nine-interventions { display: grid; gap: 2px; }
            .ncp-page-nine-narrative { grid-column: 1 / -1; }
            .ncp-page-nine-universal-left { display: grid; gap: 2px; }
            .ncp-page-nine-universal-right p { margin: 0 0 6px; }
            .ncp-page-ten-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-ten-universal-grid, .ncp-page-ten-adl-grid { border-left: 1px solid #000; border-top: 1px solid #000; display: grid; grid-template-columns: 1fr 0.96fr 1.02fr; }
            .ncp-page-ten-universal-grid > *, .ncp-page-ten-adl-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-ten-universal-grid { grid-template-rows: 48px 110px; margin-top: 9px; }
            .ncp-page-ten-adl-grid { flex: 1; grid-template-rows: 48px 32px 1fr; margin-top: 18px; }
            .ncp-page-ten-universal-grid > .ncp-section-bar, .ncp-page-ten-adl-grid > .ncp-section-bar { color: #fff !important; }
            .ncp-page-ten-universal-grid > .ncp-comm-head:nth-child(2), .ncp-page-ten-adl-grid > .ncp-comm-head:nth-child(2) { grid-column: 2; grid-row: 1; }
            .ncp-page-ten-universal-grid > .ncp-comm-head:nth-child(3), .ncp-page-ten-adl-grid > .ncp-comm-head:nth-child(3) { grid-column: 3; grid-row: 1; }
            .ncp-page-ten-universal-left, .ncp-page-ten-universal-middle, .ncp-page-ten-universal-right, .ncp-page-ten-functional, .ncp-page-ten-ambulation, .ncp-page-ten-adl-middle, .ncp-page-ten-adl-right { font-size: 8.3px; line-height: 1.22; padding: 4px 5px; }
            .ncp-page-ten-universal-left { display: grid; gap: 4px; }
            .ncp-page-ten-functional { grid-column: 1 / -1; }
            .ncp-page-ten-ambulation p, .ncp-page-ten-adl-middle p, .ncp-page-ten-adl-right p { margin: 0 0 7px; }
            .ncp-page-eleven-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-eleven-adl-grid { border-left: 1px solid #000; border-top: 1px solid #000; display: grid; flex: 1; grid-template-columns: 1fr 0.86fr 0.86fr; grid-template-rows: 48px 214px 1fr; margin-top: 10px; }
            .ncp-page-eleven-adl-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-eleven-adl-grid > .ncp-section-bar { color: #fff !important; }
            .ncp-page-eleven-adl-grid > .ncp-comm-head:nth-child(2) { grid-column: 2; grid-row: 1; }
            .ncp-page-eleven-adl-grid > .ncp-comm-head:nth-child(3) { grid-column: 3; grid-row: 1; }
            .ncp-page-eleven-top-left, .ncp-page-eleven-top-middle, .ncp-page-eleven-top-right, .ncp-page-eleven-transfer-left, .ncp-page-eleven-transfer-middle, .ncp-page-eleven-transfer-right { font-size: 8.3px; line-height: 1.22; padding: 4px 5px; }
            .ncp-page-eleven-top-left p, .ncp-page-eleven-transfer-left p, .ncp-page-eleven-transfer-middle p, .ncp-page-eleven-transfer-right p { margin: 0 0 7px; }
            .ncp-page-twelve-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-twelve-adl-grid { border-left: 1px solid #000; border-top: 1px solid #000; display: grid; flex: 1; grid-template-columns: 1fr 0.86fr 0.86fr; grid-template-rows: 48px 192px 232px 1fr; margin-top: 10px; }
            .ncp-page-twelve-adl-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-twelve-adl-grid > .ncp-section-bar { color: #fff !important; }
            .ncp-page-twelve-adl-grid > .ncp-comm-head:nth-child(2) { grid-column: 2; grid-row: 1; }
            .ncp-page-twelve-adl-grid > .ncp-comm-head:nth-child(3) { grid-column: 3; grid-row: 1; }
            .ncp-page-twelve-device-left, .ncp-page-twelve-device-middle, .ncp-page-twelve-device-right, .ncp-page-twelve-eating-left, .ncp-page-twelve-eating-middle, .ncp-page-twelve-eating-right, .ncp-page-twelve-toileting-left, .ncp-page-twelve-toileting-middle, .ncp-page-twelve-toileting-right { font-size: 8.3px; line-height: 1.18; padding: 4px 5px; }
            .ncp-page-twelve-device-left p, .ncp-page-twelve-eating-left p, .ncp-page-twelve-eating-middle p, .ncp-page-twelve-eating-right p, .ncp-page-twelve-toileting-left p, .ncp-page-twelve-toileting-middle p, .ncp-page-twelve-toileting-right p { margin: 0 0 5px; }
            .ncp-page-thirteen-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-thirteen-adl-grid { border-left: 1px solid #000; border-top: 1px solid #000; display: grid; flex: 1; grid-template-columns: 1fr 0.86fr 0.86fr; grid-template-rows: 48px 224px 184px 1fr; margin-top: 10px; }
            .ncp-page-thirteen-adl-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-thirteen-adl-grid > .ncp-section-bar { color: #fff !important; }
            .ncp-page-thirteen-adl-grid > .ncp-comm-head:nth-child(2) { grid-column: 2; grid-row: 1; }
            .ncp-page-thirteen-adl-grid > .ncp-comm-head:nth-child(3) { grid-column: 3; grid-row: 1; }
            .ncp-page-thirteen-toileting-left, .ncp-page-thirteen-toileting-middle, .ncp-page-thirteen-toileting-right, .ncp-page-thirteen-dressing-left, .ncp-page-thirteen-dressing-middle, .ncp-page-thirteen-dressing-right, .ncp-page-thirteen-hygiene-left, .ncp-page-thirteen-hygiene-middle, .ncp-page-thirteen-hygiene-right { font-size: 8.3px; line-height: 1.18; padding: 4px 5px; }
            .ncp-page-thirteen-toileting-left p, .ncp-page-thirteen-toileting-right p, .ncp-page-thirteen-dressing-left p, .ncp-page-thirteen-dressing-middle p, .ncp-page-thirteen-dressing-right p, .ncp-page-thirteen-hygiene-left p, .ncp-page-thirteen-hygiene-middle p, .ncp-page-thirteen-hygiene-right p { margin: 0 0 5px; }
            .ncp-page-fourteen-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-fourteen-adl-grid { border-left: 1px solid #000; border-top: 1px solid #000; display: grid; flex: 1; grid-template-columns: 1fr 0.86fr 0.86fr; grid-template-rows: 48px 230px 187px 1fr; margin-top: 10px; }
            .ncp-page-fourteen-adl-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-fourteen-adl-grid > .ncp-section-bar { color: #fff !important; }
            .ncp-page-fourteen-adl-grid > .ncp-comm-head:nth-child(2) { grid-column: 2; grid-row: 1; }
            .ncp-page-fourteen-adl-grid > .ncp-comm-head:nth-child(3) { grid-column: 3; grid-row: 1; }
            .ncp-page-fourteen-hygiene-left, .ncp-page-fourteen-hygiene-middle, .ncp-page-fourteen-hygiene-right, .ncp-page-fourteen-bathing-left, .ncp-page-fourteen-bathing-middle, .ncp-page-fourteen-bathing-right, .ncp-page-fourteen-foot-left, .ncp-page-fourteen-foot-middle, .ncp-page-fourteen-foot-right { font-size: 8.3px; line-height: 1.18; padding: 4px 5px; }
            .ncp-page-fourteen-hygiene-left p, .ncp-page-fourteen-hygiene-middle p, .ncp-page-fourteen-hygiene-right p, .ncp-page-fourteen-bathing-left p, .ncp-page-fourteen-bathing-middle p, .ncp-page-fourteen-bathing-right p, .ncp-page-fourteen-foot-left p, .ncp-page-fourteen-foot-middle p, .ncp-page-fourteen-foot-right p { margin: 0 0 5px; }
            .ncp-page-fifteen-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-fifteen-adl-grid { border-left: 1px solid #000; border-top: 1px solid #000; display: grid; flex: 1; grid-template-columns: 1fr 0.86fr 0.86fr; grid-template-rows: 48px 204px 1fr; margin-top: 10px; }
            .ncp-page-fifteen-adl-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-fifteen-adl-grid > .ncp-section-bar { color: #fff !important; }
            .ncp-page-fifteen-adl-grid > .ncp-comm-head:nth-child(2) { grid-column: 2; grid-row: 1; }
            .ncp-page-fifteen-adl-grid > .ncp-comm-head:nth-child(3) { grid-column: 3; grid-row: 1; }
            .ncp-page-fifteen-foot-left, .ncp-page-fifteen-foot-middle, .ncp-page-fifteen-foot-right, .ncp-page-fifteen-skin-left, .ncp-page-fifteen-skin-middle, .ncp-page-fifteen-skin-right { font-size: 8.3px; line-height: 1.18; padding: 4px 5px; }
            .ncp-page-fifteen-foot-left p, .ncp-page-fifteen-foot-middle p, .ncp-page-fifteen-foot-right p, .ncp-page-fifteen-skin-left p, .ncp-page-fifteen-skin-middle p, .ncp-page-fifteen-skin-right p { margin: 0 0 5px; }
            .ncp-page-sixteen-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-sixteen-iadl-grid { border-top: 1px solid #000; display: grid; flex: 0 0 auto; grid-template-columns: 1fr 0.86fr 0.86fr; grid-template-rows: 48px 156px 148px 166px 48px; margin-top: 10px; }
            .ncp-page-sixteen-iadl-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-sixteen-iadl-grid > .ncp-section-bar { color: #fff !important; }
            .ncp-page-sixteen-iadl-grid > .ncp-section-bar, .ncp-page-sixteen-finances-left, .ncp-page-sixteen-shopping-left, .ncp-page-sixteen-transport-left, .ncp-page-sixteen-social-left { border-left: 1px solid #000; }
            .ncp-page-sixteen-iadl-grid > .ncp-comm-head:nth-child(2) { grid-column: 2; grid-row: 1; }
            .ncp-page-sixteen-iadl-grid > .ncp-comm-head:nth-child(3) { grid-column: 3; grid-row: 1; }
            .ncp-page-sixteen-finances-left, .ncp-page-sixteen-finances-middle, .ncp-page-sixteen-finances-right, .ncp-page-sixteen-shopping-left, .ncp-page-sixteen-shopping-middle, .ncp-page-sixteen-shopping-right, .ncp-page-sixteen-transport-left, .ncp-page-sixteen-transport-middle, .ncp-page-sixteen-transport-right, .ncp-page-sixteen-social-left, .ncp-page-sixteen-social-middle, .ncp-page-sixteen-social-right { font-size: 7.7px; line-height: 1.08; padding: 3px 4px; }
            .ncp-page-sixteen-finances-left p, .ncp-page-sixteen-finances-middle p, .ncp-page-sixteen-finances-right p, .ncp-page-sixteen-shopping-left p, .ncp-page-sixteen-shopping-middle p, .ncp-page-sixteen-shopping-right p, .ncp-page-sixteen-transport-left p, .ncp-page-sixteen-transport-middle p, .ncp-page-sixteen-transport-right p, .ncp-page-sixteen-social-left p, .ncp-page-sixteen-social-middle p, .ncp-page-sixteen-social-right p { margin: 0 0 3px; }
            .ncp-page-seventeen-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-seventeen-grid { border-top: 1px solid #000; display: grid; flex: 0 0 auto; grid-template-columns: 1fr 0.86fr 0.86fr; grid-template-rows: 172px 17px 144px 53px 1fr; margin-bottom: 15px; margin-top: 5px; }
            .ncp-page-seventeen-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-seventeen-social-left, .ncp-page-seventeen-preferences-title, .ncp-page-seventeen-preferences-left, .ncp-page-seventeen-narrative, .ncp-page-seventeen-smoking-left { border-left: 1px solid #000; }
            .ncp-page-seventeen-preferences-title, .ncp-page-seventeen-narrative { grid-column: 1 / -1; }
            .ncp-page-seventeen-social-left, .ncp-page-seventeen-social-middle, .ncp-page-seventeen-social-right, .ncp-page-seventeen-preferences-left, .ncp-page-seventeen-preferences-middle, .ncp-page-seventeen-preferences-right, .ncp-page-seventeen-narrative, .ncp-page-seventeen-smoking-left, .ncp-page-seventeen-smoking-middle, .ncp-page-seventeen-smoking-right { font-size: 8.7px; line-height: 1.08; padding: 3px 4px; }
            .ncp-page-seventeen-preferences-title { align-items: center; display: flex; font-size: 9px; justify-content: center; padding: 1px 4px; text-align: center; }
            .ncp-page-seventeen-social-left p, .ncp-page-seventeen-social-middle p, .ncp-page-seventeen-social-right p, .ncp-page-seventeen-preferences-left p, .ncp-page-seventeen-preferences-middle p, .ncp-page-seventeen-preferences-right p, .ncp-page-seventeen-smoking-left p, .ncp-page-seventeen-smoking-middle p, .ncp-page-seventeen-smoking-right p { margin: 0 0 3px; }
            .ncp-page-eighteen-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-eighteen-grid { border-top: 1px solid #000; display: grid; grid-template-columns: 1fr 0.86fr 0.86fr; grid-template-rows: 126px 50px 158px; margin-top: 7px; }
            .ncp-page-eighteen-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-eighteen-case-left, .ncp-page-eighteen-other, .ncp-page-eighteen-review-left { border-left: 1px solid #000; }
            .ncp-page-eighteen-other { grid-column: 1 / -1; }
            .ncp-page-eighteen-case-left, .ncp-page-eighteen-case-middle, .ncp-page-eighteen-case-right, .ncp-page-eighteen-other, .ncp-page-eighteen-review-left, .ncp-page-eighteen-review-middle, .ncp-page-eighteen-review-right { color: #000; font-size: 9.2px; line-height: 1.1; padding: 3px 5px; }
            .ncp-page-eighteen-case-left p, .ncp-page-eighteen-case-right p, .ncp-page-eighteen-review-middle p, .ncp-page-eighteen-review-right p { margin: 0 0 4px; }
            .ncp-page-eighteen-case-right ul, .ncp-page-eighteen-review-right ul { margin: 1px 0 0 14px; padding: 0; }
            .ncp-page-eighteen-abbreviations { color: #000; display: grid; font-size: 9.2px; grid-template-columns: 1fr 1fr 1fr 1fr; line-height: 1.12; margin-top: 1px; }
            .ncp-page-eighteen-abbreviations > strong { grid-column: 1 / -1; font-size: 9.8px; }
            .ncp-page-eighteen-abbreviations p { margin: 0 0 3px; }
            .ncp-page-eighteen-approval { color: #000; font-size: 9.2px; line-height: 1.12; margin-top: 14px; }
            .ncp-page-eighteen-approval h3 { color: #000; font-size: 12px; margin: 0 0 4px; }
            .ncp-page-eighteen-approval p { margin: 10px 0 0; }
            .ncp-page-nineteen-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-nineteen-grid { border-top: 1px solid #000; display: grid; grid-template-columns: 0.62fr 0.64fr 0.98fr 0.98fr; grid-template-rows: 37px repeat(10, 26.5px) 21px 58px; margin-top: 7px; }
            .ncp-page-nineteen-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-nineteen-involved, .ncp-page-nineteen-verbal, .ncp-page-nineteen-recommendations { border-left: 1px solid #000; }
            .ncp-page-nineteen-involved { font-size: 8.6px; grid-column: 1; grid-row: 1 / 12; line-height: 1.12; padding: 3px 5px; }
            .ncp-page-nineteen-head { align-items: center; display: flex; font-size: 8.6px; font-weight: 900; justify-content: space-between; padding: 3px 5px; }
            .ncp-page-nineteen-approver, .ncp-page-nineteen-signature { font-size: 8.2px; line-height: 1.1; padding: 3px 4px; }
            .ncp-page-nineteen-verbal { font-size: 8.4px; grid-column: 1 / 3; padding: 3px 5px; }
            .ncp-page-nineteen-sent { font-size: 8.4px; grid-column: 3 / 5; padding: 3px 5px; }
            .ncp-page-nineteen-recommendations { font-size: 8.4px; grid-column: 1 / 5; padding: 4px 5px; }
            .ncp-page-nineteen-involved p, .ncp-page-nineteen-approver p, .ncp-page-nineteen-signature p { margin: 0 0 3px; }
            .ncp-page-nineteen-note { color: #000; font-size: 10px; line-height: 1.18; margin-top: 4px; }
            .ncp-med-right ul { margin: 3px 0 0 18px; padding: 0; }
            .ncp-preview-table { border-collapse: collapse; margin: 0 0 8px; table-layout: fixed; width: 100%; }
            .ncp-preview-table th { background: #eef7ee; border: 1px solid #a6a6a6; color: #143f2b; font-size: 11px; padding: 6px; text-align: left; }
            .ncp-preview-table td { border: 1px solid #b8c2cc; padding: 5px 6px; vertical-align: top; }
            .ncp-preview-label { background: #f8fafc; color: #111827; font-weight: 700; width: 32%; }
            .ncp-preview-value { min-height: 18px; white-space: pre-wrap; }
          </style>
        </head>
        <body>
          ${negotiatedTemplateDocumentHtml}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const printPreliminaryPlan = () => {
    const printWindow = window.open("", "_blank", "width=920,height=720");
    if (!printWindow) return;
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>${recordName} - ${processResidentName(assessment)}</title>
          <style>
            body { color: #172b21; font-family: Arial, sans-serif; line-height: 1.5; margin: 40px; }
            h1 { color: #143f2b; margin-bottom: 4px; }
            .meta { border-bottom: 2px solid #d9ead6; color: #475569; font-weight: 700; margin-bottom: 24px; padding-bottom: 12px; }
            h2, h3, h4 { color: #4e8a3c; }
            strong { color: #143f2b; }
          </style>
        </head>
        <body>
          <h1>${recordName}</h1>
          <div class="meta">${processResidentName(assessment)} - ${assessment.name || assessment.title || "Assessment process"}</div>
          ${planDescription}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const renderNcpFillableLabel = (key, label) => {
    let checkboxIndex = 0;
    return String(label).split(/(\[\s*\])/g).map((part, index) => {
      if (!/\[\s*\]/.test(part)) return part;
      const currentIndex = checkboxIndex;
      checkboxIndex += 1;
      return (
        <button
          aria-label={`${label} option ${checkboxIndex}`}
          aria-pressed={ncpCheckValue(key, currentIndex)}
          className="ncp-bracket-toggle"
          key={`${label}-${index}`}
          onClick={(event) => {
            event.preventDefault();
            updateNegotiatedTemplateCheck(key, currentIndex, !ncpCheckValue(key, currentIndex));
          }}
          type="button"
        >
          {ncpCheckText(key, currentIndex)}
        </button>
      );
    });
  };

  const renderNegotiatedField = (key, label, className = "") => (
    <label className={`ncp-page-one-field ${className}`.trim()}>
      <span>{renderNcpFillableLabel(key, label)}</span>
      <textarea
        aria-label={label}
        value={negotiatedFieldValue(key)}
        onChange={(event) => updateNegotiatedTemplateField(key, event.target.value)}
      />
    </label>
  );

  return (
    <CareShell className="dashboard-content residents-page assessments-page">
      <div className="residents-page-head">
        <div className="page-title">
          <h1>{recordName}</h1>
          <p>{processResidentName(assessment)} - {assessment.name || assessment.title || "Assessment process"}</p>
        </div>
        <div className="residents-actions">
          <Link className="mini-btn" to={`/assessments/${assessment.id}`}><i className="ti ti-arrow-left" /> Assessment Process</Link>
        </div>
      </div>

      {record && isNegotiatedPlan ? (
        <>
          <div className="assessment-drawer-action-links assessment-page-action-links probation-action-buttons preliminary-plan-actions">
            <button className="primary-action" type="button"><i className="ti ti-sparkles" /> Generate From Resources</button>
            <button type="button" onClick={improveNegotiatedPlan}><i className="ti ti-wand" /> Improve</button>
            <button type="button" onClick={formatNegotiatedPlan}><i className="ti ti-brush" /> Format</button>
            <button type="button" disabled={!negotiatedPlanHistory.length} onClick={undoNegotiatedPlanAction}><i className="ti ti-arrow-back-up" /> Undo</button>
            <button type="button" onClick={() => setNegotiatedPreviewOpen(true)}><i className="ti ti-eye" /> Preview</button>
            <button type="button" onClick={printNegotiatedPlan}><i className="ti ti-printer" /> Print</button>
            <button className="success" type="button" onClick={confirmMarkCompleted}><i className="ti ti-circle-check" /> Mark as Completed</button>
          </div>

          <div className="preliminary-plan-grid negotiated-plan-grid">
            <div className="preliminary-plan-main negotiated-template-main">
              <section className="assessment-drawer-section negotiated-template-banner">
                <h3>Standard Template Document</h3>
                <p>The template structure is locked to the AFH/HCS Negotiated Care Plan form. AI fills the fields from previous resources; staff can edit and improve the generated content inside each section.</p>
              </section>
              <section className="assessment-drawer-section negotiated-template-document">
                <div className="negotiated-template-title">
                  <h3>Adult Family Home Resident Negotiated Care Plan (NCP)</h3>
                  <span>Form Version {record.formVersion}</span>
                </div>
                {negotiatedTemplatePages.map((page) => (
                  <div className="negotiated-form-page" key={page.pageNumber}>
                    <div className="negotiated-form-page-head">
                      <strong>Page {page.pageNumber} of 19</strong>
                      <span>Landscape NCP template page</span>
                    </div>
                    {page.pageNumber === 1 ? (
                      <div className="ncp-page-one-grid ncp-page-one-form-grid">
                        <div className="ncp-cell ncp-provider-cell">{renderNegotiatedField("providerName", "Provider's Name")}</div>
                        <div className="ncp-cell ncp-started-cell">{renderNegotiatedField("dateNcpStarted", "Date NCP Started")}</div>
                        <div className="ncp-cell ncp-moved-cell">{renderNegotiatedField("movedInDate", "Moved In Date")}</div>
                        <div className="ncp-cell ncp-completed-cell">{renderNegotiatedField("dateCompleted", "Date Completed")}</div>
                        <div className="ncp-cell ncp-discharged-cell">{renderNegotiatedField("dateDischarged", "Date Discharged")}</div>
                        <div className="ncp-cell ncp-resident-cell">
                          {renderNegotiatedField("residentName", "Resident's Name")}
                          {renderNegotiatedField("pronouns", "Pronouns")}
                        </div>
                        <div className="ncp-cell ncp-dob-cell">{renderNegotiatedField("dateOfBirthAge", "Date of Birth/Age")}</div>
                        <div className="ncp-cell ncp-language-cell">
                          {renderNegotiatedField("primaryLanguage", "Primary Language")}
                          {renderNegotiatedField("speaksEnglish", "Speaks English?")}
                          {renderNegotiatedField("interpreterNeeded", "Interpreter needed?")}
                        </div>
                        <div className="ncp-cell ncp-allergy-cell">{renderNegotiatedField("allergies", "ALLERGIES:", "ncp-allergy-form-field")}</div>
                        <div className="ncp-cell ncp-left-column">
                          {renderNegotiatedField("legalDocuments", "Legal Documents: [] None / [] Advanced Directives / [ ] POLST Form / [ ] Other")}
                          {renderNegotiatedField("specialtyNeeds", "Specialty Needs: [ ] No / [ ] Dementia / [ ] Mental Health / [ ] Developmental Disability / [ ] Other")}
                        </div>
                        <div className="ncp-cell ncp-emergency-heading"><strong>EMERGENCY EVACUATION</strong></div>
                        <div className="ncp-cell ncp-evacuation-copy">
                          {renderNegotiatedField("evacuationIndependent", "[] NONE - RESIDENT IS INDEPENDENT")}
                          {renderNegotiatedField("evacuationAssistance", "[] ASSISTANCE REQUIRED")}
                          {renderNegotiatedField("evacuationSafetyInstructions", "RESIDENT'S EVACUATION and SAFETY INSTRUCTIONS")}
                          {renderNegotiatedField("evacuationCaregiverInstructions", "Caregiver will")}
                        </div>
                        <div className="ncp-medical-band ncp-medical-form-band">
                          {renderNegotiatedField("medicalDiagnosis", "MENTAL/PHYSICAL HEALTH - CURRENT MEDICAL STATUS/DIAGNOSIS")}
                        </div>
                        <div className="ncp-index-grid">
                          {ncpPageOneIndexLinks.map((link) => <span key={link}>{link}</span>)}
                        </div>
                        <div className="ncp-page-footer">
                          <span>Resident's Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 1 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 2 ? (
                      <div className="ncp-page-two-sheet ncp-page-two-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-contacts-form-table">
                          <div className="ncp-contacts-form-title">
                            <strong>RESPONSIBLE PARTIES - CONTACTS</strong>
                            <span>Add those involved in care planning for your resident: Case Manager, DPOA, Guardian, Medical/Mental Health Providers, Pharmacy, Hospice, etc. - Indicate which contact method is preferred.</span>
                          </div>
                          <table className="ncp-contacts-edit-table">
                            <thead>
                              <tr>
                                {ncpContactColumns.map((column) => <th key={column.key}>{column.label}</th>)}
                              </tr>
                            </thead>
                            <tbody>
                              {[1, 2, 3, 4, 5, 6].map((number) => (
                                <tr key={number}>
                                  {ncpContactColumns.map((column, columnIndex) => {
                                    const fieldKey = ncpContactFieldKey(number, column.key);
                                    return (
                                      <td key={fieldKey}>
                                        <label className="ncp-contact-cell-field">
                                          {columnIndex === 0 ? <span>{number}.</span> : null}
                                          <input
                                            aria-label={`Contact ${number} ${column.label}`}
                                            value={negotiatedFieldValue(fieldKey)}
                                            onChange={(event) => updateNegotiatedTemplateField(fieldKey, event.target.value)}
                                          />
                                        </label>
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p className="ncp-page-two-tip">(Place your cursor in the last box and hit your tab key to add another row)</p>
                        <div className="ncp-communication-title">COMMUNICATION</div>
                        <div className="ncp-communication-grid ncp-communication-form-grid">
                          <div className="ncp-comm-head ncp-comm-topic">SPEECH/HEARING/VISION</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Caregiver Instructions<br />Who, How, When/How Often</div>
                          <div className="ncp-comm-left">
                            {renderNegotiatedField("communicationModes", "Modes of Expression")}
                            {renderNegotiatedField("expressionProblems", "Problems with Mode of Expression - [ ] Yes [ ] No / Describe")}
                            {renderNegotiatedField("expressionEquipment", "Equipment")}
                            {renderNegotiatedField("hearingProblems", "Problems with Hearing - [ ] Yes [ ] No / Describe / Equipment")}
                            {renderNegotiatedField("visionProblems", "Problems with Vision - [ ] Yes [ ] No / Describe / Equipment")}
                            {renderNegotiatedField("phoneAbility", "Ability to Use the Phone")}
                            {renderNegotiatedField("preferredLanguageComments", "Preferred Language")}
                          </div>
                          <div className="ncp-comm-middle">
                            {renderNegotiatedField("selfUnderstood", "How resident makes self-understood")}
                            {renderNegotiatedField("understandsOthers", "How resident understands others")}
                            {renderNegotiatedField("communicationStrengths", "Strength and Abilities")}
                          </div>
                          <div className="ncp-comm-right">
                            {renderNegotiatedField("communicationAssistance", "Assistance Required / Caregiver will")}
                          </div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 2 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 3 ? (
                      <div className="ncp-page-three-sheet ncp-page-three-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-communication-title">COMMUNICATION</div>
                        <div className="ncp-comments-grid ncp-comments-form-grid">
                          <div className="ncp-comm-head ncp-comm-topic">SPEECH/HEARING/VISION</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Caregiver Instructions<br />Who, How, When/How Often</div>
                          <div className="ncp-comments-row">{renderNegotiatedField("communicationComments", "Comments")}</div>
                        </div>
                        <div className="ncp-med-overview">
                          <div className="ncp-section-bar">MEDICATION MANAGEMENT - Overview</div>
                          <div className="ncp-med-overview-grid ncp-med-overview-form-grid">
                            <div>
                              <div className="ncp-red-field">{renderNegotiatedField("medicationAllergies", "[ ] MEDICATION ALLERGIES")}</div>
                              {renderNegotiatedField("medicationAssistanceNeeds", "[ ] Resident needs more than one kind of medication assistance need")}
                              {renderNegotiatedField("psychopharmacologicMeds", "[ ] Resident is prescribed psychopharmacologic medications")}
                            </div>
                            <div>
                              {renderNegotiatedField("marReference", "[ ] See MAR for current medications, dosage, frequency, and route")}
                              {renderNegotiatedField("medsOrderedBy", "Meds are ordered by")}
                              {renderNegotiatedField("medsDeliveredBy", "Meds are delivered by")}
                              <div className="ncp-blue-field ncp-link-field">{renderNegotiatedField("pharmacyPacked", "[ ] Meds are Pharmacy Packed / Note")}</div>
                            </div>
                          </div>
                        </div>
                        <div className="ncp-med-management">
                          <div className="ncp-section-bar">MEDICATION MANAGEMENT</div>
                          <div className="ncp-med-management-grid ncp-med-management-form-grid">
                            <div className="ncp-med-intro"><em>The amount of assistance required to receive prescription medications, over the counter medications, or herbal supplements.</em></div>
                            <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                            <div className="ncp-comm-head">Assistance Required<br />Caregiver Instruction<br />Who, How, When/How Often</div>
                            <div className="ncp-med-left">
                              <div className="ncp-blue-field ncp-link-field">{renderNegotiatedField("medicationManagementType", "[ ] SELF-ADMINISTRATION / [ ] SELF-ADMINISTRATION W/ ASSISTANCE / [ ] MEDICATION ADMINISTRATION")}</div>
                              <div className="ncp-route-checklist">
                                <strong>Equipment:</strong>
                                <span>Type of Medication Management is Needed:</span>
                                {ncpPageThreeMedicationRouteOptions.map((option) => (
                                  <label key={option}>
                                    <input type="checkbox" />
                                    {option}
                                  </label>
                                ))}
                              </div>
                              {renderNegotiatedField("medicationRoutes", "Other / notes")}
                            </div>
                            <div className="ncp-med-middle">
                              <div className="ncp-blue-field">{renderNegotiatedField("medicationStrengths", "Strengths and Abilities")}</div>
                            </div>
                            <div className="ncp-med-right">
                              <div className="ncp-blue-field">{renderNegotiatedField("medicationAssistance", "Assistance Required / Caregiver will")}</div>
                            </div>
                          </div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 3 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 4 ? (
                      <div className="ncp-page-four-sheet ncp-page-four-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-section-bar">MEDICATION MANAGEMENT</div>
                        <div className="ncp-page-four-grid ncp-page-four-form-grid">
                          <div className="ncp-med-intro"><em>The amount of assistance required to receive prescription medications, over the counter medications, or herbal supplements.</em></div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Caregiver Instruction<br />Who, How, When/How Often</div>
                          <div className="ncp-page-four-left">
                            <div className="ncp-route-checklist">
                              <strong>[ ] Injections:</strong>
                              {injectionSupportOptions.map((option) => (
                                <label className={option === "(Insulin Only)" ? "ncp-indent-line" : ""} key={option}>
                                  {option === "(Insulin Only)" ? null : <input type="checkbox" />}
                                  {option}
                                </label>
                              ))}
                              {ncpPageFourMedicationRouteOptions.map((option) => (
                                <label key={option}>
                                  <input type="checkbox" />
                                  {option}
                                </label>
                              ))}
                            </div>
                            <div className="ncp-blue-field ncp-link-field">{renderNegotiatedField("nurseDelegation", "[ ] Requires Nurse Delegation / RN Delegator")}</div>
                          </div>
                          <div className="ncp-page-four-middle" />
                          <div className="ncp-page-four-right">
                            <div className="ncp-blue-field">{renderNegotiatedField("medicationAssistance", "Assistance Required / Caregiver will")}</div>
                          </div>
                          <div className="ncp-page-four-plan-title">
                            <strong className="ncp-blue">Medication Plan When Resident is not in the AFH</strong>
                          </div>
                          <div className="ncp-page-four-plan-middle">
                            {renderNegotiatedField("medicationOutsideAfhStrengths", "Strengths and Abilities")}
                          </div>
                          <div className="ncp-page-four-plan-right ncp-blue-field">
                            {renderNegotiatedField("medicationOutsideAfh", "Assistance Required / Caregiver will")}
                          </div>
                          <div className="ncp-page-four-plan-title">
                            <strong className="ncp-blue">Medication Refusal Plan</strong>
                          </div>
                          <div className="ncp-page-four-plan-middle">
                            {renderNegotiatedField("medicationRefusalStrengths", "Strengths and Abilities")}
                          </div>
                          <div className="ncp-page-four-plan-right">
                            {renderNegotiatedField("medicationRefusalPlan", "Assistance Required / Caregiver will")}
                          </div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 4 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 5 ? (
                      <div className="ncp-page-five-sheet ncp-page-five-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-section-bar ncp-underlined-bar">HEALTH INDICATORS</div>
                        <div className="ncp-page-five-health-grid ncp-page-five-form-grid">
                          <div className="ncp-health-topic"><strong>Health Indicator Monitoring and Support</strong></div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-health-left">
                            <div className="ncp-route-checklist">
                              {healthIndicatorOptions.map((option) => {
                                const isNested = option === "Pain Impact:" || option === "Current Weight:" || option === "Current Height:";
                                const isBlue = option === "Pain" || option === "Other:";
                                return (
                                  <label className={isNested ? "ncp-indent-line" : ""} key={option}>
                                    {isNested ? null : <input type="checkbox" />}
                                    <span className={isBlue ? "ncp-blue" : ""}>{option}</span>
                                  </label>
                                );
                              })}
                            </div>
                            {renderNegotiatedField("healthIndicatorMonitoring", "Health indicator notes")}
                          </div>
                          <div className="ncp-health-middle">
                            {renderNegotiatedField("healthStrengths", "Strengths and Abilities")}
                          </div>
                          <div className="ncp-health-right">
                            <strong>Assistance Required</strong>
                            {renderNegotiatedField("healthAssistance", "Assistance details")}
                            <div className="ncp-health-monitoring-spacer">
                              {renderNegotiatedField("monitoringReporting", "Monitoring/Reporting significant changes and/or concerns")}
                            </div>
                          </div>
                          <div className="ncp-health-allergy-title"><strong>Allergies</strong></div>
                          <div className="ncp-health-allergy-cell">{renderNegotiatedField("allergySubstanceReaction", "Substance")}</div>
                          <div className="ncp-health-allergy-cell">{renderNegotiatedField("allergySubstanceReaction", "Reaction")}</div>
                        </div>
                        <div className="ncp-section-bar ncp-page-five-treatment-bar">TREATMENTS/PROGRAMS/THERAPIES</div>
                        <div className="ncp-page-five-treatment-grid ncp-page-five-form-grid">
                          <div className="ncp-comm-head">Treatment/Program<br />Therapy/Interventions</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-treatment-left">
                            <strong>Type of Treatment:</strong>
                            <div className="ncp-route-checklist">
                              {treatmentTypeOptions.map((option) => (
                                <label className={option === "INR/LAB - Health Provider:" || option === "Easily bruised/Anti coagulation therapy" ? "ncp-indent-line" : ""} key={option}>
                                  <input type="checkbox" />
                                  {option}
                                </label>
                              ))}
                            </div>
                            {renderNegotiatedField("treatmentTypes", "Treatment notes")}
                          </div>
                          <div className="ncp-treatment-middle ncp-blue-field">
                            {renderNegotiatedField("treatmentStrengths", "Strengths and Abilities")}
                          </div>
                          <div className="ncp-treatment-right ncp-blue-field">
                            {renderNegotiatedField("treatmentAssistance", "Assistance Required / Caregiver will")}
                          </div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 5 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 6 ? (
                      <div className="ncp-page-six-sheet ncp-page-six-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-section-bar">TREATMENTS/PROGRAMS/THERAPIES</div>
                        <div className="ncp-page-six-grid ncp-page-six-form-grid">
                          <div className="ncp-comm-head">Treatment/Program<br />Therapy/Interventions</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-six-left">
                            <div className="ncp-route-checklist">
                              {treatmentTherapyOptions.map((option) => {
                                const isBlue = option === "Range of Motion" || option === "Other:";
                                const isLabelOnly = option === "Treatments/Therapies - Tasks:";
                                return (
                                  <label key={option}>
                                    {isLabelOnly ? null : <input type="checkbox" />}
                                    <span className={isBlue ? "ncp-blue" : ""}>{option}</span>
                                  </label>
                                );
                              })}
                            </div>
                            {renderNegotiatedField("rangeOfMotionTherapies", "Treatment/therapy notes")}
                          </div>
                          <div className="ncp-page-six-middle" />
                          <div className="ncp-page-six-right" />
                          <div className="ncp-page-six-left">
                            <strong>Programs Resident Requires/Attends:</strong>
                            <div className="ncp-route-checklist">
                              {treatmentProgramOptions.map((option) => (
                                <label className={option === "Hospice Plan" ? "ncp-indent-line" : ""} key={option}>
                                  <input type="checkbox" />
                                  <span className={option === "Home Health" || option === "Hospice - Agency:" ? "ncp-blue" : ""}>{option}</span>
                                </label>
                              ))}
                            </div>
                            {renderNegotiatedField("programsAttended", "Program notes")}
                          </div>
                          <div className="ncp-page-six-middle" />
                          <div className="ncp-page-six-right" />
                          <div className="ncp-page-six-left">
                            <strong>Interventions</strong>
                            <div className="ncp-route-checklist">
                              <label><input type="checkbox" />Meaningful Day</label>
                            </div>
                            {renderNegotiatedField("meaningfulDay", "Intervention notes")}
                          </div>
                          <div className="ncp-page-six-middle" />
                          <div className="ncp-page-six-right" />
                          <div className="ncp-page-six-plan-title">
                            <strong className="ncp-blue">Physical Enablers</strong>
                          </div>
                          <div className="ncp-page-six-plan-middle">
                            {renderNegotiatedField("physicalEnablers", "Strengths and Abilities")}
                          </div>
                          <div className="ncp-page-six-plan-right ncp-blue-field">
                            {renderNegotiatedField("physicalEnablers", "Assistance Required / Caregiver will")}
                          </div>
                          <div className="ncp-page-six-plan-title">
                            <strong>TREATMENT/PROGRAM/THERAPY REFUSAL PLAN</strong>
                          </div>
                          <div className="ncp-page-six-plan-middle">
                            {renderNegotiatedField("treatmentRefusalPlan", "Strengths and Abilities")}
                          </div>
                          <div className="ncp-page-six-plan-right ncp-blue-field">
                            {renderNegotiatedField("treatmentRefusalPlan", "Assistance Required / Caregiver will")}
                          </div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 6 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 7 ? (
                      <div className="ncp-page-seven-sheet ncp-page-seven-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-seven-grid ncp-page-seven-form-grid">
                          <div className="ncp-psych-title">PSYCH/SOCIAL/COGNITIVE STATUS</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          {psychStatusCells.map((cell, cellIndex) => (
                            <div className={cell.className} key={cellIndex}>
                              {cell.lines.map((line, lineIndex) => {
                                if (line.section) return <p className="ncp-psych-section-line" key={lineIndex}>{line.text}</p>;
                                if (line.note) return <p className="ncp-psych-note-line" key={lineIndex}>{line.text}</p>;
                                return (
                                  <label className="ncp-psych-check-line" key={lineIndex}>
                                    {line.checkbox ? <input type="checkbox" /> : null}
                                    {line.blueText ? (
                                      <><span className="ncp-blue">{line.blueText}</span>{line.suffix || ""}</>
                                    ) : (
                                      <><span className={line.blue ? "ncp-blue" : ""}>{line.text}</span>{line.suffix || ""}</>
                                    )}
                                  </label>
                                );
                              })}
                            </div>
                          ))}
                          <div className="ncp-psych-middle">
                            {renderNegotiatedField("sleepStrengths", "Sleep strengths and preferences")}
                            {renderNegotiatedField("memoryStrengths", "Memory strengths and preferences")}
                          </div>
                          <div className="ncp-psych-right">
                            {renderNegotiatedField("sleepAssistance", "Sleep assistance")}
                            {renderNegotiatedField("memoryAssistance", "Memory / behavior assistance")}
                            {renderNegotiatedField("decisionMaking", "Decision making notes")}
                          </div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 7 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 8 ? (
                      <div className="ncp-page-eight-sheet ncp-page-eight-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-eight-grid ncp-page-eight-form-grid">
                          <div className="ncp-psych-title">PSYCH/SOCIAL/COGNITIVE STATUS</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          {psychPageEightBehaviorRows.map((row) => (
                            <label className="ncp-page-eight-behavior-row" key={row}>
                              <input type="checkbox" />
                              {row}
                            </label>
                          ))}
                          <div className="ncp-page-eight-support">
                            {psychPageEightSupportLines.map((line, index) => (
                              <p className={line.spacer ? "ncp-spaced-line" : ""} key={index}>
                                {line.checkbox ? <input type="checkbox" /> : null}
                                {line.blue ? <span className="ncp-blue ncp-doc-link">{line.text}</span> : line.strong ? <strong>{line.text}</strong> : line.text}
                                {line.suffix || ""}
                              </p>
                            ))}
                            {renderNegotiatedField("psychMeds", "Medication / symptoms notes")}
                            {renderNegotiatedField("behavioralHealthSupport", "Behavioral health support notes")}
                          </div>
                          <div className="ncp-page-eight-programs">
                            {specializedBehaviorProgramLines.map((line, index) => (
                              <p className={line.spacer ? "ncp-spaced-line" : ""} key={index}>
                                {line.strong ? <strong>{line.text}</strong> : line.text.includes("Services") || line.text.includes("Provider/Program") || line.text.includes("Meaningful") || line.text.includes("Expanded") ? <><input type="checkbox" defaultChecked={line.checked} />{line.text}</> : line.text}
                              </p>
                            ))}
                            {renderNegotiatedField("specializedBehaviorPrograms", "Program contact info")}
                          </div>
                          <div className="ncp-page-eight-middle-top">{renderNegotiatedField("behaviors", "Behavior strengths / goals")}</div>
                          <div className="ncp-page-eight-right-top">{renderNegotiatedField("behavioralHealthSupport", "Assistance required")}</div>
                          <div className="ncp-page-eight-middle-bottom">{renderNegotiatedField("specializedBehaviorPrograms", "Program strengths / goals")}</div>
                          <div className="ncp-page-eight-right-bottom">{renderNegotiatedField("psychInterventions", "Program assistance")}</div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 8 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 9 ? (
                      <div className="ncp-page-nine-sheet ncp-page-nine-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-nine-psych-grid">
                          <div className="ncp-psych-title">PSYCH/SOCIAL/COGNITIVE STATUS</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-nine-interventions">
                            <strong>Interventions</strong>
                            <label><input type="checkbox" />Meaningful Day</label>
                            <label><input type="checkbox" />Other:</label>
                          </div>
                          <div className="ncp-page-nine-psych-middle">{renderNegotiatedField("psychInterventions", "Intervention strengths / goals")}</div>
                          <div className="ncp-page-nine-psych-right">{renderNegotiatedField("typicalDayNarrative", "Intervention assistance")}</div>
                          <div className="ncp-page-nine-narrative">{renderNegotiatedField("typicalDayNarrative", "Narrative (optional) - What does a typical day look like?")}</div>
                        </div>
                        <div className="ncp-page-nine-left-grid">
                          <div className="ncp-section-bar">Left Alone</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-nine-left-title ncp-blue-field">{renderNegotiatedField("leftAloneAbility", "Ability of Resident to Be Left Unattended")}</div>
                          <div className="ncp-page-nine-left-middle">{renderNegotiatedField("leftAloneStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-nine-left-right ncp-blue-field">{renderNegotiatedField("leftAloneAssistance", "Assistance Required / Caregiver will")}</div>
                        </div>
                        <div className="ncp-page-nine-universal-grid">
                          <div className="ncp-section-bar">UNIVERSAL PRECAUTIONS</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-nine-universal-left">
                            {universalPrecautionLines.map((line) => (
                              <label className={line.nested ? "ncp-indent-line" : ""} key={line.text}>
                                {line.noBox ? null : <input type="checkbox" />}
                                <span className={line.blue ? "ncp-blue" : ""}>{line.text}</span>
                              </label>
                            ))}
                            {renderNegotiatedField("universalPrecautions", "Universal precautions notes")}
                          </div>
                          <div className="ncp-page-nine-universal-middle">{renderNegotiatedField("universalPrecautionsStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-nine-universal-right">{renderNegotiatedField("universalPrecautionsCaregiver", "Caregiver instructions")}{renderNegotiatedField("universalPrecautionsAssistance", "Assistance Required")}</div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 9 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 10 ? (
                      <div className="ncp-page-ten-sheet ncp-page-ten-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-ten-universal-grid">
                          <div className="ncp-section-bar">UNIVERSAL PRECAUTIONS</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-ten-universal-left">
                            {universalPrecautionPageTenLines.map((line) => (
                              <label key={line.text}><input type="checkbox" />{line.text}{line.blueSuffix ? <><br /><span className="ncp-blue">{line.suffix}</span></> : null}</label>
                            ))}
                            {renderNegotiatedField("universalPrecautions", "Vaccinations / equipment notes")}
                          </div>
                          <div className="ncp-page-ten-universal-middle">{renderNegotiatedField("universalPrecautionsStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-ten-universal-right">{renderNegotiatedField("universalPrecautionsAssistance", "Assistance Required")}</div>
                        </div>
                        <div className="ncp-page-ten-adl-grid">
                          <div className="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-ten-functional">{renderNegotiatedField("functionalLimitations", "Resident functional limitations that impact ADL functioning")}</div>
                          <div className="ncp-page-ten-ambulation">
                            {renderNegotiatedField("locomotionRoom", "Locomotion in room and immediate living environment")}
                            {renderNegotiatedField("locomotionOutside", "Locomotion outside of immediate living environment")}
                            {renderNegotiatedField("fallRisks", "Risks for falls / Fall prevention plan")}
                          </div>
                          <div className="ncp-page-ten-adl-middle ncp-blue-field">{renderNegotiatedField("mobilityStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-ten-adl-right ncp-blue-field">{renderNegotiatedField("mobilityAssistance", "Monitoring / Assistance Required")}</div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 10 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 11 ? (
                      <div className="ncp-page-eleven-sheet ncp-page-eleven-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-eleven-adl-grid">
                          <div className="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-eleven-top-left">
                            <div className="ncp-blue-field">{renderNegotiatedField("bedroomDoorLock", "[ ] Resident chooses bedroom door lock")}</div>
                            {renderNegotiatedField("mobilityEquipment", "Equipment/Supplies / Vendor")}
                            {renderNegotiatedField("mobilityLimitations", "Limitations")}
                          </div>
                          <div className="ncp-page-eleven-top-middle" />
                          <div className="ncp-page-eleven-top-right" />
                          <div className="ncp-page-eleven-transfer-left">
                            {renderNegotiatedField("transferLevel", "BED MOBILITY/TRANSFER")}
                            {renderNegotiatedField("skinCarePositioning", "Skin care required / equipment")}
                            {renderNegotiatedField("turningRepositioning", "Turning and repositioning frequency")}
                            {renderNegotiatedField("transferSafetyPlan", "Safety assessment / fall prevention plan")}
                          </div>
                          <div className="ncp-page-eleven-transfer-middle ncp-blue-field">{renderNegotiatedField("transferStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-eleven-transfer-right ncp-blue-field">{renderNegotiatedField("transferAssistance", "Monitoring / Assistance Required")}</div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 11 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 12 ? (
                      <div className="ncp-page-twelve-sheet ncp-page-twelve-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-twelve-adl-grid">
                          <div className="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-twelve-device-left">
                            {renderNegotiatedField("medicalDevices", "Safety plan / Medical Device(s) / Nighttime care needs")}
                            {renderNegotiatedField("transferEquipment", "Equipment/supplies")}
                          </div>
                          <div className="ncp-page-twelve-device-middle" />
                          <div className="ncp-page-twelve-device-right" />
                          <div className="ncp-page-twelve-eating-left">
                            {renderNegotiatedField("eatingLevel", "EATING")}
                            {renderNegotiatedField("specialDiet", "Special Diet / Supplements / Eating Habits / Food Allergies")}
                            {renderNegotiatedField("eatingEquipment", "Equipment / Supplies / Procedures / Limitations")}
                          </div>
                          <div className="ncp-page-twelve-eating-middle ncp-blue-field">{renderNegotiatedField("eatingStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-twelve-eating-right ncp-blue-field">{renderNegotiatedField("eatingAssistance", "Monitoring / Assistance Required")}</div>
                          <div className="ncp-page-twelve-toileting-left">{renderNegotiatedField("toiletingLevel", "TOILETING/CONTINENCE ISSUES")}{renderNegotiatedField("continenceIssues", "Continence details")}</div>
                          <div className="ncp-page-twelve-toileting-middle ncp-blue-field">{renderNegotiatedField("toiletingStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-twelve-toileting-right ncp-blue-field">{renderNegotiatedField("toiletingAssistance", "Monitoring / Assistance Required")}</div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 12 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 13 ? (
                      <div className="ncp-page-thirteen-sheet ncp-page-thirteen-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-thirteen-adl-grid">
                          <div className="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-thirteen-toileting-left">
                            {renderNegotiatedField("toiletingLevel", "Toileting level / frequency")}
                            {renderNegotiatedField("continenceIssues", "Continence Issues / Equipment / Limitations")}
                          </div>
                          <div className="ncp-page-thirteen-toileting-middle ncp-blue-field">{renderNegotiatedField("toiletingStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-thirteen-toileting-right ncp-blue-field">{renderNegotiatedField("toiletingAssistance", "Caregiver will")}</div>
                          <div className="ncp-page-thirteen-dressing-left">{renderNegotiatedField("dressing", "DRESSING")}</div>
                          <div className="ncp-page-thirteen-dressing-middle ncp-blue-field">{renderNegotiatedField("dressing", "Strengths and Abilities")}</div>
                          <div className="ncp-page-thirteen-dressing-right ncp-blue-field">{renderNegotiatedField("dressing", "Monitoring / Assistance Required")}</div>
                          <div className="ncp-page-thirteen-hygiene-left">{renderNegotiatedField("personalHygiene", "PERSONAL HYGIENE")}</div>
                          <div className="ncp-page-thirteen-hygiene-middle ncp-blue-field">{renderNegotiatedField("personalHygiene", "Strengths and Abilities")}</div>
                          <div className="ncp-page-thirteen-hygiene-right ncp-blue-field">{renderNegotiatedField("personalHygiene", "Monitoring / Assistance Required")}</div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 13 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 14 ? (
                      <div className="ncp-page-fourteen-sheet ncp-page-fourteen-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-fourteen-adl-grid">
                          <div className="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-fourteen-hygiene-left">{renderNegotiatedField("personalHygiene", "Oral hygiene / hair care / menses care / equipment / limitations")}</div>
                          <div className="ncp-page-fourteen-hygiene-middle ncp-blue-field">{renderNegotiatedField("personalHygiene", "Strengths and Abilities")}</div>
                          <div className="ncp-page-fourteen-hygiene-right ncp-blue-field">{renderNegotiatedField("personalHygiene", "Assistance Required")}</div>
                          <div className="ncp-page-fourteen-bathing-left">{renderNegotiatedField("bathing", "BATHING")}</div>
                          <div className="ncp-page-fourteen-bathing-middle ncp-blue-field">{renderNegotiatedField("bathing", "Strengths and Abilities")}</div>
                          <div className="ncp-page-fourteen-bathing-right ncp-blue-field">{renderNegotiatedField("bathing", "Monitoring / Assistance Required")}</div>
                          <div className="ncp-page-fourteen-foot-left">{renderNegotiatedField("footCare", "FOOT CARE")}</div>
                          <div className="ncp-page-fourteen-foot-middle ncp-blue-field">{renderNegotiatedField("footCare", "Strengths and Abilities")}</div>
                          <div className="ncp-page-fourteen-foot-right ncp-blue-field">{renderNegotiatedField("footCare", "Monitoring / Assistance Required")}</div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 14 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 15 ? (
                      <div className="ncp-page-fifteen-sheet ncp-page-fifteen-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-fifteen-adl-grid">
                          <div className="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-fifteen-foot-left">{renderNegotiatedField("footCare", "Diabetic foot care / nail care / home health / equipment / limitations")}</div>
                          <div className="ncp-page-fifteen-foot-middle ncp-blue-field">{renderNegotiatedField("footCare", "Strengths and Abilities")}</div>
                          <div className="ncp-page-fifteen-foot-right ncp-blue-field">{renderNegotiatedField("footCare", "Caregiver will")}</div>
                          <div className="ncp-page-fifteen-skin-left">{renderNegotiatedField("skinCare", "SKIN CARE")}</div>
                          <div className="ncp-page-fifteen-skin-middle ncp-blue-field">{renderNegotiatedField("skinCare", "Strengths and Abilities")}</div>
                          <div className="ncp-page-fifteen-skin-right ncp-blue-field">{renderNegotiatedField("skinCare", "Caregiver will")}</div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 15 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 16 ? (
                      <div className="ncp-page-sixteen-sheet ncp-page-sixteen-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-sixteen-iadl-grid">
                          <div className="ncp-section-bar">INSTRUMENTAL ACTIVITIES OF DAILY LIVING</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When And How</div>
                          <div className="ncp-page-sixteen-finances-left">{renderNegotiatedField("finances", "MANAGING FINANCES")}</div>
                          <div className="ncp-page-sixteen-finances-middle ncp-blue-field">{renderNegotiatedField("finances", "Strengths and Abilities")}</div>
                          <div className="ncp-page-sixteen-finances-right ncp-blue-field">{renderNegotiatedField("finances", "Assistance Required")}</div>
                          <div className="ncp-page-sixteen-shopping-left">{renderNegotiatedField("shopping", "SHOPPING")}</div>
                          <div className="ncp-page-sixteen-shopping-middle ncp-blue-field">{renderNegotiatedField("shopping", "Strengths and Abilities")}</div>
                          <div className="ncp-page-sixteen-shopping-right ncp-blue-field">{renderNegotiatedField("shopping", "Assistance Required")}</div>
                          <div className="ncp-page-sixteen-transport-left">{renderNegotiatedField("transportation", "TRANSPORTATION")}</div>
                          <div className="ncp-page-sixteen-transport-middle ncp-blue-field">{renderNegotiatedField("transportation", "Strengths and Abilities")}</div>
                          <div className="ncp-page-sixteen-transport-right ncp-blue-field">{renderNegotiatedField("transportation", "Assistance Required")}</div>
                          <div className="ncp-page-sixteen-social-left">{renderNegotiatedField("activitiesSocial", "ACTIVITIES/SOCIAL")}</div>
                          <div className="ncp-page-sixteen-social-middle ncp-blue-field">{renderNegotiatedField("activitiesSocial", "Strengths and Abilities")}</div>
                          <div className="ncp-page-sixteen-social-right ncp-blue-field">{renderNegotiatedField("activitiesSocial", "Assistance Required")}</div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 16 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 17 ? (
                      <div className="ncp-page-seventeen-sheet ncp-page-seventeen-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-seventeen-grid">
                          <div className="ncp-page-seventeen-social-left">{renderNegotiatedField("activitiesSocial", "Activities / Social details")}</div>
                          <div className="ncp-page-seventeen-social-middle">{renderNegotiatedField("activityPreferences", "Resident strengths and preferences")}</div>
                          <div className="ncp-page-seventeen-social-right ncp-blue-field">{renderNegotiatedField("specialArrangements", "Special arrangements / participation assistance")}</div>
                          <div className="ncp-page-seventeen-preferences-title"><strong>ACTIVITY PREFERENCES AT A GLANCE</strong></div>
                          <div className="ncp-page-seventeen-preferences-left">{renderNegotiatedField("activityPreferences", "Preference list column 1")}</div>
                          <div className="ncp-page-seventeen-preferences-middle">{renderNegotiatedField("activityPreferences", "Preference list column 2")}</div>
                          <div className="ncp-page-seventeen-preferences-right">{renderNegotiatedField("activityPreferences", "Preference list column 3")}</div>
                          <div className="ncp-page-seventeen-narrative">{renderNegotiatedField("activityNarrative", "ACTIVITY NARRATIVE")}</div>
                          <div className="ncp-page-seventeen-smoking-left">{renderNegotiatedField("smoking", "SMOKING")}</div>
                          <div className="ncp-page-seventeen-smoking-middle ncp-blue-field">{renderNegotiatedField("smoking", "Strengths and Abilities")}</div>
                          <div className="ncp-page-seventeen-smoking-right ncp-blue-field">{renderNegotiatedField("smoking", "Assistance Required")}</div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 17 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 18 ? (
                      <div className="ncp-page-eighteen-sheet ncp-page-eighteen-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-eighteen-grid">
                          <div className="ncp-page-eighteen-case-left">{renderNegotiatedField("caseManagement", "CASE MANAGEMENT")}</div>
                          <div className="ncp-page-eighteen-case-middle">{renderNegotiatedField("caseManagement", "Case manager notes")}</div>
                          <div className="ncp-page-eighteen-case-right">{renderNegotiatedField("cmContactedWhen", "CM will be contacted when")}</div>
                          <div className="ncp-page-eighteen-other">{renderNegotiatedField("otherIssues", "OTHER ISSUES/CONCERNS/PROBLEMS")}</div>
                          <div className="ncp-page-eighteen-review-left ncp-blue-field">{renderNegotiatedField("reviewSchedule", "NEGOTIATED CARE PLAN REVIEW")}</div>
                          <div className="ncp-page-eighteen-review-middle">{renderNegotiatedField("reviewSchedule", "Resident participation")}</div>
                          <div className="ncp-page-eighteen-review-right">{renderNegotiatedField("reviewSchedule", "Review/revision schedule")}</div>
                        </div>
                        <div className="ncp-page-eighteen-abbreviations">{renderNegotiatedField("abbreviations", "Abbreviations used in this NCP")}</div>
                        <div className="ncp-page-eighteen-approval">{renderNegotiatedField("reviewDates", "Negotiated Care Plan Review and Approval")}</div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 18 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 19 ? (
                      <div className="ncp-page-nineteen-sheet ncp-page-nineteen-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-nineteen-grid">
                          <div className="ncp-page-nineteen-involved">{renderNegotiatedField("involvedInDevelopment", "INVOLVED IN NCP DEVELOPMENT")}</div>
                          <div className="ncp-page-nineteen-head">PERSON APPROVING PLAN</div>
                          <div className="ncp-page-nineteen-head">SIGNATURE <span>DATE*</span></div>
                          <div className="ncp-page-nineteen-head">SIGNATURE <span>DATE*</span></div>
                          <div className="ncp-page-nineteen-approver">{renderNegotiatedField("approvingPerson", "PROVIDER")}</div>
                          <div className="ncp-page-nineteen-signature">{renderNegotiatedField("providerSignature", "Signature / Date")}</div>
                          <div className="ncp-page-nineteen-signature">{renderNegotiatedField("providerSignature", "Signature / Date")}</div>
                          <div className="ncp-page-nineteen-approver">{renderNegotiatedField("approvingPerson", "RESIDENT")}</div>
                          <div className="ncp-page-nineteen-signature">{renderNegotiatedField("residentSignature", "Signature / Date")}</div>
                          <div className="ncp-page-nineteen-signature">{renderNegotiatedField("residentSignature", "Signature / Date")}</div>
                          <div className="ncp-page-nineteen-approver">{renderNegotiatedField("approvingPerson", "RESIDENT REPRESENTATIVE")}</div>
                          <div className="ncp-page-nineteen-signature">{renderNegotiatedField("residentRepresentativeSignature", "Signature / Date")}</div>
                          <div className="ncp-page-nineteen-signature">{renderNegotiatedField("residentRepresentativeSignature", "Signature / Date")}</div>
                          <div className="ncp-page-nineteen-verbal">{renderNegotiatedField("residentVerbalAgreement", "Resident verbally agreed to NCP - Date")}</div>
                          <div className="ncp-page-nineteen-sent">{renderNegotiatedField("sentToDshs", "NCP sent to DSHS CM on")}</div>
                          <div className="ncp-page-nineteen-recommendations">{renderNegotiatedField("residentRecommendations", "Resident Recommendations")}</div>
                        </div>
                        <div className="ncp-page-nineteen-note">{renderNegotiatedField("signatureNote", "Signature note")}</div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 19 of 19</strong>
                        </div>
                      </div>
                    ) : page.sections.map((section) => (
                      <article className="negotiated-template-section" key={section.title}>
                        <div className="negotiated-template-section-head">
                          <h4>{section.title}</h4>
                          <small>{section.fields.length} template field{section.fields.length === 1 ? "" : "s"}</small>
                        </div>
                        <div className="negotiated-template-field-grid">
                          {section.fields.map((field) => (
                            <label className="negotiated-template-field" key={field.key}>
                              <span>{field.label}</span>
                              <textarea
                                aria-label={field.label}
                                value={field.value}
                                onChange={(event) => updateNegotiatedTemplateField(field.key, event.target.value)}
                              />
                            </label>
                          ))}
                        </div>
                      </article>
                    ))}
                  </div>
                ))}
              </section>
            </div>

            <aside className="preliminary-plan-side">
              <section className="assessment-drawer-section">
                <h3>Plan Metadata</h3>
                <div className="assessment-detail-grid">
                  <div><span>Status</span><strong><em className="pill blue">{record.status}</em></strong></div>
                  <div><span>Template</span><strong>{record.templateName}</strong></div>
                  <div><span>Latest generated at</span><strong>{formatDateTime(record.latestGeneratedAt)}</strong></div>
                  <div><span>Latest generated by</span><strong>{record.latestGeneratedBy}</strong></div>
                  <div><span>Last updated at</span><strong>{formatDateTime(record.lastUpdatedAt)}</strong></div>
                  <div><span>Last updated by</span><strong>{record.lastUpdatedBy}</strong></div>
                </div>
              </section>
              <section className="assessment-drawer-section">
                <h3>Data Sources</h3>
                <div className="preliminary-source-list">
                  {(record.dataSources || []).map((source) => (
                    <span key={source}><i className="ti ti-database" /> {source}</span>
                  ))}
                </div>
              </section>
              <section className="assessment-drawer-section">
                <h3>Attached Files</h3>
                <div className="assessment-uploaded-doc-list">
                  {(record.attachedFiles || []).map((file) => (
                    <span key={file.name}>
                      <span className="assessment-attachment-thumb document"><i className="ti ti-file-description" /></span>
                      <strong>{file.name}<small>{file.caption}</small></strong>
                      <span className="assessment-document-actions">
                        <button aria-label={`Preview ${file.name}`} type="button"><i className="ti ti-eye" /></button>
                        <button aria-label={`Download ${file.name}`} type="button"><i className="ti ti-download" /></button>
                      </span>
                    </span>
                  ))}
                </div>
              </section>
            </aside>
          </div>
        </>
      ) : record && isPreliminaryPlan ? (
        <>
          <div className="assessment-drawer-action-links assessment-page-action-links probation-action-buttons preliminary-plan-actions">
            <button className="primary-action" type="button" disabled={generationLocked}><i className="ti ti-sparkles" /> Generate Draft</button>
            <button className="danger-outline" type="button" onClick={() => setPreliminaryGenerationLocked(true)}><i className="ti ti-lock" /> Prevent Further Generation</button>
            <button type="button" onClick={() => setPreliminaryPreviewOpen(true)}><i className="ti ti-eye" /> Preview</button>
            <button type="button" onClick={printPreliminaryPlan}><i className="ti ti-printer" /> Print</button>
            <button className="success" type="button" onClick={confirmMarkCompleted}><i className="ti ti-circle-check" /> Mark as Completed</button>
          </div>

          <div className="preliminary-plan-grid">
            <div className="preliminary-plan-main">
              <section className="assessment-drawer-section">
                <div className="assessment-section-heading-row">
                  <h3>Description</h3>
                  <span>{generationLocked ? "Generation locked" : "Generation available"}</span>
                </div>
                <div className="preliminary-ai-actions">
                  <button type="button" onClick={refinePreliminaryPlan}><i className="ti ti-wand" /> Refine</button>
                  <button type="button" onClick={summarizePreliminaryPlan}><i className="ti ti-list-details" /> Summarize</button>
                  <button type="button" onClick={formatPreliminaryPlan}><i className="ti ti-brush" /> Format</button>
                  <button type="button" disabled={!preliminaryPlanHistory.length} onClick={undoPreliminaryPlanAction}><i className="ti ti-arrow-back-up" /> Undo</button>
                </div>
                <CareRichTextEditor autoGrow height={420} onChange={setPreliminaryPlanDescription} value={planDescription} />
                <input name="preliminaryPlanDescription" readOnly type="hidden" value={planDescription} />
              </section>

              <section className="assessment-drawer-section">
                <h3>Data Sources</h3>
                <div className="preliminary-source-list">
                  {(record.dataSources || []).map((source) => (
                    <span key={source}><i className="ti ti-database" /> {source}</span>
                  ))}
                </div>
              </section>
            </div>

            <aside className="preliminary-plan-side">
              <section className="assessment-drawer-section">
                <h3>Plan Metadata</h3>
                <div className="assessment-detail-grid">
                  <div><span>Status</span><strong><em className="pill blue">{record.status}</em></strong></div>
                  {record.templateName ? <div><span>Template</span><strong>{record.templateName}</strong></div> : null}
                  <div><span>Latest generated at</span><strong>{formatDateTime(record.latestGeneratedAt)}</strong></div>
                  <div><span>Latest generated by</span><strong>{record.latestGeneratedBy}</strong></div>
                  <div><span>Created at</span><strong>{formatDateTime(record.createdAt)}</strong></div>
                  <div><span>Created by</span><strong>{record.createdBy}</strong></div>
                  <div><span>Last updated at</span><strong>{formatDateTime(record.lastUpdatedAt)}</strong></div>
                  <div><span>Last updated by</span><strong>{record.lastUpdatedBy}</strong></div>
                </div>
              </section>

              <section className="assessment-drawer-section">
                <div className="assessment-section-heading-row">
                  <h3>Attached Files</h3>
                  <label className="preliminary-upload-button">
                    <input className="assessment-file-input" multiple onChange={addPreliminaryAttachedFiles} type="file" />
                    <i className="ti ti-file-plus" /> Add files
                  </label>
                </div>
                <div className="assessment-uploaded-doc-list">
                  {(record.attachedFiles || []).map((file) => (
                    <span key={file.name}>
                      <span className={`assessment-attachment-thumb ${file.type === "photo" ? "photo" : "document"}`}>
                        <i className={`ti ${file.type === "photo" ? "ti-photo" : "ti-file-description"}`} />
                      </span>
                      <strong>{file.name}<small>{file.caption}</small></strong>
                      <span className="assessment-document-actions">
                        <button aria-label={`Preview ${file.name}`} type="button"><i className="ti ti-eye" /></button>
                        <button aria-label={`Download ${file.name}`} type="button"><i className="ti ti-download" /></button>
                      </span>
                    </span>
                  ))}
                  {preliminaryAttachedFiles.map((file, index) => (
                    <div className="assessment-uploaded-doc-row preliminary-uploaded-file-row" key={`${file.name}-${index}`}>
                      <span className={`assessment-attachment-thumb ${file.type === "photo" ? "photo" : "document"}`}>
                        {file.previewUrl ? <img alt="" src={file.previewUrl} /> : <i className={`ti ${file.type === "photo" ? "ti-photo" : "ti-file-description"}`} />}
                      </span>
                      <div className="preliminary-uploaded-file-fields">
                        <label>
                          <span>File name</span>
                          <input
                            onChange={(event) => updatePreliminaryAttachedFile(index, "name", event.target.value)}
                            type="text"
                            value={file.name}
                          />
                        </label>
                        <label>
                          <span>Description</span>
                          <input
                            onChange={(event) => updatePreliminaryAttachedFile(index, "caption", event.target.value)}
                            type="text"
                            value={file.caption}
                          />
                        </label>
                        <small className="preliminary-file-added-at">
                          <i className="ti ti-clock" /> Added at {formatDateTime(file.addedAt)}
                        </small>
                      </div>
                      <button aria-label={`Remove ${file.name}`} type="button" onClick={() => removePreliminaryAttachedFile(index)}>
                        <i className="ti ti-x" />
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            </aside>
          </div>
        </>
      ) : record && (isResidentProbation || isAnnualResidencyReview) ? (
        <>
          <div className="assessment-drawer-action-links assessment-page-action-links probation-action-buttons">
            <button className="primary-action" type="button" onClick={openAddDailyNoteDrawer}><i className="ti ti-note" /> Add Daily Note</button>
            <button type="button"><i className="ti ti-edit" /> Edit</button>
            <button className="success" type="button" onClick={confirmMarkCompleted}><i className="ti ti-circle-check" /> Mark as Completed</button>
          </div>

          <div className="probation-page-grid">
            <div className="probation-main-column">
              <section className="assessment-drawer-section">
                <h3>{isAnnualResidencyReview ? "Annual Progress Period" : "Probation Period"}</h3>
                <div className="probation-summary-grid">
                  <div><span>Start date</span><strong>{formatDate(record.startDate)}</strong></div>
                  <div><span>End date</span><strong>{formatDate(record.endDate)}</strong></div>
                  <div><span>Status</span><strong><em className="pill blue">{record.status}</em></strong></div>
                </div>
                <p className="probation-description">{record.description}</p>
                {isAnnualResidencyReview ? (
                  <div className="annual-review-evaluation">
                    <h4>Overall Evaluation / Status Notes</h4>
                    <div className="assessment-record-notes" dangerouslySetInnerHTML={richTextMarkup(record.evaluationNotes)} />
                  </div>
                ) : null}
              </section>

              <section className="assessment-drawer-section">
                <div className="assessment-section-heading-row">
                  <h3>Daily Notes</h3>
                  <button className="mini-btn" type="button" onClick={openAddDailyNoteDrawer}><i className="ti ti-plus" /> Add daily note</button>
                </div>
                <div className="probation-note-toolbar">
                  <label className="ncp-form-field">
                    <span>From</span>
                    <input
                      max={probationNoteRange.end}
                      onChange={(event) => {
                        setProbationNoteRange((current) => ({ ...current, start: event.target.value }));
                        setVisibleProbationNoteCount(10);
                      }}
                      type="date"
                      value={probationNoteRange.start}
                    />
                  </label>
                  <label className="ncp-form-field">
                    <span>To</span>
                    <input
                      min={probationNoteRange.start}
                      onChange={(event) => {
                        setProbationNoteRange((current) => ({ ...current, end: event.target.value }));
                        setVisibleProbationNoteCount(10);
                      }}
                      type="date"
                      value={probationNoteRange.end}
                    />
                  </label>
                </div>
                <div className="probation-note-list">
                  {visibleProbationNotes.length ? visibleProbationNotes.map((note) => (
                    <button className="probation-note-card" type="button" onClick={() => setSelectedDailyNote(note)} key={`${note.title}-${note.date}`}>
                      <div className="probation-note-card-head">
                        <div>
                          <strong>{note.title}</strong>
                          <span>Note date: {formatDate(note.noteDate || dateInputValue(note.date))}</span>
                        </div>
                        <em>{note.category}</em>
                      </div>
                      <div className="probation-note-card-meta">
                        <span><i className="ti ti-user-plus" /> Added by {note.addedBy}</span>
                        <span><i className="ti ti-clock" /> {formatDateTime(note.date)}</span>
                        <span><i className="ti ti-edit" /> Updated by {note.updatedBy || note.addedBy}</span>
                        <span><i className="ti ti-refresh" /> {formatDateTime(note.updatedAt || note.date)}</span>
                        {note.photos?.length ? <span><i className="ti ti-photo" /> {note.photos.length} photo{note.photos.length === 1 ? "" : "s"}</span> : null}
                      </div>
                      <p>{note.summary}</p>
                    </button>
                  )) : (
                    <p className="probation-empty">No daily notes were found for the selected date range.</p>
                  )}
                  {probationNotes.length > visibleProbationNoteCount ? (
                    <button className="probation-view-more" type="button" onClick={() => setVisibleProbationNoteCount((current) => current + 10)}>
                      View more notes ({probationNotes.length - visibleProbationNoteCount} remaining)
                    </button>
                  ) : null}
                </div>
              </section>
            </div>

            <aside className="assessment-drawer-section probation-staff-panel">
              <div className="assessment-section-heading-row">
                <h3>Assigned Care Provider Staff</h3>
                <button className="mini-btn" type="button" onClick={addProbationStaff}><i className="ti ti-plus" /> Add staff</button>
              </div>
              <div className="probation-staff-list">
                {assignedProbationStaff.map((staff, index) => (
                  <article className="probation-staff-edit-card" key={`${staff.name}-${staff.from}-${index}`}>
                    <span className="tiny-icon"><i className="ti ti-user-heart" /></span>
                    <div className="probation-staff-readonly">
                      <div className="probation-staff-title-row">
                        <strong>{staff.name || "Unnamed staff member"}</strong>
                        <small>{staff.role || "Role not set"}</small>
                      </div>
                      <div className="probation-staff-time-list">
                        <p><i className="ti ti-clock-up" /> <span>From</span> {staff.from ? formatDateTime(staff.from) : "Not set"}</p>
                        <p><i className="ti ti-clock-down" /> <span>To</span> {staff.to ? formatDateTime(staff.to) : "Not set"}</p>
                      </div>
                    </div>
                    <div className="probation-staff-row-actions">
                      <button aria-label={`Edit ${staff.name || "staff assignment"}`} type="button" onClick={() => editProbationStaff(staff)}><i className="ti ti-edit" /></button>
                      <button aria-label={`Remove ${staff.name || "staff assignment"}`} type="button" onClick={() => removeProbationStaff(staff.originalIndex)}><i className="ti ti-trash" /></button>
                    </div>
                  </article>
                ))}
              </div>
            </aside>
          </div>
        </>
      ) : record && isCareMeetingRecord ? (
        <>
          <div className="assessment-drawer-action-links assessment-page-action-links">
            <Link to={`/assessments/${assessment.id}/records/${recordId}/edit`}><i className="ti ti-edit" /> Edit</Link>
            <button className="success" type="button" onClick={confirmMarkCompleted}><i className="ti ti-circle-check" /> Mark as Completed</button>
            <button className="warning" type="button"><i className="ti ti-player-skip-forward" /> Skip</button>
          </div>
          <div className="meet-greet-detail-grid">
            <div className="meet-greet-main-column">
              <section className="assessment-drawer-section">
                <h3>{isClientOnboarding ? "Onboarding Details" : "Meeting Details"}</h3>
                <div className="assessment-detail-grid">
                  <div><span>{isClientOnboarding ? "Onboarding date" : "Date"}</span><strong>{formatDate(record.meetingDate)}</strong></div>
                <div><span>Location</span><strong>{record.location}</strong></div>
                <div><span>Social worker</span><strong>{record.socialWorker}</strong></div>
                <div><span>Facilitator</span><strong>{record.facilitator}</strong></div>
                <div className="wide"><span>Present care provider staff</span><strong>{record.careProviderStaff.join(", ")}</strong></div>
              </div>
            </section>
              <section className="assessment-drawer-section">
                <h3>Care Circle</h3>
                <div className="assessment-signed-document-list">
                  {(record.careCircle || []).map((member) => (
                    <span key={member}><i className="ti ti-users" /> {member}</span>
                  ))}
                </div>
              </section>
              <section className="assessment-drawer-section">
                <h3>Questions and Responses</h3>
                <div className="assessment-question-list">
                  {record.questions.map(([question, answer]) => (
                    <article key={question}>
                      <strong>{question}</strong>
                      <p>{answer}</p>
                    </article>
                  ))}
                </div>
              </section>
            </div>
            <div className="assessment-mag-notes-column">
              <section className="assessment-drawer-section">
                <h3>Notes</h3>
                <div className="assessment-record-notes" dangerouslySetInnerHTML={richTextMarkup(record.notes)} />
              </section>
              <section className="assessment-drawer-section">
                <h3>Signed Documents</h3>
                <div className="assessment-signed-document-list">
                  {(record.signedDocuments || []).length ? (
                    (record.signedDocuments || []).map((document) => (
                      <span key={document}>
                        <i className="ti ti-circle-check" />
                        <strong>{document}</strong>
                        <span className="assessment-document-actions">
                          <button aria-label={`Preview ${document}`} type="button"><i className="ti ti-eye" /></button>
                          <button aria-label={`Download ${document}`} type="button"><i className="ti ti-download" /></button>
                        </span>
                      </span>
                    ))
                  ) : (
                    <span><i className="ti ti-file-alert" /> No signed documents recorded yet</span>
                  )}
                </div>
              </section>
              <section className="assessment-drawer-section">
                <h3>Uploaded Documents</h3>
                <div className="assessment-uploaded-doc-list">
                  {(record.uploadedDocuments || []).map((document) => (
                    <span key={document}>
                      <span className="assessment-attachment-thumb document"><i className="ti ti-file-description" /></span>
                      <strong>{document}</strong>
                      <span className="assessment-document-actions">
                        <button aria-label={`View ${document}`} type="button"><i className="ti ti-eye" /></button>
                        <button aria-label={`Download ${document}`} type="button"><i className="ti ti-download" /></button>
                      </span>
                    </span>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </>
      ) : record ? (
        <>
          <div className="assessment-drawer-action-links assessment-page-action-links">
            <Link to={`/assessments/${assessment.id}/records/${recordId}/edit`}><i className="ti ti-edit" /> Edit</Link>
            <button className="danger" type="button"><i className="ti ti-trash" /> Delete</button>
            {canPreviewDocument ? <button type="button" onClick={() => setPreviewOpen(true)}><i className="ti ti-eye" /> Preview</button> : null}
            <button className="success" type="button"><i className="ti ti-download" /> Download</button>
          </div>
          <div className="assessment-detail-page-grid">
            <section className="assessment-drawer-section">
              <h3>Document</h3>
              <div className="assessment-detail-grid">
                <div className="wide">
                  <span>Document</span>
                  <strong className="assessment-document-title">
                    <i className="ti ti-file-description" /> {record.document}
                    {canPreviewDocument ? (
                      <button type="button" onClick={() => setPreviewOpen(true)}>
                        <i className="ti ti-eye" /> Preview
                      </button>
                    ) : null}
                  </strong>
                </div>
                <div><span>Date uploaded</span><strong>{formatDate(record.dateUploaded)}</strong></div>
                <div><span>Uploaded by</span><strong>{record.uploadedBy}</strong></div>
                <div><span>Social worker</span><strong>{record.socialWorker}</strong></div>
                <div><span>Issuer</span><strong>{record.issuer}</strong></div>
              </div>
            </section>
            <section className="assessment-drawer-section">
              <h3>Notes</h3>
              <div className="assessment-record-notes" dangerouslySetInnerHTML={richTextMarkup(record.notes)} />
            </section>
          </div>
        </>
      ) : isCareMeetingRecord ? (
        <Panel title={`Add ${recordName}`}>
          <form className="assessment-page-form assessment-edit-form">
            <div className="assessment-mag-edit-grid">
              <div className="assessment-mag-edit-main">
                <section className="assessment-drawer-section">
                  <h3>{isClientOnboarding ? "Onboarding Details" : "Meeting Details"}</h3>
                  <div className="assessment-drawer-grid">
                    <label className="ncp-form-field">
                      <span>{isClientOnboarding ? "Onboarding date" : "Meeting date"}</span>
                      <input name="meetingDate" type="date" />
                    </label>
                    <label className="ncp-form-field">
                      <span>Location</span>
                      <input name="location" placeholder="Hospital, facility, home, or virtual meeting location" />
                    </label>
                    <label className="ncp-form-field">
                      <span>Social worker</span>
                      <input name="socialWorker" defaultValue={assessment.socialWorker} />
                    </label>
                    <label className="ncp-form-field">
                      <span>Facilitator</span>
                      <input name="facilitator" />
                    </label>
                    <div className="ncp-form-field-wide">
                      <span className="assessment-inline-label">Present care provider staff members</span>
                      <div className="assessment-care-circle-form-list">
                        {careProviderStaffMembers.map(([name, role], index) => (
                          <div className="assessment-care-circle-row" key={`staff-${index}`}>
                            <label className="ncp-form-field">
                              <span>Person name</span>
                              <input defaultValue={name} name={`staffName-${index}`} placeholder="Full name" />
                            </label>
                            <label className="ncp-form-field">
                              <span>Role</span>
                              <input defaultValue={role} name={`staffRole-${index}`} placeholder="Role present" />
                            </label>
                            <button className="assessment-remove-row" type="button" onClick={() => removeCareProviderStaffMember(index)}>
                              <i className="ti ti-x" /> Remove
                            </button>
                          </div>
                        ))}
                      </div>
                      <button className="mini-btn assessment-add-row" type="button" onClick={addCareProviderStaffMember}>
                        <i className="ti ti-plus" /> Add staff member
                      </button>
                    </div>
                  </div>
                </section>
                <section className="assessment-drawer-section">
                  <h3>Care Circle</h3>
                  <div className="assessment-care-circle-form-list">
                    {careCircleMembers.map(([name, relationship], index) => (
                      <div className="assessment-care-circle-row" key={`${name}-${relationship}-${index}`}>
                        <label className="ncp-form-field">
                          <span>Person name</span>
                          <input defaultValue={name} name={`careCircleName-${index}`} placeholder="Full name" />
                        </label>
                        <label className="ncp-form-field">
                          <span>Relationship</span>
                          <input defaultValue={relationship} name={`careCircleRelationship-${index}`} placeholder="Relationship to resident" />
                        </label>
                        <button className="assessment-remove-row" type="button" onClick={() => removeCareCircleMember(index)}>
                          <i className="ti ti-x" /> Remove
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="assessment-custom-question">
                    <button className="mini-btn" type="button" onClick={addCustomCareCircleMember}><i className="ti ti-plus" /> Add member</button>
                  </div>
                </section>
                <section className="assessment-drawer-section">
                  <div className="assessment-section-heading-row">
                    <h3>Questions and Responses</h3>
                    <span>{meetAndGreetQuestions.length + customQuestions.length} questions</span>
                  </div>
                  <div className="assessment-question-form-list">
                    {[...meetAndGreetQuestions, ...customQuestions].map((question) => (
                      <label className="ncp-form-field ncp-form-field-wide" key={question}>
                        <span>{question}</span>
                        <textarea name={`question-${question}`} rows={2} />
                      </label>
                    ))}
                  </div>
                  <div className="assessment-custom-question">
                    <input value={customQuestion} placeholder="Add custom question" onChange={(event) => setCustomQuestion(event.target.value)} />
                    <button className="mini-btn" type="button" onClick={addCustomQuestion}><i className="ti ti-plus" /> Add question</button>
                  </div>
                </section>
              </div>
              <aside className="assessment-mag-notes-column assessment-mag-notes-panel">
                <section className="assessment-drawer-section">
                  <h3>Notes</h3>
                  <CareRichTextEditor height={360} onChange={setMeetNotes} value={meetNotes} />
                  <input name="notes" readOnly type="hidden" value={meetNotes} />
                </section>
                <section className="assessment-drawer-section">
                  <h3>Signed Documents</h3>
                  <div className="assessment-document-checklist">
                    {[...signedDocumentOptions, ...customDocuments].map((document) => (
                      <label key={document}>
                        <input name={`document-${document}`} type="checkbox" />
                        <span>{document}</span>
                      </label>
                    ))}
                  </div>
                  <div className="assessment-custom-question">
                    <input value={customDocument} placeholder="Add custom signed document" onChange={(event) => setCustomDocument(event.target.value)} />
                    <button className="mini-btn" type="button" onClick={addCustomDocument}><i className="ti ti-plus" /> Add document</button>
                  </div>
                </section>
                <section className="assessment-drawer-section">
                  <h3>Upload Documents</h3>
                  <label className="assessment-upload assessment-upload-compact">
                    <input className="assessment-file-input" multiple name="uploadedDocuments" onChange={addUploadedDocuments} type="file" />
                    <i className="ti ti-file-upload" />
                    <strong>Attach documents</strong>
                    <small>Upload PDF, Word, images, or care files related to this record.</small>
                  </label>
                  {uploadedDocuments.length ? (
                    <div className="assessment-uploaded-doc-list">
                      {uploadedDocuments.map((document, index) => (
                        <div className="assessment-uploaded-doc-row" key={`${document.fileName}-${index}`}>
                          <span className="assessment-attachment-thumb document"><i className="ti ti-file-description" /></span>
                          <div>
                            <strong>{document.fileName}</strong>
                            <label className="ncp-form-field">
                              <span>Document name</span>
                              <input
                                name={`uploadedDocumentName-${index}`}
                                onChange={(event) => updateUploadedDocumentName(index, event.target.value)}
                                placeholder="Document name"
                                value={document.name}
                              />
                            </label>
                          </div>
                          <button type="button" onClick={() => removeUploadedDocument(index)}><i className="ti ti-x" /></button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </section>
              </aside>
            </div>
            <div className="assessment-drawer-actions">
              <Link className="btn secondary" to={`/assessments/${assessment.id}`}>Cancel</Link>
              <button className="btn primary" type="button">Save {recordName}</button>
            </div>
          </form>
        </Panel>
      ) : (
        <Panel title={`Add ${recordName}`}>
          <form className="assessment-page-form">
            <section className="assessment-drawer-section">
              <h3>Document</h3>
              <div className="assessment-drawer-grid">
                <label className="ncp-form-field ncp-form-field-wide">
                  <span>Document</span>
                  <input name="document" type="file" />
                </label>
                <label className="ncp-form-field">
                  <span>Date uploaded</span>
                  <input name="dateUploaded" type="date" />
                </label>
                <label className="ncp-form-field">
                  <span>Uploaded by</span>
                  <input name="uploadedBy" />
                </label>
                <label className="ncp-form-field">
                  <span>Social worker</span>
                  <input name="socialWorker" defaultValue={assessment.socialWorker} />
                </label>
                <label className="ncp-form-field">
                  <span>Issuer</span>
                  <input name="issuer" placeholder="State, agency, provider, or organization" />
                </label>
                <div className="ncp-form-field-wide">
                  <CareRichTextEditor label="Notes" onChange={setRecordNotes} value={recordNotes} />
                  <input name="notes" readOnly type="hidden" value={recordNotes} />
                </div>
              </div>
            </section>
            <div className="assessment-drawer-actions">
              <Link className="btn secondary" to={`/assessments/${assessment.id}`}>Cancel</Link>
              <button className="btn primary" type="button">Save record</button>
            </div>
          </form>
        </Panel>
      )}
      <Modal
        className="assessment-document-preview-modal"
        footer={null}
        onCancel={() => setPreviewOpen(false)}
        open={previewOpen}
        title={`${recordName} Preview`}
        width={920}
      >
        {record ? (
          <div className="assessment-document-preview-window">
            <div className="assessment-document-preview-toolbar">
              <div>
                <span>{record.document}</span>
                <strong>{processResidentName(assessment)}</strong>
              </div>
              <button className="mini-btn" type="button"><i className="ti ti-download" /> Download</button>
            </div>
            <div className="assessment-document-page">
              <div className="assessment-document-page-head">
                <strong>State Initial Assessment</strong>
                <span>{formatDate(record.dateUploaded)}</span>
              </div>
              <div className="assessment-document-lines">
                <b>Resident</b><p>{processResidentName(assessment)}</p>
                <b>Social worker</b><p>{record.socialWorker}</p>
                <b>Issuer</b><p>{record.issuer}</p>
                <b>Assessment summary</b>
                <div className="assessment-record-notes" dangerouslySetInnerHTML={richTextMarkup(record.notes)} />
              </div>
            </div>
          </div>
        ) : null}
      </Modal>
      <Modal
        className="assessment-document-preview-modal"
        footer={null}
        onCancel={() => setPreliminaryPreviewOpen(false)}
        open={preliminaryPreviewOpen}
        title={`${recordName} Preview`}
        width={920}
      >
        <div className="assessment-document-preview-window preliminary-plan-preview-window">
          <div className="assessment-document-preview-toolbar">
            <div>
              <span>{assessment.name || assessment.title || "Assessment process"}</span>
              <strong>{processResidentName(assessment)}</strong>
            </div>
            <button className="mini-btn" type="button" onClick={printPreliminaryPlan}><i className="ti ti-printer" /> Print</button>
          </div>
          <div className="assessment-document-page">
            <div className="assessment-document-page-head">
              <strong>{recordName}</strong>
              <span>{formatDate(new Date())}</span>
            </div>
            <div className="assessment-record-notes preliminary-plan-preview-content" dangerouslySetInnerHTML={richTextMarkup(planDescription)} />
          </div>
        </div>
      </Modal>
      <Modal
        className="assessment-document-preview-modal"
        footer={null}
        onCancel={() => setNegotiatedPreviewOpen(false)}
        open={negotiatedPreviewOpen}
        title={`${recordName} Preview`}
        width={1180}
      >
        <div className="assessment-document-preview-window negotiated-preview-window">
          <div className="assessment-document-preview-toolbar">
            <div>
              <span>{record?.templateName}</span>
              <strong>{processResidentName(assessment)}</strong>
            </div>
            <button className="mini-btn" type="button" onClick={printNegotiatedPlan}><i className="ti ti-printer" /> Print</button>
          </div>
          <div className="assessment-document-page negotiated-preview-page">
            <p className="ncp-document-note">NOTE: Place an X in the bracket [] - [x] - to indicate/select your choice. Form Version: {record?.formVersion}</p>
            <div className="assessment-record-notes preliminary-plan-preview-content negotiated-template-preview-content" dangerouslySetInnerHTML={richTextMarkup(negotiatedTemplateDocumentHtml)} />
          </div>
        </div>
      </Modal>
      <Drawer
        title={dailyNoteEditingNote ? "Edit Daily Note" : "Add Daily Note"}
        placement="right"
        open={dailyNoteDrawerOpen}
        onClose={() => {
          setDailyNoteDrawerOpen(false);
          setDailyNoteEditingNote(null);
        }}
        width={640}
        className="resident-action-drawer daily-note-drawer"
      >
        <form className="assessment-drawer-form">
          <section className="assessment-drawer-section">
            <h3>Note Details</h3>
            <div className="assessment-drawer-grid">
              <label className="ncp-form-field">
                <span>Note date</span>
                <input name="noteDate" type="date" value={dailyNoteDate} onChange={(event) => setDailyNoteDate(event.target.value)} />
              </label>
              <label className="ncp-form-field">
                <span>Category</span>
                <select name="category" value={dailyNoteCategory} onChange={(event) => setDailyNoteCategory(event.target.value)}>
                  {dailyNoteCategories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </label>
              {dailyNoteCategory === "Other" ? (
                <label className="ncp-form-field ncp-form-field-wide">
                  <span>Specify category</span>
                  <input name="otherCategory" placeholder="Enter note category" value={dailyNoteOtherCategory} onChange={(event) => setDailyNoteOtherCategory(event.target.value)} />
                </label>
              ) : null}
            </div>
          </section>

          <section className="assessment-drawer-section">
            <h3>Notes</h3>
            <CareRichTextEditor height={260} onChange={setDailyNoteRichText} value={dailyNoteRichText} />
            <input name="noteBody" readOnly type="hidden" value={dailyNoteRichText} />
          </section>

          <section className="assessment-drawer-section">
            <h3>Attach Photo</h3>
            <label className="assessment-upload assessment-upload-compact">
              <input accept="image/*" className="assessment-file-input" multiple name="photos" onChange={addDailyNotePhotos} type="file" />
              <i className="ti ti-photo-plus" />
              <strong>Attach photos</strong>
              <small>Add one or more photos related to this daily note.</small>
            </label>
            {dailyNotePhotos.length ? (
              <div className="assessment-uploaded-doc-list">
                {dailyNotePhotos.map((photo, index) => (
                  <div className="assessment-uploaded-doc-row" key={`${photo.fileName}-${index}`}>
                    <span className="assessment-attachment-thumb photo">
                      {photo.previewUrl ? <img alt="" src={photo.previewUrl} /> : <i className="ti ti-photo" />}
                    </span>
                    <div>
                      <strong>{photo.fileName}</strong>
                      <label className="ncp-form-field">
                        <span>Photo description</span>
                        <input
                          name={`dailyNotePhotoDescription-${index}`}
                          onChange={(event) => updateDailyNotePhotoDescription(index, event.target.value)}
                          placeholder="Photo description"
                          value={photo.description}
                        />
                      </label>
                    </div>
                    <button type="button" onClick={() => removeDailyNotePhoto(index)}><i className="ti ti-x" /></button>
                  </div>
                ))}
              </div>
            ) : null}
          </section>

          <div className="assessment-drawer-actions">
            <button className="btn secondary" type="button" onClick={() => {
              setDailyNoteDrawerOpen(false);
              setDailyNoteEditingNote(null);
            }}>Cancel</button>
            <button className="btn primary" type="button">{dailyNoteEditingNote ? "Save Changes" : "Save Daily Note"}</button>
          </div>
        </form>
      </Drawer>
      <Drawer
        title="Daily Note Details"
        placement="right"
        open={Boolean(selectedDailyNote)}
        onClose={() => setSelectedDailyNote(null)}
        width={560}
        className="resident-action-drawer daily-note-drawer"
      >
        {selectedDailyNote ? (
          <div className="resident-drawer-content">
            <div className="assessment-drawer-action-links">
              <button type="button" onClick={() => openEditDailyNoteDrawer(selectedDailyNote)}><i className="ti ti-edit" /> Edit</button>
              <button className="danger" type="button"><i className="ti ti-trash" /> Delete</button>
            </div>
            <section className="assessment-drawer-section">
              <h3>Note Details</h3>
              <div className="assessment-detail-grid">
                <div className="wide"><span>Title</span><strong>{selectedDailyNote.title}</strong></div>
                <div><span>Category</span><strong>{selectedDailyNote.category}</strong></div>
                <div><span>Note date</span><strong>{formatDate(selectedDailyNote.noteDate || dateInputValue(selectedDailyNote.date))}</strong></div>
                <div className="wide audit-pair"><span>Added</span><strong>{selectedDailyNote.addedBy} at {formatDateTime(selectedDailyNote.date)}</strong></div>
                <div className="wide audit-pair"><span>Updated</span><strong>{selectedDailyNote.updatedBy || selectedDailyNote.addedBy} at {formatDateTime(selectedDailyNote.updatedAt || selectedDailyNote.date)}</strong></div>
              </div>
            </section>
            <section className="assessment-drawer-section">
              <h3>Notes</h3>
              <div className="assessment-record-notes" dangerouslySetInnerHTML={richTextMarkup(selectedDailyNote.notes || `<p>${selectedDailyNote.summary || ""}</p>`)} />
            </section>
            <section className="assessment-drawer-section">
              <h3>Attached Photos</h3>
              {selectedDailyNote.photos?.length ? (
                <div className="assessment-uploaded-doc-list">
                  {selectedDailyNote.photos.map((photo, index) => (
                    <div className="assessment-uploaded-doc-row" key={`${photo.fileName}-${index}`}>
                      <span className="assessment-attachment-thumb photo">
                        {photo.previewUrl ? <img alt="" src={photo.previewUrl} /> : <i className="ti ti-photo" />}
                      </span>
                      <div>
                        <strong>{photo.fileName}</strong>
                        <small>{photo.description || photo.fileName}</small>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="probation-empty">No photos attached to this note.</p>
              )}
            </section>
          </div>
        ) : null}
      </Drawer>
      <Drawer
        title={probationStaffDrawer?.index === null ? "Add Assigned Staff" : "Edit Assigned Staff"}
        placement="right"
        open={Boolean(probationStaffDrawer)}
        onClose={() => setProbationStaffDrawer(null)}
        width={560}
        className="resident-action-drawer daily-note-drawer"
      >
        {probationStaffDrawer ? (
          <form className="assessment-drawer-form">
            <section className="assessment-drawer-section">
              <h3>Staff Assignment</h3>
              <div className="assessment-drawer-grid">
                <label className="ncp-form-field">
                  <span>Name</span>
                  <input
                    value={probationStaffDrawer.values.name}
                    onChange={(event) => updateProbationStaffDrawerField("name", event.target.value)}
                    placeholder="Staff name"
                  />
                </label>
                <label className="ncp-form-field">
                  <span>Role</span>
                  <input
                    value={probationStaffDrawer.values.role}
                    onChange={(event) => updateProbationStaffDrawerField("role", event.target.value)}
                    placeholder="Care provider role"
                  />
                </label>
                <label className="ncp-form-field ncp-form-field-wide">
                  <span>From</span>
                  <input
                    type="datetime-local"
                    value={(probationStaffDrawer.values.from || "").slice(0, 16)}
                    onChange={(event) => updateProbationStaffDrawerField("from", event.target.value)}
                  />
                </label>
                <label className="ncp-form-field ncp-form-field-wide">
                  <span>To</span>
                  <input
                    type="datetime-local"
                    value={(probationStaffDrawer.values.to || "").slice(0, 16)}
                    onChange={(event) => updateProbationStaffDrawerField("to", event.target.value)}
                  />
                </label>
              </div>
            </section>
            <div className="assessment-drawer-actions">
              <button className="btn secondary" type="button" onClick={() => setProbationStaffDrawer(null)}>Cancel</button>
              <button className="btn primary" type="button" onClick={saveProbationStaffDrawer}>Save Staff</button>
            </div>
          </form>
        ) : null}
      </Drawer>
    </CareShell>
  );
}
